import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import { AlertTriangle, BarChart3, Bell, Camera, CheckCircle2, ClipboardList, Copy, Download, Eye, EyeOff, Flag, KeyRound, LogOut, MapPin, Maximize2, Minimize2, Navigation, Phone, PlayCircle, RefreshCw, Search, User, UserPlus, Wrench } from 'lucide-react';

import { Circle, MapContainer, Marker, Popup, Polyline, Tooltip, useMap, useMapEvents } from 'react-leaflet';

import L from 'leaflet';

import 'leaflet/dist/leaflet.css';
import LeafletTileLayer from './LeafletTileLayer';

import axios from 'axios';

import { Button } from './ui/button';

import { Input } from './ui/input';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

import { Textarea } from './ui/textarea';

import {

  Dialog,

  DialogContent,

  DialogDescription,

  DialogFooter,

  DialogHeader,

  DialogTitle,

} from './ui/dialog';

import { toast } from 'sonner';

import ktcLogo from '../assets/ktc.png';

import LeafletPinPicker from './LeafletPinPicker';
import InfraServiceHealthPanel from './InfraServiceHealthPanel';
import MapStatusPill from './MapStatusPill';
import LeafletFlowDirection from './LeafletFlowDirection';
import { getBandwidthStyle, resolveLinkLoad } from './leafletBandwidthStyle';

import '../pages/dadu/AdminConnectivityMap.css';

import { createOLTMarker, createODPMarker, drawConnection } from './leafletFuturisticNetwork';
import {
  createInfrastructureMarkerIcon,
  normalizeMarkerAccessType,
  normalizeMarkerStatus,
} from './leafletSvgMarkerFactory';
import { clearStaffSession, isJwtExpired, requestStaffLogout, resolveBackendBaseUrl } from '../utils/runtimeConfig';
import { resolveRouterOnlineState } from '../utils/routerStatus';
import MapLoadingOverlay from './MapLoadingOverlay';
import StaffPageHeader from './StaffPageHeader';
import useMinimumLoadingVisibility from '../hooks/useMinimumLoadingVisibility';
import {
  buildMockRecoveryByCorrelation,
  deriveCorrelationState,
  formatDurationSec,
  getCorrelationBadgeClass,
  getCorrelationMapColor,
  getRecoveryBadgeClass,
  normalizeOnuStatus,
  normalizePppoeStatus,
} from '../utils/connectionCorrelation';
import {
  mapMonitoringStatusToTone,
  semanticToneToBadgeClass,
  semanticToneToColor,
  semanticToneToTextClass,
} from '../features/ui-system/status/semanticStatus';



const API = `${resolveBackendBaseUrl()}/api`;
const MONITORING_INCIDENT_API_ENABLED = String(process.env.REACT_APP_MONITORING_INCIDENTS_ENABLED || 'false').trim().toLowerCase() === 'true';
const BASE_URL = `${resolveBackendBaseUrl()}`.replace(/\/$/, '');

const DEFAULT_CENTER = { latitude: -3.4324, longitude: 119.3430 };
const DEFAULT_CUSTOMER_FORM_CENTER = { latitude: -3.3869, longitude: 119.3098 };
const FETCH_RETRY_DELAYS_MS = [2000, 5000, 10000];
const MAP_SVG_RENDERER = L.svg();

const doneTicketStatuses = new Set(['completed', 'needs_special_handling']);

const portalConfigByRole = {

  technician: {

    title: 'Portal Teknisi',

    description: 'Monitoring koneksi pelanggan, tambah pelanggan, dan approval penanganan lapangan.',

    canRestartOnu: true,

    canHandleTickets: true,

    canAddCustomers: true,

    canViewPerformance: true,

  },

  investor: {

    title: 'Portal Investor',

    description: 'Monitoring kualitas koneksi pelanggan dan performa teknisi secara realtime.',

    canRestartOnu: false,

    canHandleTickets: false,

    canAddCustomers: false,

    canViewPerformance: true,

  },
  noc: {
    title: 'Portal NOC',
    description: 'Network Operation Center untuk monitoring realtime pelanggan, infrastruktur, dan alarm gangguan.',
    canRestartOnu: true,
    canHandleTickets: false,
    canAddCustomers: false,
    canViewPerformance: true,
  },

};



const hasValidCoordinates = (lat, lon) => {

  const latitude = Number(lat);

  const longitude = Number(lon);

  return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;

};

const normalizeNodeId = (value) => String(value ?? '').trim();
const toBooleanSafe = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
};

const buildOverlapPositionLookup = (items) => {
  const groups = {};
  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = String(item?.id || '').trim();
    const lat = Number(item?.latitude);
    const lon = Number(item?.longitude);
    if (!id || !Number.isFinite(lat) || !Number.isFinite(lon)) return;
    const key = `${lat.toFixed(4)}:${lon.toFixed(4)}`;
    if (!Array.isArray(groups[key])) groups[key] = [];
    groups[key].push({ id, lat, lon });
  });

  const lookup = {};
  Object.values(groups).forEach((group) => {
    const sorted = group.slice().sort((a, b) => a.id.localeCompare(b.id));
    if (sorted.length === 1) {
      lookup[sorted[0].id] = [sorted[0].lat, sorted[0].lon];
      return;
    }
    sorted.forEach((node, idx) => {
      const angle = (Math.PI * 2 * idx) / sorted.length;
      const radiusMeters = 12 + Math.floor(idx / 8) * 8;
      const latOffset = (radiusMeters * Math.sin(angle)) / 111320;
      const lonOffset = (radiusMeters * Math.cos(angle)) / (111320 * Math.max(Math.cos((node.lat * Math.PI) / 180), 0.2));
      lookup[node.id] = [node.lat + latOffset, node.lon + lonOffset];
    });
  });
  return lookup;
};



const normalizeInfraKriteria = (value) => (String(value || 'live').trim().toLowerCase() === 'plan' ? 'plan' : 'live');

const getInfraCategoryLabel = (value) => (normalizeInfraKriteria(value) === 'plan' ? 'Plan' : 'Live');

const getInfraKriteriaColor = (value) => (normalizeInfraKriteria(value) === 'plan' ? '#f59e0b' : '#3b82f6');



const mapConnectionStatusToTone = (status) => {
  const clean = String(status || '').trim().toLowerCase();
  if (clean === 'online') return 'info';
  if (clean === 'offline') return 'danger';
  if (clean === 'not_mapped') return 'warn';
  return mapMonitoringStatusToTone(clean);
};

const getStatusColor = (status) => semanticToneToColor(mapConnectionStatusToTone(status));



const getStatusLabel = (status) => {

  if (status === 'online') return 'Online';

  if (status === 'offline') return 'Offline/Loss';

  if (status === 'not_mapped') return 'Belum mapping ONU';

  return 'Tidak diketahui';

};

const getPppoeStatusLabel = (status) => {
  if (status === 'online') return 'Online';
  if (status === 'offline') return 'Offline';
  return 'Tidak diketahui';
};

const getPppoeStatusColor = (status) => semanticToneToColor(mapMonitoringStatusToTone(status));
const mapPppSessionStatusToTone = (status) => {
  const clean = String(status || '').toUpperCase();
  if (clean === 'UP') return 'ok';
  if (clean === 'DOWN') return 'danger';
  return 'neutral';
};
const toneToMarkerStateClass = (tone) => {
  if (tone === 'ok' || tone === 'info') return 'online';
  if (tone === 'danger') return 'offline';
  return 'unknown';
};

const buildDistributionPath = (fromPoint, toPoint, seed = '') => {
  if (!Array.isArray(fromPoint) || !Array.isArray(toPoint)) return [fromPoint, toPoint];
  const fromLat = Number(fromPoint[0]);
  const fromLon = Number(fromPoint[1]);
  const toLat = Number(toPoint[0]);
  const toLon = Number(toPoint[1]);
  if (!Number.isFinite(fromLat) || !Number.isFinite(fromLon) || !Number.isFinite(toLat) || !Number.isFinite(toLon)) {
    return [fromPoint, toPoint];
  }
  const hash = String(seed || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const direction = hash % 2 === 0 ? 1 : -1;
  const curveStrength = 0.00012 + (hash % 7) * 0.00001;
  const midLat = (fromLat + toLat) / 2;
  const midLon = (fromLon + toLon) / 2;
  const ctrlLat = midLat + (toLon - fromLon) * curveStrength * direction;
  const ctrlLon = midLon - (toLat - fromLat) * curveStrength * direction;
  return [
    [fromLat, fromLon],
    [ctrlLat, ctrlLon],
    [toLat, toLon],
  ];
};



const getTicketStatusLabel = (status) => {

  if (status === 'pending_approval') return 'Menunggu Approval';

  if (status === 'approved') return 'Turun Lapangan';

  if (status === 'completed') return 'Selesai';

  if (status === 'needs_special_handling') return 'Butuh Khusus';

  return 'Tidak diketahui';

};



const mapTicketWorkflowStatusToTone = (status) => {
  const clean = String(status || '').toLowerCase();
  if (clean === 'completed') return 'ok';
  if (clean === 'needs_special_handling') return 'danger';
  if (clean === 'approved' || clean === 'in_progress') return 'info';
  if (clean === 'pending_approval' || clean === 'open') return 'warn';
  return mapMonitoringStatusToTone(clean);
};

const getTicketStatusClass = (status) => semanticToneToTextClass(mapTicketWorkflowStatusToTone(status));

const normalizeIncidentSourceType = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'customer') return 'customer';
  if (raw === 'odp') return 'odp';
  if (raw === 'olt') return 'olt';
  if (raw === 'nas') return 'nas';
  if (raw === 'cluster' || raw === 'anomaly_cluster') return 'cluster';
  return 'topology_node';
};

const getIncidentSeverityLabel = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'critical') return 'Critical';
  if (raw === 'high') return 'High';
  if (raw === 'medium') return 'Medium';
  return 'Low';
};

const getIncidentStatusLabel = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'open') return 'Open';
  if (raw === 'monitoring') return 'Monitoring';
  if (raw === 'resolved') return 'Resolved';
  if (raw === 'false_positive') return 'False Positive';
  return '-';
};

const mapIncidentStatusToTone = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'open') return 'warn';
  if (raw === 'monitoring') return 'info';
  if (raw === 'resolved') return 'ok';
  if (raw === 'false_positive') return 'neutral';
  return mapMonitoringStatusToTone(raw);
};

const mapIncidentSeverityToTone = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'critical') return 'danger';
  if (raw === 'high' || raw === 'medium') return 'warn';
  if (raw === 'low') return 'ok';
  return mapMonitoringStatusToTone(raw);
};

const getSeverityRank = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'critical') return 4;
  if (raw === 'high') return 3;
  if (raw === 'medium' || raw === 'warning') return 2;
  if (raw === 'low') return 1;
  return 0;
};

const normalizeAiSeverity = (value) => {
  return getSeverityRank(value) >= 3 ? 'CRITICAL' : 'WARNING';
};

const resolveIncidentCoordinates = (incident = {}) => {
  const latitudeCandidates = [
    incident?.latitude,
    incident?.lat,
    incident?.center_latitude,
    incident?.center_lat,
    incident?.geometry?.latitude,
    incident?.geometry?.lat,
    incident?.location?.latitude,
    incident?.location?.lat,
  ];
  const longitudeCandidates = [
    incident?.longitude,
    incident?.lng,
    incident?.center_longitude,
    incident?.center_lng,
    incident?.geometry?.longitude,
    incident?.geometry?.lng,
    incident?.location?.longitude,
    incident?.location?.lng,
  ];
  const latitude = latitudeCandidates.find((item) => Number.isFinite(Number(item)));
  const longitude = longitudeCandidates.find((item) => Number.isFinite(Number(item)));
  if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) return null;
  return { latitude: Number(latitude), longitude: Number(longitude) };
};

const taskPriorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };

const mapTaskPriorityToTone = (priority) => {
  const clean = String(priority || '').toLowerCase();
  if (clean === 'critical' || clean === 'high' || clean === 'medium') return 'warn';
  if (clean === 'low') return 'ok';
  return 'neutral';
};

const getTaskPriorityClass = (priority) => semanticToneToBadgeClass(mapTaskPriorityToTone(priority));

const getTaskPriorityColor = (priority) => {
  const clean = String(priority || '').toLowerCase();
  if (clean === 'critical') return '#ef4444';
  if (clean === 'high') return '#f97316';
  if (clean === 'medium') return '#f59e0b';
  return '#22c55e';
};

const getTaskStatusLabel = (status) => {
  const clean = String(status || '').toLowerCase();
  if (clean === 'unassigned') return 'Belum Diambil';
  if (clean === 'assigned') return 'Ditugaskan';
  if (clean === 'on_progress') return 'On Progress';
  if (clean === 'completed') return 'Selesai';
  if (clean === 'blocked') return 'Blocked';
  return 'Tidak diketahui';
};

const mapTaskStatusToTone = (status) => {
  const clean = String(status || '').toLowerCase();
  if (clean === 'completed') return 'ok';
  if (clean === 'blocked') return 'danger';
  if (clean === 'on_progress' || clean === 'assigned') return 'info';
  if (clean === 'unassigned') return 'warn';
  return mapMonitoringStatusToTone(clean);
};

const getTaskStatusClass = (status) => semanticToneToTextClass(mapTaskStatusToTone(status));



const formatSignal = (status) => {

  if (!status) return 'Tidak tersedia';

  if (Number.isFinite(status.attenuation_db)) return `${status.attenuation_db.toFixed(2)} dB`;

  if (Number.isFinite(status.rx_power_dbm)) return `${status.rx_power_dbm.toFixed(2)} dBm`;

  return 'Tidak tersedia';

};

const parseLastInformLatencySeconds = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const diffSeconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
  if (!Number.isFinite(diffSeconds) || diffSeconds < 0) return null;
  return diffSeconds;
};

const parseNodeRxPower = (node) => {
  const statusDetail = node?.status_detail || {};
  const candidates = [
    statusDetail?.rx_power_dbm,
    statusDetail?.rx_power,
    node?.rx_power_dbm,
    node?.rx_power,
  ];
  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const isNodeFlapping = (node) => {
  const statusDetail = node?.status_detail || {};
  const candidates = [
    statusDetail?.is_flapping,
    statusDetail?.flapping,
    node?.is_flapping,
    node?.flapping,
    node?.status_flapping,
  ];
  return candidates.some((value) => {
    if (typeof value === 'boolean') return value;
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'true' || normalized === 'flapping' || normalized === 'yes';
  });
};

const detectAnomaly = (node) => {
  const issues = [];
  const connectionStatus = String(node?.connection_status || '').trim().toLowerCase();
  const latencySeconds = parseLastInformLatencySeconds(node?.status_detail?.last_inform);
  const rxPower = parseNodeRxPower(node);
  const flapping = isNodeFlapping(node);

  if (connectionStatus === 'offline' || connectionStatus === 'unavailable') {
    issues.push({ code: 'offline', label: 'Node offline', severity: 'high' });
  }
  if (Number.isFinite(latencySeconds) && latencySeconds > 80) {
    issues.push({ code: 'latency_high', label: `Latency tinggi (${latencySeconds}s)`, severity: latencySeconds > 120 ? 'high' : 'medium' });
  }
  if (Number.isFinite(rxPower) && rxPower < -25) {
    issues.push({ code: 'signal_weak', label: `RX power lemah (${rxPower.toFixed(2)} dBm)`, severity: rxPower < -27 ? 'high' : 'medium' });
  }
  if (flapping) {
    issues.push({ code: 'status_flapping', label: 'Status flapping', severity: 'medium' });
  }

  return {
    hasAnomaly: issues.length > 0,
    issues,
    latencySeconds,
    rxPower,
  };
};

const predictIssue = (node) => {
  const predictions = [];
  const latencySeconds = parseLastInformLatencySeconds(node?.status_detail?.last_inform);
  const rxPower = parseNodeRxPower(node);
  if (Number.isFinite(rxPower) && rxPower < -27) {
    predictions.push('Signal berpotensi drop (RX < -27 dBm)');
  }
  if (Number.isFinite(latencySeconds) && latencySeconds > 120) {
    predictions.push('Potensi congestion (latency > 120s)');
  }
  return predictions;
};

const estimateCustomerLoad = (customer = {}) => {
  const direct = resolveLinkLoad(customer);
  if (direct > 0) return direct;
  const stations = Number(customer?.status_detail?.total_stations);
  const isOnline = String(customer?.connection_status || '').toLowerCase() === 'online';
  if (Number.isFinite(stations) && stations > 0) {
    return Math.min(100, stations * 16 + (isOnline ? 18 : 0));
  }
  if (isOnline) return 42;
  if (String(customer?.connection_status || '').toLowerCase() === 'offline') return 0;
  return 14;
};

const formatBps = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return '0 bps';
  if (numeric >= 1000000000) return `${(numeric / 1000000000).toFixed(2)} Gbps`;
  if (numeric >= 1000000) return `${(numeric / 1000000).toFixed(2)} Mbps`;
  if (numeric >= 1000) return `${(numeric / 1000).toFixed(2)} Kbps`;
  return `${numeric.toFixed(0)} bps`;
};



const normalizeCustomerMarkerStatus = normalizeMarkerStatus;
const normalizeInfraMarkerStatus = normalizeMarkerStatus;
const normalizeAccessMarkerType = normalizeMarkerAccessType;

const createWifiIcon = (
  status,
  selected = false,
  ai = { anomaly: false, predictive: false },
) => {
  const markerStatus = normalizeCustomerMarkerStatus(status);
  const stateClass = toneToMarkerStateClass(mapMonitoringStatusToTone(markerStatus));
  const selectedClass = selected ? ' wifi-selected' : '';
  const anomalyClass = ai?.anomaly ? ' wifi-anomaly node-alert' : '';
  const predictiveBadge = ai?.predictive ? '<span class="wifi-predict-badge">AI</span>' : '';
  return L.divIcon({
    className: 'wifi-marker-wrapper',
    html: `<div class=\"wifi-marker wifi-${stateClass}${selectedClass}${anomalyClass}\">${predictiveBadge}<span class=\"wifi-pulse\"></span><svg viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M12 20h.01\"></path><path d=\"M2 8.82a15 15 0 0 1 20 0\"></path><path d=\"M5 12.86a10 10 0 0 1 14 0\"></path><path d=\"M8.5 16.43a5 5 0 0 1 7 0\"></path></svg></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -18],
  });
};

const createCustomerClusterIcon = ({ count = 0, status = 'unknown', anomaly = false }) => {
  const markerStatus = normalizeCustomerMarkerStatus(status);
  const stateClass = toneToMarkerStateClass(mapMonitoringStatusToTone(markerStatus));
  return L.divIcon({
    className: 'wifi-cluster-wrapper',
    html: `<div class="wifi-cluster-marker ${stateClass}${anomaly ? ' has-anomaly' : ''}"><span class="count">${Math.max(1, Number(count || 0))}</span></div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -18],
  });
};

const buildCustomerClusters = (customers, zoom) => {
  if (!Array.isArray(customers) || customers.length === 0) return [];
  const zoomLevel = Number(zoom || 13);
  const cellSize = zoomLevel <= 11 ? 0.07 : (zoomLevel <= 12 ? 0.05 : 0.03);
  const groups = {};
  customers.forEach((customer) => {
    const lat = Number(customer?.latitude);
    const lng = Number(customer?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const latKey = Math.floor(lat / cellSize);
    const lngKey = Math.floor(lng / cellSize);
    const key = `${latKey}:${lngKey}`;
    if (!groups[key]) {
      groups[key] = {
        key,
        count: 0,
        latSum: 0,
        lngSum: 0,
        ids: [],
        statusCount: { healthy: 0, degraded: 0, down: 0, unknown: 0 },
        accessCount: { ftth: 0, htb: 0 },
      };
    }
    const bucket = groups[key];
    const normalizedStatus = normalizeCustomerMarkerStatus(
      customer?.health_status || customer?.health || customer?.connection_status,
    );
    const accessType = normalizeAccessMarkerType(customer?.access_type || customer?.accessType);
    bucket.count += 1;
    bucket.latSum += lat;
    bucket.lngSum += lng;
    bucket.ids.push(String(customer?.id || ''));
    bucket.statusCount[normalizedStatus] = (bucket.statusCount[normalizedStatus] || 0) + 1;
    bucket.accessCount[accessType] = (bucket.accessCount[accessType] || 0) + 1;
  });
  return Object.values(groups).map((bucket) => {
    const dominantStatus = Object.entries(bucket.statusCount).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown';
    const dominantAccess = bucket.accessCount.htb > bucket.accessCount.ftth ? 'htb' : 'ftth';
    return {
      key: bucket.key,
      count: bucket.count,
      latitude: bucket.latSum / bucket.count,
      longitude: bucket.lngSum / bucket.count,
      status: dominantStatus,
      accessType: dominantAccess,
      hasCritical: Number(bucket.statusCount.down || 0) > 0,
      ids: bucket.ids,
    };
  });
};



const FitBoundsController = ({ points, enabled }) => {

  const map = useMap();



  useEffect(() => {

    if (!enabled) return;
    if (!Array.isArray(points) || points.length === 0) return;



    if (points.length === 1) {

      map.setView(points[0], 16, { animate: true });

      return;

    }



    const bounds = L.latLngBounds(points);

    map.fitBounds(bounds, { padding: [40, 40] });

  }, [enabled, map, points]);



  return null;

};



const FocusCustomerController = ({ customer }) => {

  const map = useMap();



  useEffect(() => {

    if (!customer) return;

    if (!hasValidCoordinates(customer.latitude, customer.longitude)) return;



    map.setView([Number(customer.latitude), Number(customer.longitude)], 17, { animate: true });

  }, [customer, map]);



  return null;

};

const createRouterNasIcon = (status = 'unknown', selected = false, anomaly = false) => createInfrastructureMarkerIcon({
  type: 'router',
  status,
  selected,
  anomaly,
});

const OdpFocusController = ({ selectedOdp, linkedOlt, customers }) => {
  const map = useMap();

  useEffect(() => {
    if (!selectedOdp) return;
    const scopedPoints = [];
    if (hasValidCoordinates(selectedOdp?.latitude, selectedOdp?.longitude)) {
      scopedPoints.push([Number(selectedOdp.latitude), Number(selectedOdp.longitude)]);
    }
    if (linkedOlt && hasValidCoordinates(linkedOlt?.latitude, linkedOlt?.longitude)) {
      scopedPoints.push([Number(linkedOlt.latitude), Number(linkedOlt.longitude)]);
    }
    (Array.isArray(customers) ? customers : []).forEach((item) => {
      if (hasValidCoordinates(item?.latitude, item?.longitude)) {
        scopedPoints.push([Number(item.latitude), Number(item.longitude)]);
      }
    });

    if (scopedPoints.length === 0) return;
    if (scopedPoints.length === 1) {
      map.setView(scopedPoints[0], Math.max(map.getZoom(), 16), { animate: true, duration: 0.35 });
      return;
    }
    map.flyToBounds(L.latLngBounds(scopedPoints), {
      animate: true,
      duration: 0.45,
      padding: [76, 76],
      maxZoom: 17,
    });
  }, [customers, linkedOlt, map, selectedOdp]);

  return null;
};

const RouterFocusController = ({ selectedRouter, customers }) => {
  const map = useMap();

  useEffect(() => {
    if (!selectedRouter) return;
    const scopedPoints = [];
    if (hasValidCoordinates(selectedRouter?.latitude, selectedRouter?.longitude)) {
      scopedPoints.push([Number(selectedRouter.latitude), Number(selectedRouter.longitude)]);
    }
    (Array.isArray(customers) ? customers : []).forEach((item) => {
      if (hasValidCoordinates(item?.latitude, item?.longitude)) {
        scopedPoints.push([Number(item.latitude), Number(item.longitude)]);
      }
    });
    if (scopedPoints.length === 0) return;
    if (scopedPoints.length === 1) {
      map.setView(scopedPoints[0], Math.max(map.getZoom(), 16), { animate: true, duration: 0.35 });
      return;
    }
    map.flyToBounds(L.latLngBounds(scopedPoints), {
      animate: true,
      duration: 0.45,
      padding: [76, 76],
      maxZoom: 17,
    });
  }, [customers, map, selectedRouter]);

  return null;
};

const MapSelectionResetController = ({ onReset }) => {
  useMapEvents({
    click: () => {
      if (typeof onReset === 'function') onReset();
    },
  });
  return null;
};

const AiTargetFocusController = ({ request }) => {
  const map = useMap();

  useEffect(() => {
    if (!request || !Array.isArray(request.points)) return;
    const points = request.points.filter((item) => Array.isArray(item) && Number.isFinite(Number(item[0])) && Number.isFinite(Number(item[1])));
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], Math.max(map.getZoom(), 15), { animate: true, duration: 0.35 });
      return;
    }
    map.flyToBounds(L.latLngBounds(points), {
      animate: true,
      duration: 0.45,
      padding: [84, 84],
      maxZoom: 17,
    });
  }, [map, request]);

  return null;
};

const TopLeftZoomControl = () => {
  const map = useMap();

  useEffect(() => {
    const control = L.control.zoom({ position: 'topleft' });
    control.addTo(map);
    return () => {
      control.remove();
    };
  }, [map]);

  return null;
};

const MapZoomStateController = ({ onZoomChange }) => {
  const map = useMapEvents({
    zoomend: () => {
      if (typeof onZoomChange === 'function') onZoomChange(map.getZoom());
    },
  });
  useEffect(() => {
    if (typeof onZoomChange === 'function') onZoomChange(map.getZoom());
  }, [map, onZoomChange]);
  return null;
};



const StaffMonitoringMap = ({ role }) => {

  const navigate = useNavigate();

  const config = portalConfigByRole[role] || portalConfigByRole.investor;



  const [user, setUser] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [pppoeProfiles, setPppoeProfiles] = useState([]);
  const [nasRouters, setNasRouters] = useState([]);
  const [routerTrafficById, setRouterTrafficById] = useState({});
  const [routerTrafficHistoryById, setRouterTrafficHistoryById] = useState({});
  const [loadingRouterTraffic, setLoadingRouterTraffic] = useState(false);
  const [loadingRouterHistoryId, setLoadingRouterHistoryId] = useState('');
  const [selectedTrafficRouterId, setSelectedTrafficRouterId] = useState('');

  const [olts, setOlts] = useState([]);

  const [odps, setOdps] = useState([]);

  const [statusByCustomerId, setStatusByCustomerId] = useState({});

  const [serverAvailable, setServerAvailable] = useState(true);
  const [systemMetrics, setSystemMetrics] = useState(null);

  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [autoFitMap, setAutoFitMap] = useState(false);
  const showLoadingOverlay = useMinimumLoadingVisibility(loading, 1200);

  const [statusFilter, setStatusFilter] = useState('all');

  const [infraFilter, setInfraFilter] = useState('all');
  const [selectedNasId, setSelectedNasId] = useState('all');

  const [selectedOltId, setSelectedOltId] = useState('all');

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedAiTargetId, setSelectedAiTargetId] = useState('');
  const [aiMapFocusRequest, setAiMapFocusRequest] = useState(null);
  const [aiAutoFocusEnabled, setAiAutoFocusEnabled] = useState(true);
  const [odpScopeRenderTick, setOdpScopeRenderTick] = useState(0);
  const [monitorMode, setMonitorMode] = useState('semua');
  const [activeOpsTab, setActiveOpsTab] = useState('ai');
  const selectedEntityType = String(selectedEntity?.type || '').trim().toLowerCase();
  const selectedEntityId = normalizeNodeId(selectedEntity?.id);
  const selectedCustomerId = selectedEntityType === 'customer' ? selectedEntityId : '';
  const selectedOdpId = selectedEntityType === 'odp' ? selectedEntityId : '';
  const selectedMapRouterId = selectedEntityType === 'router' ? selectedEntityId : '';
  const selectEntity = useCallback((type, id) => {
    const normalizedId = normalizeNodeId(id);
    const normalizedType = String(type || '').trim().toLowerCase();
    if (!normalizedId || !['customer', 'odp', 'router'].includes(normalizedType)) return;
    setSelectedAiTargetId('');
    setSelectedEntity((prev) => {
      const prevType = String(prev?.type || '').trim().toLowerCase();
      const prevId = normalizeNodeId(prev?.id);
      if (prevType === normalizedType && prevId === normalizedId) return prev;
      return { type: normalizedType, id: normalizedId };
    });
  }, []);
  const toggleEntity = useCallback((type, id) => {
    const normalizedId = normalizeNodeId(id);
    const normalizedType = String(type || '').trim().toLowerCase();
    if (!normalizedId || !['customer', 'odp', 'router'].includes(normalizedType)) return;
    setSelectedAiTargetId('');
    setSelectedEntity((prev) => {
      const prevType = String(prev?.type || '').trim().toLowerCase();
      const prevId = normalizeNodeId(prev?.id);
      if (prevType === normalizedType && prevId === normalizedId) return null;
      return { type: normalizedType, id: normalizedId };
    });
  }, []);
  const clearSelectedEntity = useCallback(() => {
    setSelectedEntity(null);
    setSelectedAiTargetId('');
  }, []);
  const [mapLayerVisibility, setMapLayerVisibility] = useState({
    showCoverage: true,
    showOlt: true,
    showOdp: true,
    showRouters: true,
    showCoreLinks: true,
    showCustomerLinks: false,
    showCustomers: true,
    showPppSessions: true,
    showCorrelationAlerts: true,
    showRecoveryQueue: true,
    animate: true,
  });
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [mapZoom, setMapZoom] = useState(13);
  const mapPanelRef = useRef(null);
  const [aiModeEnabled, setAiModeEnabled] = useState(false);
  const lastAiTargetFocusRef = useRef({ id: '', at: 0 });

  const [lastUpdated, setLastUpdated] = useState('');

  const [restartingCustomerId, setRestartingCustomerId] = useState('');

  const [streamConnected, setStreamConnected] = useState(false);
  const [streamReconnectCount, setStreamReconnectCount] = useState(0);
  const [isRetryScheduled, setIsRetryScheduled] = useState(false);

  const streamErrorNotifiedRef = useRef(false);
  const streamReadyRef = useRef(false);
  const authExpiredNotifiedRef = useRef(false);
  const fetchRetryAttemptRef = useRef(0);
  const fetchRetryTimerRef = useRef(null);
  const firstSuccessfulLoadRef = useRef(false);



  const [fieldTickets, setFieldTickets] = useState([]);

  const [loadingTickets, setLoadingTickets] = useState(false);
  const [monitoringIncidents, setMonitoringIncidents] = useState([]);
  const [loadingMonitoringIncidents, setLoadingMonitoringIncidents] = useState(false);
  const [resolvingMonitoringIncidentId, setResolvingMonitoringIncidentId] = useState('');

  const [technicianTasks, setTechnicianTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const [taskScope, setTaskScope] = useState('assigned');
  const [taskRadiusKm, setTaskRadiusKm] = useState(5);
  const [taskActionLoadingId, setTaskActionLoadingId] = useState('');
  const [taskReportById, setTaskReportById] = useState({});
  const [assignmentSettings, setAssignmentSettings] = useState({
    enabled: false,
    prefer_nearest: true,
    balance_workload: true,
    priority_override: true,
  });
  const [savingAssignmentSettings, setSavingAssignmentSettings] = useState(false);
  const [regionTaskQueue, setRegionTaskQueue] = useState([]);
  const [loadingRegionTaskQueue, setLoadingRegionTaskQueue] = useState(false);
  const [technicianUsers, setTechnicianUsers] = useState([]);
  const [loadingTechnicianUsers, setLoadingTechnicianUsers] = useState(false);
  const [manualAssignTechnicianId, setManualAssignTechnicianId] = useState('');
  const [quickActionLoading, setQuickActionLoading] = useState('');
  const [quickActionFeedback, setQuickActionFeedback] = useState(null);
  const [quickActionCooldownUntil, setQuickActionCooldownUntil] = useState({});
  const [quickActionNowTick, setQuickActionNowTick] = useState(Date.now());
  const [customerContextById, setCustomerContextById] = useState({});
  const [loadingCustomerContext, setLoadingCustomerContext] = useState(false);
  const [customerContextError, setCustomerContextError] = useState('');
  const [recoverySummary, setRecoverySummary] = useState(null);
  const [recoveryStatusByCustomerId, setRecoveryStatusByCustomerId] = useState({});

  const [notifications, setNotifications] = useState([]);

  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const seenNotificationIdsRef = useRef(new Set());

  const notificationBootstrapRef = useRef(false);

  const [joiningTicketId, setJoiningTicketId] = useState('');

  const [completingTicketId, setCompletingTicketId] = useState('');

  const [completionFormByTicketId, setCompletionFormByTicketId] = useState({});

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const [passwordSaving, setPasswordSaving] = useState(false);

  const [passwordForm, setPasswordForm] = useState({

    old_password: '',

    new_password: '',

    confirm_password: '',

  });

  const [customerForm, setCustomerForm] = useState({

    name: '',

    phone: '',

    address: '',

    email: '',
    router_id: '',
    olt_id: '',

    package: '',
    pppoe_username: '',
    pppoe_password: '',
    ktp_photo_url: '',

    odp_id: '',

    latitude: '',

    longitude: '',

    onu_serial: '',

    notes: '',

  });

  const [customerSaving, setCustomerSaving] = useState(false);
  const [ktpUploading, setKtpUploading] = useState(false);
  const [distributionSearch, setDistributionSearch] = useState('');

  const [gettingLocation, setGettingLocation] = useState(false);



  useEffect(() => {

    const storedUser = localStorage.getItem('ktc_user');

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }

    }

  }, []);

  const handleAuthExpired = useCallback(() => {
    if (authExpiredNotifiedRef.current) return;
    authExpiredNotifiedRef.current = true;
    clearStaffSession();
    toast.error('Sesi login berakhir. Silakan login ulang.');
    navigate('/dadu/login');
  }, [navigate]);

  const getAuthToken = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    if (!token || isJwtExpired(token)) {
      handleAuthExpired();
      return '';
    }
    return token;
  }, [handleAuthExpired]);

  const getHeaders = useCallback(() => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getAuthToken]);

  const packageOptions = useMemo(() => {
    return pppoeProfiles
      .map((item) => ({
        value: String(item?.id || '').trim(),
        label: String(item?.name || '-'),
        price: Number(item?.price || 0),
        speed: Number(item?.speed_mbps || 0),
        service_type: String(item?.service_type || '').trim().toLowerCase(),
        router_id: String(item?.router_id || '').trim(),
      }))
      .filter((item) => item.value && item.service_type === 'pppoe');
  }, [pppoeProfiles]);
  const packageOptionsByRouter = useMemo(() => {
    const selectedRouterId = String(customerForm.router_id || '').trim();
    if (!selectedRouterId) return packageOptions;
    return packageOptions.filter((item) => !item.router_id || item.router_id === selectedRouterId);
  }, [customerForm.router_id, packageOptions]);

  const buildAssetUrl = useCallback((rawUrl) => {
    const value = String(rawUrl || '').trim();
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `${BASE_URL}${value.startsWith('/') ? value : `/${value}`}`;
  }, []);

  const getNextPppoeSequence = useCallback(() => {
    let maxNum = 0;
    customers.forEach((customer) => {
      const raw = String(customer?.pppoe_username || '').trim();
      const digitsOnly = raw.replace(/\D/g, '');
      if (!digitsOnly) return;
      const value = parseInt(digitsOnly, 10);
      if (!Number.isNaN(value) && value > maxNum) maxNum = value;
    });
    return String(maxNum + 1).padStart(6, '0');
  }, [customers]);

  const fetchNextPppoeFromServer = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers/next-pppoe`, { headers: getHeaders() });
      const username = String(response.data?.pppoe_username || '').trim();
      const password = String(response.data?.pppoe_password || '').trim();
      if (username && password) {
        return { pppoe_username: username, pppoe_password: password };
      }
    } catch {
      // fallback handled below
    }
    const seq = getNextPppoeSequence();
    return { pppoe_username: seq, pppoe_password: `KTC-${seq}` };
  }, [getHeaders, getNextPppoeSequence]);

  const handleApiError = useCallback((error, fallbackMessage, options = {}) => {
    const { silent = false } = options;
    if (error?.response?.status === 401) {
      handleAuthExpired();
      return true;
    }
    if (!silent) {
      toast.error(error?.response?.data?.detail || fallbackMessage);
    }
    return false;
  }, [handleAuthExpired]);

  const clearFetchRetry = useCallback(() => {
    fetchRetryAttemptRef.current = 0;
    if (fetchRetryTimerRef.current) {
      window.clearTimeout(fetchRetryTimerRef.current);
      fetchRetryTimerRef.current = null;
    }
    setIsRetryScheduled(false);
  }, []);



  const applyStatusPayload = useCallback((statusPayload, radiusPayload = null) => {

    const payload = statusPayload || {};
    const radius = radiusPayload || {};

    const statuses = Array.isArray(payload.statuses) ? payload.statuses : [];
    const radiusStatuses = Array.isArray(radius.statuses) ? radius.statuses : [];

    const mappedStatus = {};



    statuses.forEach((item) => {

      if (item?.customer_id) {
        mappedStatus[item.customer_id] = {
          ...item,
          pppoe_connection_status: 'unknown',
          pppoe_status_detail: null,
        };
      }

    });

    radiusStatuses.forEach((item) => {
      if (!item?.customer_id) return;
      const existing = mappedStatus[item.customer_id] || { customer_id: item.customer_id, connection_status: 'unknown', status_detail: null };
      mappedStatus[item.customer_id] = {
        ...existing,
        pppoe_connection_status: item.connection_status || 'unknown',
        pppoe_status_detail: item.status_detail || null,
      };
    });



    setStatusByCustomerId(mappedStatus);

    setServerAvailable(Boolean(payload.available));

    setLastUpdated(payload.updated_at || new Date().toISOString());

  }, []);



  const fetchData = useCallback(async (showLoader = true) => {
    let keepLoader = false;

    const normalizedSearch = String(debouncedSearchQuery || '').trim();
    const useServerSearch = normalizedSearch.length >= 3;
    const skipCustomerLoad = normalizedSearch.length > 0 && normalizedSearch.length < 3;
    const shouldShowGlobalLoader = showLoader && normalizedSearch.length === 0 && !firstSuccessfulLoadRef.current;
    if (shouldShowGlobalLoader) setLoading(true);
    if (useServerSearch) setSearchLoading(true);
    if (!useServerSearch) setSearchLoading(false);



    try {

      const headersValue = getHeaders();
      if (!headersValue.Authorization) {
        setSearchLoading(false);
        if (shouldShowGlobalLoader) setLoading(false);
        return;
      }
      const headers = { headers: headersValue };
      
      const customerRequest = skipCustomerLoad
        ? Promise.resolve({ data: [] })
        : axios.get(`${API}/customers`, {
          ...headers,
          params: useServerSearch ? { search: normalizedSearch } : undefined,
        });

      const [customersRes, oltsRes, odpsRes, nasRes, topologyRes, statusRes, radiusRes, pppoeProfilesRes] = await Promise.all([

        customerRequest,

        axios.get(`${API}/ftth/olts`, headers),

        axios.get(`${API}/ftth/odps`, headers),

        axios.get(`${API}/voucher/nas`, headers).catch(() => ({ data: { items: [] } })),

        axios.get(`${API}/ftth/topology`, headers).catch(() => ({ data: {} })),

        axios.get(`${API}/genieacs/customer-status`, headers),
        axios.get(`${API}/freeradius/customer-status`, headers).catch(() => ({ data: {} })),
        axios.get(`${API}/service-profiles`, {
          ...headers,
          params: { service_type: 'pppoe', active_only: true },
        }).catch(() => ({ data: { items: [] } })),

      ]);



      const topologyPayload = topologyRes?.data && typeof topologyRes.data === 'object' ? topologyRes.data : {};
      const nasPayload = nasRes?.data && typeof nasRes.data === 'object' ? nasRes.data : {};

      const topologyCustomers = Array.isArray(topologyPayload.customers) ? topologyPayload.customers : [];

      const topologyOlts = Array.isArray(topologyPayload.olts) ? topologyPayload.olts : [];

      const topologyOdps = Array.isArray(topologyPayload.odps) ? topologyPayload.odps : [];

      if (!skipCustomerLoad) {
        const nextCustomers = useServerSearch
          ? (Array.isArray(customersRes.data) ? customersRes.data : [])
          : (topologyCustomers.length > 0 ? topologyCustomers : (Array.isArray(customersRes.data) ? customersRes.data : []));
        setCustomers(nextCustomers);
      }

      setOlts(topologyOlts.length > 0 ? topologyOlts : (Array.isArray(oltsRes.data) ? oltsRes.data : []));

      setOdps(topologyOdps.length > 0 ? topologyOdps : (Array.isArray(odpsRes.data) ? odpsRes.data : []));
      setNasRouters(Array.isArray(nasPayload.items) ? nasPayload.items : []);
      setPppoeProfiles(Array.isArray(pppoeProfilesRes?.data?.items) ? pppoeProfilesRes.data.items : []);

      applyStatusPayload(statusRes.data || {}, radiusRes?.data || {});
      firstSuccessfulLoadRef.current = true;
      clearFetchRetry();

    } catch (error) {
      if (error?.response?.status === 401) {
        clearFetchRetry();
        handleAuthExpired();
      } else {
        const attempt = fetchRetryAttemptRef.current;
        const retryDelay = FETCH_RETRY_DELAYS_MS[attempt] ?? null;
        if (retryDelay !== null) {
          fetchRetryAttemptRef.current = attempt + 1;
          keepLoader = shouldShowGlobalLoader;
          if (!fetchRetryTimerRef.current) {
            setIsRetryScheduled(true);
            fetchRetryTimerRef.current = window.setTimeout(() => {
              fetchRetryTimerRef.current = null;
              setIsRetryScheduled(false);
              fetchData(shouldShowGlobalLoader);
            }, retryDelay);
            if (attempt === 0) {
              toast.warning('Gagal memuat monitoring, mencoba ulang otomatis...');
            }
          }
        } else {
          setIsRetryScheduled(false);
          handleApiError(error, 'Gagal memuat monitoring konektivitas');
          setServerAvailable(false);
          setStreamConnected(false);
        }
      }

    } finally {

      if (useServerSearch) setSearchLoading(false);
      if (shouldShowGlobalLoader && !keepLoader) setLoading(false);

    }

  }, [applyStatusPayload, clearFetchRetry, debouncedSearchQuery, getHeaders, handleApiError, handleAuthExpired]);

  const fetchSystemMetrics = useCallback(async () => {
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const response = await axios.get(`${API}/system/metrics`, { headers: headersValue });
      setSystemMetrics(response?.data || null);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      }
    }
  }, [getHeaders, handleAuthExpired]);

  const fetchRouterTrafficCurrent = useCallback(async (routerIds = []) => {
    const normalizedIds = Array.from(
      new Set((Array.isArray(routerIds) ? routerIds : []).map((value) => normalizeNodeId(value)).filter(Boolean))
    );
    if (!normalizedIds.length) {
      setRouterTrafficById({});
      return;
    }

    try {
      setLoadingRouterTraffic(true);
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;

      const responses = await Promise.all(
        normalizedIds.map((routerId) =>
          axios
            .get(`${API}/router-monitoring/routers/${encodeURIComponent(routerId)}/traffic/current`, {
              headers: headersValue,
              params: { refresh: true },
              timeout: 15000,
            })
            .then((res) => ({ routerId, data: res?.data || {} }))
            .catch((error) => ({
              routerId,
              data: {
                ok: false,
                interfaces: [],
                degraded_reason: error?.response?.data?.detail || 'fetch_failed',
              },
            })),
        )
      );

      const mapped = {};
      responses.forEach((entry) => {
        mapped[entry.routerId] = entry.data;
      });
      setRouterTrafficById(mapped);
    } finally {
      setLoadingRouterTraffic(false);
    }
  }, [getHeaders]);

  const fetchRouterTrafficHistory = useCallback(async (routerId, minutes = 180, limit = 120) => {
    const normalizedRouterId = normalizeNodeId(routerId);
    if (!normalizedRouterId) return;
    try {
      setLoadingRouterHistoryId(normalizedRouterId);
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const response = await axios.get(`${API}/router-monitoring/routers/${encodeURIComponent(normalizedRouterId)}/traffic/history`, {
        headers: headersValue,
        params: { minutes, limit },
        timeout: 15000,
      });
      setRouterTrafficHistoryById((prev) => ({
        ...prev,
        [normalizedRouterId]: response?.data || { samples: [], total: 0, ok: false },
      }));
      setSelectedTrafficRouterId(normalizedRouterId);
    } catch (error) {
      setRouterTrafficHistoryById((prev) => ({
        ...prev,
        [normalizedRouterId]: {
          ok: false,
          samples: [],
          total: 0,
          message: error?.response?.data?.detail || 'Gagal memuat history traffic',
        },
      }));
    } finally {
      setLoadingRouterHistoryId('');
    }
  }, [getHeaders]);

  const fetchRecoverySummary = useCallback(async () => {
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const response = await axios.get(`${API}/recovery/summary`, { headers: headersValue });
      setRecoverySummary(response?.data || null);
    } catch (error) {
      handleApiError(error, 'Gagal memuat ringkasan auto recovery', { silent: true });
      setRecoverySummary(null);
    }
  }, [getHeaders, handleApiError]);

  const fetchRecoveryStatusForCustomers = useCallback(async (customerIds = []) => {
    const ids = (Array.isArray(customerIds) ? customerIds : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .slice(0, 80);
    if (!ids.length) return;
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const responses = await Promise.allSettled(
        ids.map((customerId) =>
          axios.get(`${API}/recovery/customers/${encodeURIComponent(customerId)}/status`, {
            headers: headersValue,
            timeout: 10000,
          }),
        ),
      );
      const next = {};
      responses.forEach((result, idx) => {
        const customerId = ids[idx];
        if (result.status === 'fulfilled') {
          next[customerId] = result.value?.data || null;
        }
      });
      if (Object.keys(next).length > 0) {
        setRecoveryStatusByCustomerId((prev) => ({ ...prev, ...next }));
      }
    } catch (error) {
      handleApiError(error, 'Gagal memuat status recovery pelanggan', { silent: true });
    }
  }, [getHeaders, handleApiError]);



  const fetchFieldTickets = useCallback(async () => {

    if (!config.canHandleTickets && !config.canViewPerformance) return;



    setLoadingTickets(true);

    try {

      const response = await axios.get(`${API}/field-tickets`, { headers: getHeaders() });

      setFieldTickets(Array.isArray(response.data) ? response.data : []);

    } catch (error) {

      handleApiError(error, 'Gagal memuat tiket penanganan');

      setFieldTickets([]);

    } finally {

      setLoadingTickets(false);

    }

  }, [config.canHandleTickets, config.canViewPerformance, getHeaders, handleApiError]);

  const fetchMonitoringIncidents = useCallback(async () => {
    if (!config.canHandleTickets && !config.canViewPerformance) return;
    if (!MONITORING_INCIDENT_API_ENABLED) {
      setMonitoringIncidents([]);
      setLoadingMonitoringIncidents(false);
      return;
    }
    setLoadingMonitoringIncidents(true);
    try {
      const response = await axios.get(`${API}/monitoring-incidents`, {
        headers: getHeaders(),
        params: { limit: 20 },
      });
      const items = Array.isArray(response?.data?.items) ? response.data.items : [];
      setMonitoringIncidents(items);
    } catch (error) {
      handleApiError(error, 'Gagal memuat monitoring incident');
      setMonitoringIncidents([]);
    } finally {
      setLoadingMonitoringIncidents(false);
    }
  }, [config.canHandleTickets, config.canViewPerformance, getHeaders, handleApiError]);

  const handleResolveMonitoringIncident = useCallback(async (incident, falsePositive = false) => {
    const incidentId = String(incident?.id || '').trim();
    if (!incidentId) return;
    const prompt = falsePositive
      ? 'Tandai incident ini sebagai false positive?'
      : 'Tandai incident ini sebagai resolved?';
    if (!window.confirm(prompt)) return;
    setResolvingMonitoringIncidentId(incidentId);
    try {
      await axios.post(
        `${API}/monitoring-incidents/${encodeURIComponent(incidentId)}/resolve`,
        { false_positive: Boolean(falsePositive) },
        { headers: getHeaders(), timeout: 12000 },
      );
      toast.success(falsePositive ? 'Incident ditandai false positive.' : 'Incident ditandai resolved.');
      fetchMonitoringIncidents();
    } catch (error) {
      handleApiError(error, 'Gagal mengubah status incident');
    } finally {
      setResolvingMonitoringIncidentId('');
    }
  }, [fetchMonitoringIncidents, getHeaders, handleApiError]);

  const fetchTechnicianTasks = useCallback(async () => {
    if (!config.canHandleTickets) return;
    setLoadingTasks(true);
    try {
      let latitude;
      let longitude;
      if (navigator?.geolocation && taskScope === 'nearby') {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
          });
          latitude = position?.coords?.latitude;
          longitude = position?.coords?.longitude;
        } catch (_error) {
          latitude = undefined;
          longitude = undefined;
        }
      }

      const response = await axios.get(`${API}/technician-tasks`, {
        headers: getHeaders(),
        params: {
          scope: taskScope,
          radius_km: taskRadiusKm,
          latitude,
          longitude,
        },
      });
      setTechnicianTasks(Array.isArray(response?.data?.items) ? response.data.items : []);
    } catch (error) {
      handleApiError(error, 'Gagal memuat daftar tugas teknisi');
      setTechnicianTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, [config.canHandleTickets, getHeaders, handleApiError, taskRadiusKm, taskScope]);

  const fetchTechnicianTaskDetail = useCallback(async (taskId) => {
    if (!taskId) {
      setSelectedTaskDetail(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/technician-tasks/${taskId}`, { headers: getHeaders() });
      setSelectedTaskDetail(response?.data || null);
    } catch (error) {
      handleApiError(error, 'Gagal memuat detail tugas');
      setSelectedTaskDetail(null);
    }
  }, [getHeaders, handleApiError]);

  const fetchCustomerContext = useCallback(async (customerId, { force = false } = {}) => {
    const id = String(customerId || '').trim();
    if (!id) return;
    if (!force && customerContextById[id]) return;
    setLoadingCustomerContext(true);
    setCustomerContextError('');
    try {
      const response = await axios.get(`${API}/customer-context/${id}`, { headers: getHeaders() });
      const payload = response?.data || null;
      setCustomerContextById((prev) => ({ ...prev, [id]: payload }));
    } catch (error) {
      setCustomerContextError('Gagal memuat konteks pelanggan');
      handleApiError(error, 'Gagal memuat konteks pelanggan', { silent: true });
    } finally {
      setLoadingCustomerContext(false);
    }
  }, [customerContextById, getHeaders, handleApiError]);

  const fetchAssignmentSettings = useCallback(async () => {
    if (!config.canHandleTickets) return;
    try {
      const response = await axios.get(`${API}/technician-task-assignment/settings`, { headers: getHeaders() });
      const settings = response?.data?.settings;
      if (settings && typeof settings === 'object') {
        setAssignmentSettings((prev) => ({ ...prev, ...settings }));
      }
    } catch (error) {
      handleApiError(error, 'Gagal memuat pengaturan auto-assign', { silent: true });
    }
  }, [config.canHandleTickets, getHeaders, handleApiError]);

  const saveAssignmentSettings = useCallback(async (nextSettings) => {
    if (!config.canHandleTickets) return;
    setSavingAssignmentSettings(true);
    try {
      const response = await axios.post(`${API}/technician-task-assignment/settings`, nextSettings, { headers: getHeaders() });
      const saved = response?.data?.settings;
      if (saved && typeof saved === 'object') {
        setAssignmentSettings((prev) => ({ ...prev, ...saved }));
      }
      toast.success('Pengaturan auto-assign diperbarui');
    } catch (error) {
      handleApiError(error, 'Gagal menyimpan pengaturan auto-assign');
    } finally {
      setSavingAssignmentSettings(false);
    }
  }, [config.canHandleTickets, getHeaders, handleApiError]);

  const fetchRegionTaskQueue = useCallback(async () => {
    if (!config.canHandleTickets) return;
    setLoadingRegionTaskQueue(true);
    try {
      const response = await axios.get(`${API}/technician-task-queue/region`, { headers: getHeaders() });
      setRegionTaskQueue(Array.isArray(response?.data?.items) ? response.data.items : []);
    } catch (error) {
      handleApiError(error, 'Gagal memuat queue region', { silent: true });
      setRegionTaskQueue([]);
    } finally {
      setLoadingRegionTaskQueue(false);
    }
  }, [config.canHandleTickets, getHeaders, handleApiError]);

  const fetchTechnicianUsers = useCallback(async () => {
    if (!config.canHandleTickets) return;
    setLoadingTechnicianUsers(true);
    try {
      const response = await axios.get(`${API}/users`, { headers: getHeaders(), params: { role: 'technician' } });
      const items = Array.isArray(response?.data) ? response.data : [];
      setTechnicianUsers(items.filter((item) => String(item?.role || '').toLowerCase() === 'technician'));
    } catch (error) {
      handleApiError(error, 'Gagal memuat daftar teknisi', { silent: true });
      setTechnicianUsers([]);
    } finally {
      setLoadingTechnicianUsers(false);
    }
  }, [config.canHandleTickets, getHeaders, handleApiError]);



  const fetchNotifications = useCallback(async ({ silent = false, withToast = false } = {}) => {

    if (!silent) setLoadingNotifications(true);

    try {

      const response = await axios.get(`${API}/notifications`, { headers: getHeaders(), params: { limit: 6 } });

      const nextNotifications = Array.isArray(response.data?.notifications) ? response.data.notifications : [];



      if (!notificationBootstrapRef.current) {

        seenNotificationIdsRef.current = new Set(nextNotifications.map((item) => item?.id).filter(Boolean));

        notificationBootstrapRef.current = true;

      } else if (withToast) {

        nextNotifications

          .filter((item) => item && !item.is_read && item.id && !seenNotificationIdsRef.current.has(item.id))

          .slice()

          .reverse()

          .forEach((item) => {

            toast(item.title || 'Notifikasi baru', {

              description: item.message || 'Ada pembaruan operasional baru.',

            });

          });

      }



      seenNotificationIdsRef.current = new Set(nextNotifications.map((item) => item?.id).filter(Boolean));

      setNotifications(nextNotifications);

    } catch (error) {
      handleApiError(error, 'Gagal memuat notifikasi', { silent: true });
      if (!silent) setNotifications([]);

    } finally {

      if (!silent) setLoadingNotifications(false);

    }

  }, [getHeaders, handleApiError]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalized = String(searchQuery || '').trim();
      if (normalized.length === 0 || normalized.length >= 3) {
        setDebouncedSearchQuery(normalized);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);



  useEffect(() => {

    fetchData(true);
    fetchSystemMetrics();
    fetchRecoverySummary();

  }, [fetchData, fetchSystemMetrics, fetchRecoverySummary]);

  useEffect(() => () => clearFetchRetry(), [clearFetchRetry]);



  useEffect(() => {

    fetchFieldTickets();

  }, [fetchFieldTickets]);
  useEffect(() => {
    fetchMonitoringIncidents();
  }, [fetchMonitoringIncidents]);

  useEffect(() => {
    fetchTechnicianTasks();
  }, [fetchTechnicianTasks]);

  useEffect(() => {
    fetchAssignmentSettings();
    fetchRegionTaskQueue();
    fetchTechnicianUsers();
  }, [fetchAssignmentSettings, fetchRegionTaskQueue, fetchTechnicianUsers]);

  useEffect(() => {
    if (!selectedTaskId) {
      setSelectedTaskDetail(null);
      return;
    }
    fetchTechnicianTaskDetail(selectedTaskId);
  }, [fetchTechnicianTaskDetail, selectedTaskId]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    fetchCustomerContext(selectedCustomerId);
  }, [fetchCustomerContext, selectedCustomerId]);

  useEffect(() => {
    setQuickActionFeedback(null);
    setCustomerContextError('');
  }, [selectedCustomerId]);

  useEffect(() => {
    const task = technicianTasks.find((item) => String(item.id || '') === String(selectedTaskId || ''));
    const assigned = String(task?.assigned_to || '').trim();
    setManualAssignTechnicianId(assigned);
  }, [selectedTaskId, technicianTasks]);



  useEffect(() => {

    fetchNotifications();

  }, [fetchNotifications]);



  useEffect(() => {

    const timer = window.setInterval(() => {

      fetchNotifications({ silent: true, withToast: true });

    }, 20000);



    return () => window.clearInterval(timer);

  }, [fetchNotifications]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      fetchSystemMetrics();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [fetchSystemMetrics]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      fetchRecoverySummary();
    }, 45000);
    return () => window.clearInterval(timer);
  }, [fetchRecoverySummary]);

  useEffect(() => {
    const ids = filteredCustomers.map((item) => item?.id).filter(Boolean);
    if (!ids.length) return;
    fetchRecoveryStatusForCustomers(ids);
  }, [fetchRecoveryStatusForCustomers, filteredCustomers]);



  useEffect(() => {

    const token = getAuthToken();
    if (!token) return undefined;



    const streamUrl = `${API}/genieacs/customer-status/stream?token=${encodeURIComponent(token)}`;

    const source = new EventSource(streamUrl);



    const handleStatusEvent = (event) => {

      try {

        const payload = JSON.parse(event.data);

        applyStatusPayload(payload);

        setStreamConnected(true);
        streamReadyRef.current = true;
        streamErrorNotifiedRef.current = false;

      } catch {

        // ignore invalid payload

      }

    };



    const handleStreamError = () => {

      setStreamConnected(false);
      setStreamReconnectCount((value) => value + 1);

      if (!streamReadyRef.current) return;

      if (!streamErrorNotifiedRef.current) {

        toast.error('Koneksi realtime terputus, mencoba menyambung ulang...');

        streamErrorNotifiedRef.current = true;

      }

    };



    source.addEventListener('status', handleStatusEvent);

    source.addEventListener('error', handleStreamError);



    return () => {

      source.removeEventListener('status', handleStatusEvent);

      source.removeEventListener('error', handleStreamError);

      source.close();

    };

  }, [applyStatusPayload, getAuthToken]);



  const customersWithStatus = useMemo(() => {

    return customers.map((customer) => {

      const status = statusByCustomerId[customer.id] || null;

      return {

        ...customer,

        connection_status: status?.connection_status || 'unknown',

        status_detail: status,
        pppoe_connection_status: status?.pppoe_connection_status || 'unknown',
        pppoe_status_detail: status?.pppoe_status_detail || null,

      };

    });

  }, [customers, statusByCustomerId]);



  const odpsWithCoordinates = useMemo(() => {

    if (!Array.isArray(odps) || odps.length === 0) return [];

    const odpIdByLookupKey = {};

    odps.forEach((odp) => {

      const odpId = String(odp?.id || '').trim();

      [odpId, odp?.code, odp?.name].forEach((value) => {

        const key = String(value || '').trim().toLowerCase();

        if (key && odpId) odpIdByLookupKey[key] = odpId;

      });

    });

    const pointsByOdpId = {};

    customers.forEach((customer) => {

      if (!hasValidCoordinates(customer?.latitude, customer?.longitude)) return;

      let odpId = String(customer?.odp_id || '').trim();

      if (!odpId) {

        const key = String(customer?.odp || '').trim().toLowerCase();

        odpId = key ? (odpIdByLookupKey[key] || '') : '';

      }

      if (!odpId) return;

      if (!Array.isArray(pointsByOdpId[odpId])) pointsByOdpId[odpId] = [];

      pointsByOdpId[odpId].push({ latitude: Number(customer.latitude), longitude: Number(customer.longitude) });

    });

    return odps.map((odp) => {

      if (hasValidCoordinates(odp?.latitude, odp?.longitude)) return odp;

      const odpId = String(odp?.id || '').trim();

      const points = pointsByOdpId[odpId] || [];

      if (points.length === 0) return odp;

      const averageLat = points.reduce((sum, point) => sum + point.latitude, 0) / points.length;

      const averageLon = points.reduce((sum, point) => sum + point.longitude, 0) / points.length;

      return {

        ...odp,

        latitude: Number(averageLat.toFixed(6)),

        longitude: Number(averageLon.toFixed(6)),

        fallback_from_customers: true,

      };

    });

  }, [odps, customers]);

  const nasByRouterId = useMemo(() => {
    const mapped = {};
    nasRouters.forEach((item) => {
      const key = normalizeNodeId(item?.router_id || item?.id);
      if (!key) return;
      mapped[key] = item;
    });
    return mapped;
  }, [nasRouters]);

  const nasOptions = useMemo(() => {
    return Object.entries(nasByRouterId)
      .map(([routerId, item]) => ({
        router_id: routerId,
        name: String(item?.name || item?.nas_identifier || routerId),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [nasByRouterId]);
  const nasRuntimeRows = useMemo(() => {
    return Object.entries(nasByRouterId)
      .map(([routerId, item]) => {
        const vpnRequired = toBooleanSafe(item?.vpn_required);
        const resolved = resolveRouterOnlineState(item);
        const isOnline = resolved.isOnline;
        const statusText = String(item?.status || '').trim().toLowerCase();
        return {
          routerId,
          name: String(item?.name || item?.nas_identifier || routerId),
          nasIdentifier: String(item?.nas_identifier || '').trim() || '-',
          serviceType: String(item?.service_type || '-').trim() || '-',
          vpnRequired,
          isOnline,
          systemReachable: !vpnRequired || resolved.isOnline,
          statusText: statusText || 'unknown',
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [nasByRouterId]);
  const nasOnlineCount = useMemo(() => nasRuntimeRows.filter((item) => item.systemReachable).length, [nasRuntimeRows]);
  const nasOfflineCount = useMemo(() => nasRuntimeRows.filter((item) => !item.systemReachable).length, [nasRuntimeRows]);

  useEffect(() => {
    if (activeOpsTab !== 'infrastruktur') return;
    const routerIds = nasRuntimeRows.map((item) => item.routerId).filter(Boolean);
    if (!routerIds.length) return;
    fetchRouterTrafficCurrent(routerIds);
  }, [activeOpsTab, nasRuntimeRows, fetchRouterTrafficCurrent]);

  const oltsById = useMemo(() => {
    const mapped = {};
    olts.forEach((item) => {
      const id = normalizeNodeId(item?.id);
      if (id) mapped[id] = item;
    });
    return mapped;
  }, [olts]);

  const odpsById = useMemo(() => {
    const mapped = {};
    odpsWithCoordinates.forEach((item) => {
      const id = normalizeNodeId(item?.id);
      if (id) mapped[id] = item;
    });
    return mapped;
  }, [odpsWithCoordinates]);

  const filteredOlts = useMemo(() => {
    return olts.filter((item) => {
      if (infraFilter !== 'all' && normalizeInfraKriteria(item.kriteria) !== infraFilter) return false;
      if (selectedNasId !== 'all' && normalizeNodeId(item.router_id) !== normalizeNodeId(selectedNasId)) return false;
      if (selectedOltId !== 'all' && normalizeNodeId(item.id) !== normalizeNodeId(selectedOltId)) return false;
      return true;
    });
  }, [infraFilter, olts, selectedNasId, selectedOltId]);

  const filteredOdps = useMemo(() => {
    return odpsWithCoordinates.filter((item) => {
      if (infraFilter !== 'all' && normalizeInfraKriteria(item.kriteria) !== infraFilter) return false;
      const linkedOlt = oltsById[normalizeNodeId(item.olt_id)];
      const linkedRouterId = normalizeNodeId(item.router_id || linkedOlt?.router_id);
      if (selectedNasId !== 'all' && linkedRouterId !== normalizeNodeId(selectedNasId)) return false;
      if (selectedOltId !== 'all' && normalizeNodeId(item.olt_id) !== normalizeNodeId(selectedOltId)) return false;
      return true;
    });
  }, [infraFilter, odpsWithCoordinates, oltsById, selectedNasId, selectedOltId]);



  const filteredOltsById = useMemo(() => {

    const mapped = {};

    filteredOlts.forEach((item) => {

      const id = normalizeNodeId(item?.id);

      if (id) mapped[id] = item;

    });

    return mapped;

  }, [filteredOlts]);



  const filteredOdpsById = useMemo(() => {

    const mapped = {};

    filteredOdps.forEach((item) => {

      const id = normalizeNodeId(item?.id);

      if (id) mapped[id] = item;

    });

    return mapped;

  }, [filteredOdps]);



  const allOdpsById = useMemo(() => {

    const mapped = {};

    odpsWithCoordinates.forEach((item) => {

      const id = normalizeNodeId(item?.id);

      if (id) mapped[id] = item;

    });

    return mapped;

  }, [odpsWithCoordinates]);



  const allOdpLookup = useMemo(() => {

    const mapped = {};

    odpsWithCoordinates.forEach((odp) => {

      [odp?.id, odp?.code, odp?.name].forEach((value) => {

        const key = String(value || '').trim().toLowerCase();

        const odpId = normalizeNodeId(odp?.id);

        if (key && odpId) mapped[key] = odpId;

      });

    });

    return mapped;

  }, [odpsWithCoordinates]);


  const filteredOdpLookup = useMemo(() => {

    const mapped = {};

    filteredOdps.forEach((odp) => {

      [odp?.id, odp?.code, odp?.name].forEach((value) => {

        const key = String(value || '').trim().toLowerCase();

        const odpId = normalizeNodeId(odp?.id);

        if (key && odpId) mapped[key] = odpId;

      });

    });

    return mapped;

  }, [filteredOdps]);



  const resolveFilteredOdp = useCallback((customer) => {

    const directOdpId = normalizeNodeId(customer?.linked_odp_id || customer?.odp_id);

    if (directOdpId && filteredOdpsById[directOdpId]) return filteredOdpsById[directOdpId];



    const odpKey = String(customer?.odp || '').trim().toLowerCase();

    const lookupOdpId = odpKey ? filteredOdpLookup[odpKey] : null;

    return lookupOdpId ? (filteredOdpsById[lookupOdpId] || null) : null;

  }, [filteredOdpLookup, filteredOdpsById]);



  const resolveCustomerOltId = useCallback((customer) => {

    const directOltId = normalizeNodeId(customer?.linked_olt_id || customer?.olt_id);

    if (directOltId) return directOltId;



    const directOdpId = normalizeNodeId(customer?.linked_odp_id || customer?.odp_id);

    if (directOdpId && allOdpsById[directOdpId]?.olt_id) {

      return normalizeNodeId(allOdpsById[directOdpId].olt_id);

    }



    const odpKey = String(customer?.odp || '').trim().toLowerCase();

    const lookupOdpId = odpKey ? allOdpLookup[odpKey] : null;

    if (lookupOdpId && allOdpsById[lookupOdpId]?.olt_id) {

      return normalizeNodeId(allOdpsById[lookupOdpId].olt_id);

    }



    return '';

  }, [allOdpLookup, allOdpsById]);

  const resolveCustomerRouterId = useCallback((customer) => {
    const directRouterId = normalizeNodeId(customer?.linked_router_id || customer?.router_id);
    if (directRouterId) return directRouterId;

    const directOdpId = normalizeNodeId(customer?.linked_odp_id || customer?.odp_id);
    if (directOdpId && allOdpsById[directOdpId]) {
      const odp = allOdpsById[directOdpId];
      const linkedOlt = oltsById[normalizeNodeId(odp?.olt_id)];
      const inferred = normalizeNodeId(odp?.router_id || linkedOlt?.router_id);
      if (inferred) return inferred;
    }

    const directOltId = resolveCustomerOltId(customer);
    if (directOltId && oltsById[directOltId]) {
      return normalizeNodeId(oltsById[directOltId].router_id);
    }

    return '';
  }, [allOdpsById, oltsById, resolveCustomerOltId]);



  const filteredCustomers = useMemo(() => {

    return customersWithStatus.filter((customer) => {

      if (statusFilter !== 'all' && customer.connection_status !== statusFilter) return false;

      if (infraFilter !== 'all' && !resolveFilteredOdp(customer)) return false;

      if (selectedNasId !== 'all' && resolveCustomerRouterId(customer) !== normalizeNodeId(selectedNasId)) return false;

      if (selectedOltId !== 'all' && resolveCustomerOltId(customer) !== normalizeNodeId(selectedOltId)) return false;



      const searchValue = searchQuery.trim();
      if (!searchValue) return true;
      if (searchValue.length < 3) return false;

      const key = searchValue.toLowerCase();

      return (

        String(customer.name || '').toLowerCase().includes(key) ||

        String(customer.customer_id || '').toLowerCase().includes(key) ||

        String(customer.phone || '').toLowerCase().includes(key) ||

        String(customer.odp || '').toLowerCase().includes(key)

      );

    });

  }, [customersWithStatus, statusFilter, infraFilter, selectedNasId, selectedOltId, searchQuery, resolveCustomerRouterId, resolveFilteredOdp, resolveCustomerOltId]);
  const nasWithCoordinates = useMemo(() => {
    const pointsByRouterId = {};
    filteredCustomers.forEach((customer) => {
      const routerId = resolveCustomerRouterId(customer);
      if (!routerId || !hasValidCoordinates(customer?.latitude, customer?.longitude)) return;
      if (!Array.isArray(pointsByRouterId[routerId])) pointsByRouterId[routerId] = [];
      pointsByRouterId[routerId].push({ lat: Number(customer.latitude), lng: Number(customer.longitude) });
    });

    return (Array.isArray(nasRouters) ? nasRouters : [])
      .map((router) => {
        const routerId = normalizeNodeId(router?.id || router?.router_id);
        if (!routerId) return null;
        const hasDirectCoord = hasValidCoordinates(router?.latitude, router?.longitude);
        if (hasDirectCoord) {
          return { ...router, id: routerId, latitude: Number(router.latitude), longitude: Number(router.longitude) };
        }
        const linkedPoints = pointsByRouterId[routerId] || [];
        if (linkedPoints.length === 0) return { ...router, id: routerId };
        const centroid = linkedPoints.reduce(
          (acc, item) => ({ lat: acc.lat + item.lat, lng: acc.lng + item.lng }),
          { lat: 0, lng: 0 },
        );
        return {
          ...router,
          id: routerId,
          latitude: centroid.lat / linkedPoints.length,
          longitude: centroid.lng / linkedPoints.length,
          inferred_from_customers: true,
        };
      })
      .filter(Boolean);
  }, [filteredCustomers, nasRouters, resolveCustomerRouterId]);
  const filteredNasWithCoordinates = useMemo(() => {
    return nasWithCoordinates.filter((item) => {
      const routerId = normalizeNodeId(item?.id || item?.router_id);
      if (!routerId) return false;
      if (selectedNasId !== 'all' && routerId !== normalizeNodeId(selectedNasId)) return false;
      return hasValidCoordinates(item?.latitude, item?.longitude);
    });
  }, [nasWithCoordinates, selectedNasId]);
  const filteredNasById = useMemo(() => {
    const mapped = {};
    filteredNasWithCoordinates.forEach((item) => {
      const id = normalizeNodeId(item?.id || item?.router_id);
      if (id) mapped[id] = item;
    });
    return mapped;
  }, [filteredNasWithCoordinates]);

  const customerConnectionMonitors = useMemo(() => {
    const normalizeRecoveryStatus = (raw) => {
      const value = String(raw || '').trim().toUpperCase();
      if (['IDLE', 'QUEUED', 'RUNNING', 'SUCCESS', 'FAILED', 'ESCALATED'].includes(value)) return value;
      if (value === 'IN_PROGRESS') return 'RUNNING';
      if (value === 'ERROR') return 'FAILED';
      return 'IDLE';
    };

    return filteredCustomers.map((customer) => {
      const onuStatus = normalizeOnuStatus(customer?.connection_status);
      const pppStatus = normalizePppoeStatus(customer?.pppoe_connection_status);
      const flapping = isNodeFlapping(customer);
      const state = deriveCorrelationState(onuStatus, pppStatus, flapping);
      const customerContext = customerContextById[String(customer?.id || '')] || null;
      const recoveryFromContext = customerContext?.recovery || null;
      const recoveryFromApi = recoveryStatusByCustomerId[String(customer?.id || '')] || null;
      const mockRecovery = buildMockRecoveryByCorrelation(customer?.id, state);
      const recovery = {
        eligible: Boolean(recoveryFromApi?.eligible ?? recoveryFromContext?.eligible ?? mockRecovery.eligible),
        status: normalizeRecoveryStatus(recoveryFromApi?.state || recoveryFromApi?.status || recoveryFromContext?.status || mockRecovery.status),
        retryCount: Number(recoveryFromApi?.attempts ?? recoveryFromApi?.retry_count ?? recoveryFromContext?.retry_count ?? recoveryFromContext?.retryCount ?? mockRecovery.retryCount ?? 0),
        maxRetry: Number(recoveryFromApi?.max_attempts ?? recoveryFromApi?.max_retry ?? recoveryFromContext?.max_retry ?? recoveryFromContext?.maxRetry ?? mockRecovery.maxRetry ?? 2),
        nextRecheckAt: recoveryFromApi?.next_recheck_at || recoveryFromApi?.nextRecheckAt || recoveryFromContext?.next_recheck_at || recoveryFromContext?.nextRecheckAt || mockRecovery.nextRecheckAt || null,
        waSent: Boolean(recoveryFromApi?.wa_sent ?? recoveryFromApi?.waSent ?? recoveryFromContext?.wa_sent ?? recoveryFromContext?.waSent ?? mockRecovery.waSent),
        ticketId: recoveryFromApi?.ticket_id || recoveryFromApi?.ticketId || recoveryFromContext?.ticket_id || recoveryFromContext?.ticketId || mockRecovery.ticketId || null,
      };

      const detectedAt = customer?.status_detail?.last_inform
        || customer?.pppoe_status_detail?.last_seen_at
        || customer?.updated_at
        || new Date().toISOString();
      const durationSec = Math.max(
        0,
        Math.floor((Date.now() - new Date(detectedAt).getTime()) / 1000),
      );
      const routerId = resolveCustomerRouterId(customer);
      const nasName = nasByRouterId[routerId]?.name || customer?.router_name || '-';

      return {
        customerId: String(customer?.id || ''),
        customerName: String(customer?.name || '-'),
        areaName: String(customer?.address || customer?.site_name || '-'),
        odpName: String(customer?.odp || ''),
        onu: {
          serialNumber: String(customer?.onu_serial || customer?.serial_number || '-'),
          status: onuStatus,
          oltName: String(customer?.linked_olt_name || ''),
          ponPort: String(customer?.pon_port || ''),
          onuId: String(customer?.onu_id || ''),
          opticalPower: Number.isFinite(Number(customer?.status_detail?.rx_power_dbm))
            ? Number(customer.status_detail.rx_power_dbm)
            : null,
          lastSeenAt: customer?.status_detail?.last_inform || null,
        },
        pppoe: {
          username: String(customer?.pppoe_username || '-'),
          status: pppStatus,
          nasName,
          ipAddress: customer?.pppoe_status_detail?.framed_ip || null,
          uptimeSec: Number(customer?.pppoe_status_detail?.uptime_seconds || 0) || null,
          lastSeenAt: customer?.pppoe_status_detail?.last_seen_at || null,
          disconnectReason: customer?.pppoe_status_detail?.last_disconnect_reason || null,
        },
        correlation: {
          state,
          detectedAt,
          durationSec,
          confidence: state === 'ONU_UP_PPP_DOWN' ? 0.95 : state === 'TOTAL_DOWN' ? 0.9 : 0.8,
        },
        recovery,
        location: hasValidCoordinates(customer?.latitude, customer?.longitude)
          ? { lat: Number(customer.latitude), lng: Number(customer.longitude) }
          : undefined,
      };
    });
  }, [customerContextById, filteredCustomers, nasByRouterId, recoveryStatusByCustomerId, resolveCustomerRouterId]);

  const filteredConnectionMonitors = useMemo(() => {
    if (monitorMode === 'onu') {
      return customerConnectionMonitors.filter((item) => item.onu.status !== 'UNKNOWN');
    }
    if (monitorMode === 'pppoe') {
      return customerConnectionMonitors.filter((item) => item.pppoe.status !== 'UNKNOWN');
    }
    if (monitorMode === 'korelasi') {
      return customerConnectionMonitors.filter((item) => item.correlation.state !== 'NORMAL');
    }
    if (monitorMode === 'recovery') {
      return customerConnectionMonitors.filter((item) => item.recovery.status !== 'IDLE');
    }
    return customerConnectionMonitors;
  }, [customerConnectionMonitors, monitorMode]);

  const filteredMonitorIdSet = useMemo(
    () => new Set(filteredConnectionMonitors.map((item) => String(item.customerId || ''))),
    [filteredConnectionMonitors],
  );

  useEffect(() => {
    if (selectedOltId === 'all') return;
    const exists = filteredOlts.some((item) => normalizeNodeId(item.id) === normalizeNodeId(selectedOltId));
    if (!exists) setSelectedOltId('all');
  }, [filteredOlts, selectedOltId]);

  useEffect(() => {
    if (selectedNasId === 'all') return;
    if (!nasByRouterId[normalizeNodeId(selectedNasId)]) {
      setSelectedNasId('all');
    }
  }, [nasByRouterId, selectedNasId]);

  useEffect(() => {
    if (selectedEntityType !== 'odp') return;
    if (!selectedOdpId) return;
    if (!filteredOdpsById[normalizeNodeId(selectedOdpId)]) {
      clearSelectedEntity();
    }
  }, [clearSelectedEntity, filteredOdpsById, selectedEntityType, selectedOdpId]);

  useEffect(() => {
    if (!selectedOdpId) return;
    setOdpScopeRenderTick(Date.now());
  }, [selectedOdpId]);
  useEffect(() => {
    if (selectedEntityType !== 'router') return;
    if (!selectedMapRouterId) return;
    if (!nasByRouterId[normalizeNodeId(selectedMapRouterId)]) {
      clearSelectedEntity();
    }
  }, [clearSelectedEntity, nasByRouterId, selectedEntityType, selectedMapRouterId]);

  const customerOltsForForm = useMemo(() => {
    const selectedRouterId = normalizeNodeId(customerForm.router_id);
    if (!selectedRouterId) return olts;
    return olts.filter((item) => normalizeNodeId(item?.router_id) === selectedRouterId);
  }, [customerForm.router_id, olts]);

  const customerOdpsForForm = useMemo(() => {
    const selectedOlt = normalizeNodeId(customerForm.olt_id);
    if (selectedOlt) {
      return odpsWithCoordinates.filter((item) => normalizeNodeId(item?.olt_id) === selectedOlt);
    }
    const selectedRouterId = normalizeNodeId(customerForm.router_id);
    if (!selectedRouterId) return odpsWithCoordinates;
    return odpsWithCoordinates.filter((item) => {
      const linkedOlt = oltsById[normalizeNodeId(item?.olt_id)];
      const linkedRouterId = normalizeNodeId(item?.router_id || linkedOlt?.router_id);
      return linkedRouterId === selectedRouterId;
    });
  }, [customerForm.olt_id, customerForm.router_id, odpsWithCoordinates, oltsById]);

  const odpUsageById = useMemo(() => {
    const mapped = {};
    (customers || []).forEach((item) => {
      const id = normalizeNodeId(item?.odp_id);
      if (!id) return;
      mapped[id] = (mapped[id] || 0) + 1;
    });
    return mapped;
  }, [customers]);

  const distributionCards = useMemo(() => {
    const keyword = String(distributionSearch || '').trim().toLowerCase();
    const rows = customerOdpsForForm.map((item) => {
      const id = normalizeNodeId(item?.id);
      const linkedOlt = oltsById[normalizeNodeId(item?.olt_id)];
      const capacity = Number(item?.odp_capacity || 8);
      const used = Number(item?.odp_customer_count ?? odpUsageById[id] ?? 0);
      const free = Math.max(0, (Number.isFinite(capacity) && capacity > 0 ? capacity : 8) - used);
      return {
        id,
        code: String(item?.code || '').trim(),
        name: String(item?.name || '').trim(),
        oltName: String(linkedOlt?.code || linkedOlt?.name || '-').trim(),
        used,
        free,
      };
    }).filter((item) => item.id);
    if (!keyword) return rows.slice(0, 10);
    return rows.filter((item) => `${item.code} ${item.name} ${item.oltName}`.toLowerCase().includes(keyword)).slice(0, 10);
  }, [customerOdpsForForm, distributionSearch, oltsById, odpUsageById]);

  useEffect(() => {
    const selectedOdp = odpsById[normalizeNodeId(customerForm.odp_id)];
    if (!selectedOdp) return;
    const linkedOltId = normalizeNodeId(selectedOdp.olt_id);
    const linkedOlt = oltsById[linkedOltId];
    const inferredRouterId = normalizeNodeId(selectedOdp.router_id || linkedOlt?.router_id);
    setCustomerForm((prev) => {
      const nextRouterId = inferredRouterId || prev.router_id;
      const nextOltId = linkedOltId || prev.olt_id;
      if (prev.router_id === nextRouterId && prev.olt_id === nextOltId) return prev;
      return { ...prev, router_id: nextRouterId, olt_id: nextOltId };
    });
  }, [customerForm.odp_id, odpsById, oltsById]);

  const overlapNodePositionLookup = useMemo(() => {
    const infraNodes = [
      ...filteredOlts
        .filter((item) => hasValidCoordinates(item.latitude, item.longitude))
        .map((item) => ({ id: `olt:${item.id}`, latitude: Number(item.latitude), longitude: Number(item.longitude) })),
      ...filteredOdps
        .filter((item) => hasValidCoordinates(item.latitude, item.longitude))
        .map((item) => ({ id: `odp:${item.id}`, latitude: Number(item.latitude), longitude: Number(item.longitude) })),
      ...filteredNasWithCoordinates
        .filter((item) => hasValidCoordinates(item.latitude, item.longitude))
        .map((item) => ({ id: `nas:${item.id || item.router_id}`, latitude: Number(item.latitude), longitude: Number(item.longitude) })),
    ];
    return buildOverlapPositionLookup(infraNodes);
  }, [filteredOlts, filteredOdps, filteredNasWithCoordinates]);

  const getInfraDisplayPosition = useCallback((type, node) => {
    const id = String(node?.id || '').trim();
    if (!id || !hasValidCoordinates(node?.latitude, node?.longitude)) return null;
    const key = `${type}:${id}`;
    return overlapNodePositionLookup[key] || [Number(node.latitude), Number(node.longitude)];
  }, [overlapNodePositionLookup]);



  const selectedCustomer = useMemo(() => {

    if (!selectedCustomerId) return null;

    return filteredCustomers.find((item) => item.id === selectedCustomerId && filteredMonitorIdSet.has(String(item.id || ''))) || null;

  }, [filteredCustomers, filteredMonitorIdSet, selectedCustomerId]);

  const selectedCustomerContext = useMemo(
    () => (selectedCustomerId ? customerContextById[selectedCustomerId] || null : null),
    [customerContextById, selectedCustomerId],
  );

  const monitorFilteredCustomers = useMemo(
    () => filteredCustomers.filter((customer) => filteredMonitorIdSet.has(String(customer.id || ''))),
    [filteredCustomers, filteredMonitorIdSet],
  );

  const visibleCustomerPoints = useMemo(
    () => monitorFilteredCustomers.filter((customer) => hasValidCoordinates(customer.latitude, customer.longitude)),
    [monitorFilteredCustomers],
  );
  const selectedOdp = useMemo(() => {
    const normalized = normalizeNodeId(selectedOdpId);
    if (!normalized) return null;
    return filteredOdpsById[normalized] || null;
  }, [filteredOdpsById, selectedOdpId]);
  const selectedMapRouter = useMemo(() => {
    const normalized = normalizeNodeId(selectedMapRouterId);
    if (!normalized) return null;
    return filteredNasById[normalized] || nasByRouterId[normalized] || null;
  }, [filteredNasById, nasByRouterId, selectedMapRouterId]);
  const selectedCustomerOdp = useMemo(() => {
    if (!selectedCustomer) return null;
    return resolveFilteredOdp(selectedCustomer) || null;
  }, [resolveFilteredOdp, selectedCustomer]);
  const selectedCustomerOdpNormalizedId = useMemo(
    () => normalizeNodeId(selectedCustomerOdp?.id),
    [selectedCustomerOdp],
  );
  const selectedCustomerOltNormalizedId = useMemo(
    () => normalizeNodeId(selectedCustomerOdp?.olt_id),
    [selectedCustomerOdp],
  );
  const selectedCustomerRouterNormalizedId = useMemo(
    () => normalizeNodeId(resolveCustomerRouterId(selectedCustomer)),
    [resolveCustomerRouterId, selectedCustomer],
  );
  const selectedOdpNormalizedId = useMemo(() => normalizeNodeId(selectedOdp?.id), [selectedOdp]);
  const selectedMapRouterNormalizedId = useMemo(() => normalizeNodeId(selectedMapRouter?.id || selectedMapRouter?.router_id), [selectedMapRouter]);
  const selectedOltForOdp = useMemo(() => {
    if (!selectedOdp) return null;
    return filteredOltsById[normalizeNodeId(selectedOdp?.olt_id)] || null;
  }, [filteredOltsById, selectedOdp]);
  const odpScopedCustomerPoints = useMemo(() => {
    if (!selectedOdpNormalizedId) return [];
    return visibleCustomerPoints.filter((customer) => {
      const odp = resolveFilteredOdp(customer);
      return normalizeNodeId(odp?.id) === selectedOdpNormalizedId;
    });
  }, [resolveFilteredOdp, selectedOdpNormalizedId, visibleCustomerPoints]);
  const hasOdpScopeFocus = Boolean(selectedOdpNormalizedId);
  const routerScopedCustomerPoints = useMemo(() => {
    if (!selectedMapRouterNormalizedId) return [];
    return visibleCustomerPoints.filter(
      (customer) => normalizeNodeId(resolveCustomerRouterId(customer)) === selectedMapRouterNormalizedId,
    );
  }, [resolveCustomerRouterId, selectedMapRouterNormalizedId, visibleCustomerPoints]);
  const hasRouterScopeFocus = Boolean(selectedMapRouterNormalizedId);
  const hasCustomerScopeFocus = Boolean(selectedCustomerId);
  const scopedVisibleCustomerPoints = useMemo(() => {
    if (hasOdpScopeFocus) return odpScopedCustomerPoints;
    if (hasRouterScopeFocus) return routerScopedCustomerPoints;
    if (hasCustomerScopeFocus && selectedCustomer) return [selectedCustomer];
    return visibleCustomerPoints;
  }, [hasCustomerScopeFocus, hasOdpScopeFocus, hasRouterScopeFocus, odpScopedCustomerPoints, routerScopedCustomerPoints, selectedCustomer, visibleCustomerPoints]);
  const shouldClusterCustomers = useMemo(
    () => !hasOdpScopeFocus && !hasRouterScopeFocus && !hasCustomerScopeFocus && Number(mapZoom || 13) < 14,
    [hasCustomerScopeFocus, hasOdpScopeFocus, hasRouterScopeFocus, mapZoom],
  );
  const customerClusters = useMemo(
    () => (shouldClusterCustomers ? buildCustomerClusters(visibleCustomerPoints, mapZoom) : []),
    [mapZoom, shouldClusterCustomers, visibleCustomerPoints],
  );
  const renderedCustomerPoints = useMemo(
    () => (shouldClusterCustomers ? [] : visibleCustomerPoints),
    [shouldClusterCustomers, visibleCustomerPoints],
  );
  const odpScopedCustomerIdSet = useMemo(
    () => new Set(odpScopedCustomerPoints.map((item) => String(item?.id || ''))),
    [odpScopedCustomerPoints],
  );
  const routerScopedCustomerIdSet = useMemo(
    () => new Set(routerScopedCustomerPoints.map((item) => String(item?.id || ''))),
    [routerScopedCustomerPoints],
  );
  const enableScopedLineAnimation = mapLayerVisibility.animate && odpScopedCustomerPoints.length <= 180;
  const enableRouterScopedLineAnimation = mapLayerVisibility.animate && routerScopedCustomerPoints.length <= 220;
  const technicianTaskPoints = useMemo(() => {
    return (Array.isArray(technicianTasks) ? technicianTasks : [])
      .filter((task) => hasValidCoordinates(task?.location?.lat, task?.location?.lng))
      .map((task) => ({
        id: task.id,
        lat: Number(task.location.lat),
        lng: Number(task.location.lng),
        priority: task.priority,
        status: task.status,
        customer_id: task.customer_id,
        issue_type: task.issue_type,
      }));
  }, [technicianTasks]);

  const customerCountByOdpId = useMemo(() => {
    const mapped = {};
    filteredCustomers.forEach((customer) => {
      const odp = resolveFilteredOdp(customer);
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId) return;
      mapped[odpId] = (mapped[odpId] || 0) + 1;
    });
    return mapped;
  }, [filteredCustomers, resolveFilteredOdp]);

  const odpRuntimeStatusById = useMemo(() => {
    const mapped = {};
    filteredOdps.forEach((odp) => {
      const odpId = normalizeNodeId(odp?.id);
      if (odpId) mapped[odpId] = 'offline';
    });
    filteredCustomers.forEach((customer) => {
      const odp = resolveFilteredOdp(customer);
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId) return;
      if (String(customer.connection_status || '').toLowerCase() === 'online') {
        mapped[odpId] = 'online';
      } else if (!mapped[odpId]) {
        mapped[odpId] = 'offline';
      }
    });
    return mapped;
  }, [filteredCustomers, filteredOdps, resolveFilteredOdp]);

  const oltCustomerCountById = useMemo(() => {
    const mapped = {};
    filteredOdps.forEach((odp) => {
      const oltId = normalizeNodeId(odp?.olt_id);
      const odpId = normalizeNodeId(odp?.id);
      if (!oltId || !odpId) return;
      mapped[oltId] = (mapped[oltId] || 0) + Number(customerCountByOdpId[odpId] || 0);
    });
    return mapped;
  }, [customerCountByOdpId, filteredOdps]);

  const oltRuntimeStatusById = useMemo(() => {
    const mapped = {};
    filteredOlts.forEach((olt) => {
      const oltId = normalizeNodeId(olt?.id);
      if (oltId) mapped[oltId] = 'offline';
    });
    filteredOdps.forEach((odp) => {
      const oltId = normalizeNodeId(odp?.olt_id);
      const odpId = normalizeNodeId(odp?.id);
      if (!oltId || !odpId) return;
      const odpStatus = String(odpRuntimeStatusById[odpId] || 'offline').toLowerCase();
      if (odpStatus === 'online') mapped[oltId] = 'online';
    });
    return mapped;
  }, [filteredOdps, filteredOlts, odpRuntimeStatusById]);




  const odpCoverageZones = useMemo(() => {
    const customerSource = scopedVisibleCustomerPoints;

    const mapped = {};

    customerSource.forEach((customer) => {

      const odp = resolveFilteredOdp(customer);

      if (!odp?.id) return;

      if (!hasValidCoordinates(odp.latitude, odp.longitude)) return;

      if (!mapped[odp.id]) {

        mapped[odp.id] = {

          id: odp.id,

          latitude: Number(odp.latitude),

          longitude: Number(odp.longitude),

          count: 0,

        };

      }

      mapped[odp.id].count += 1;

    });



    return Object.values(mapped).map((zone) => ({

      ...zone,

      radius: 130 + Math.min(zone.count * 18, 190),

    }));

  }, [resolveFilteredOdp, scopedVisibleCustomerPoints]);
  const mapPoints = useMemo(() => {

    const points = [];
    const customerSource = scopedVisibleCustomerPoints;

    customerSource.forEach((item) => points.push([Number(item.latitude), Number(item.longitude)]));

    filteredOdps.forEach((item) => {
      if (hasOdpScopeFocus && normalizeNodeId(item?.id) !== selectedOdpNormalizedId) return;
      if (hasRouterScopeFocus) {
        const linkedOlt = filteredOltsById[normalizeNodeId(item?.olt_id)];
        const linkedRouterId = normalizeNodeId(item?.router_id || linkedOlt?.router_id);
        if (linkedRouterId !== selectedMapRouterNormalizedId) return;
      }

      if (hasValidCoordinates(item.latitude, item.longitude)) points.push([Number(item.latitude), Number(item.longitude)]);

    });

    filteredOlts.forEach((item) => {
      if (hasOdpScopeFocus && normalizeNodeId(item?.id) !== normalizeNodeId(selectedOltForOdp?.id)) return;
      if (hasRouterScopeFocus && normalizeNodeId(item?.router_id) !== selectedMapRouterNormalizedId) return;

      if (hasValidCoordinates(item.latitude, item.longitude)) points.push([Number(item.latitude), Number(item.longitude)]);

    });
    filteredNasWithCoordinates.forEach((item) => {
      const routerId = normalizeNodeId(item?.id || item?.router_id);
      if (hasOdpScopeFocus) {
        const linkedRouterId = normalizeNodeId(selectedOdp?.router_id || selectedOltForOdp?.router_id);
        if (routerId !== linkedRouterId) return;
      }
      if (hasRouterScopeFocus && routerId !== selectedMapRouterNormalizedId) return;
      if (hasValidCoordinates(item?.latitude, item?.longitude)) {
        points.push([Number(item.latitude), Number(item.longitude)]);
      }
    });

    technicianTaskPoints.forEach((item) => {
      points.push([item.lat, item.lng]);
    });

    return points;

  }, [scopedVisibleCustomerPoints, filteredOdps, hasOdpScopeFocus, selectedOdpNormalizedId, hasRouterScopeFocus, filteredOltsById, selectedMapRouterNormalizedId, filteredOlts, selectedOltForOdp, filteredNasWithCoordinates, selectedOdp, technicianTaskPoints]);

  const onlineCount = filteredCustomers.filter((customer) => customer.connection_status === 'online').length;

  const offlineCount = filteredCustomers.filter((customer) => customer.connection_status === 'offline').length;

  const unknownCount = filteredCustomers.filter((customer) => customer.connection_status !== 'online' && customer.connection_status !== 'offline').length;

  const pppActiveCount = filteredConnectionMonitors.filter((item) => item.pppoe.status === 'UP').length;
  const pppDownCount = filteredConnectionMonitors.filter((item) => item.pppoe.status === 'DOWN').length;
  const onuUpPppDownCount = filteredConnectionMonitors.filter((item) => item.correlation.state === 'ONU_UP_PPP_DOWN').length;
  const recoveryRunningCount = filteredConnectionMonitors.filter((item) => ['RUNNING', 'QUEUED'].includes(item.recovery.status)).length;
  const recoveryFailedCount = filteredConnectionMonitors.filter((item) => item.recovery.status === 'FAILED').length;
  const escalatedCount = filteredConnectionMonitors.filter((item) => item.recovery.status === 'ESCALATED').length;

  const selectedConnectionMonitor = useMemo(
    () => filteredConnectionMonitors.find((item) => String(item.customerId || '') === String(selectedCustomerId || '')) || null,
    [filteredConnectionMonitors, selectedCustomerId],
  );
  const selectedRouterMonitorRows = useMemo(() => {
    const routerId = normalizeNodeId(selectedMapRouter?.id || selectedMapRouter?.router_id);
    if (!routerId) return [];
    return filteredConnectionMonitors.filter((row) => {
      const customer = filteredCustomers.find((item) => String(item?.id || '') === String(row?.customerId || ''));
      if (!customer) return false;
      return normalizeNodeId(resolveCustomerRouterId(customer)) === routerId;
    });
  }, [filteredConnectionMonitors, filteredCustomers, resolveCustomerRouterId, selectedMapRouter]);
  const selectedRouterSummary = useMemo(() => {
    const routerId = normalizeNodeId(selectedMapRouter?.id || selectedMapRouter?.router_id);
    if (!routerId) return null;
    const linkedCustomers = visibleCustomerPoints.filter(
      (customer) => normalizeNodeId(resolveCustomerRouterId(customer)) === routerId,
    );
    const htbCount = linkedCustomers.filter((customer) => String(customer?.accessType || customer?.access_type || '').toUpperCase() === 'HTB_PPPOE').length;
    const ftthCount = linkedCustomers.filter((customer) => String(customer?.accessType || customer?.access_type || '').toUpperCase() === 'FTTH_PPPOE').length;
    const monitorRows = selectedRouterMonitorRows;
    const pppDown = monitorRows.filter((row) => String(row?.pppoe?.status || '').toUpperCase() === 'DOWN').length;
    const pppUp = monitorRows.filter((row) => String(row?.pppoe?.status || '').toUpperCase() === 'UP').length;
    const trafficPayload = routerTrafficById[routerId] || {};
    const interfaces = Array.isArray(trafficPayload?.interfaces) ? trafficPayload.interfaces : [];
    const totalIn = interfaces.reduce((sum, item) => sum + Number(item?.in_bps || 0), 0);
    const totalOut = interfaces.reduce((sum, item) => sum + Number(item?.out_bps || 0), 0);
    return {
      routerId,
      linkedCustomerCount: linkedCustomers.length,
      htbCount,
      ftthCount,
      pppDown,
      pppUp,
      totalIn,
      totalOut,
      interfacesCount: interfaces.length,
    };
  }, [resolveCustomerRouterId, routerTrafficById, selectedMapRouter, selectedRouterMonitorRows, visibleCustomerPoints]);
  const selectedOdpSummary = useMemo(() => {
    if (!selectedOdp) return null;
    const odpId = normalizeNodeId(selectedOdp?.id);
    if (!odpId) return null;
    const linkedCustomers = visibleCustomerPoints.filter((customer) => {
      const odp = resolveFilteredOdp(customer);
      return normalizeNodeId(odp?.id) === odpId;
    });
    const downCount = linkedCustomers.filter((item) => String(item?.connection_status || '').toLowerCase() === 'offline').length;
    const degradedCount = linkedCustomers.length - downCount;
    const linkedOlt = filteredOltsById[normalizeNodeId(selectedOdp?.olt_id)] || null;
    return {
      odpId,
      linkedCustomerCount: linkedCustomers.length,
      downCount,
      degradedCount,
      linkedOltName: linkedOlt?.name || linkedOlt?.code || '-',
    };
  }, [filteredOltsById, resolveFilteredOdp, selectedOdp, visibleCustomerPoints]);
  const connectionMonitorById = useMemo(() => {
    const mapped = {};
    filteredConnectionMonitors.forEach((item) => {
      mapped[String(item.customerId || '')] = item;
    });
    return mapped;
  }, [filteredConnectionMonitors]);
  const aiNodeInsights = useMemo(() => {
    const byId = {};
    let totalAnomaly = 0;
    let latencyHigh = 0;
    let signalWeak = 0;
    let offline = 0;
    let predictiveCount = 0;
    let mostCriticalNode = null;
    let mostCriticalScore = -1;

    filteredCustomers.forEach((customer) => {
      const anomaly = detectAnomaly(customer);
      const predictions = predictIssue(customer);
      const hasPredictive = predictions.length > 0;
      const score = anomaly.issues.reduce((acc, item) => acc + (item.severity === 'high' ? 2 : 1), 0);

      if (score > mostCriticalScore && hasValidCoordinates(customer.latitude, customer.longitude)) {
        mostCriticalScore = score;
        mostCriticalNode = customer;
      }
      if (anomaly.hasAnomaly) totalAnomaly += 1;
      if (anomaly.issues.some((item) => item.code === 'latency_high')) latencyHigh += 1;
      if (anomaly.issues.some((item) => item.code === 'signal_weak')) signalWeak += 1;
      if (anomaly.issues.some((item) => item.code === 'offline')) offline += 1;
      if (hasPredictive) predictiveCount += 1;

      byId[String(customer.id || '')] = { anomaly, predictions, hasPredictive };
    });

    return {
      byId,
      summary: { totalAnomaly, latencyHigh, signalWeak, offline, predictiveCount },
      mostCriticalNode,
    };
  }, [filteredCustomers]);

  const anomalyByOdpId = useMemo(() => {
    const mapped = {};
    filteredCustomers.forEach((customer) => {
      const odp = resolveFilteredOdp(customer);
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId) return;
      const insight = aiNodeInsights.byId[String(customer.id || '')];
      if (insight?.anomaly?.hasAnomaly) mapped[odpId] = true;
    });
    return mapped;
  }, [filteredCustomers, resolveFilteredOdp, aiNodeInsights.byId]);

  const anomalyByOltId = useMemo(() => {
    const mapped = {};
    filteredCustomers.forEach((customer) => {
      const oltId = resolveCustomerOltId(customer);
      if (!oltId) return;
      const insight = aiNodeInsights.byId[String(customer.id || '')];
      if (insight?.anomaly?.hasAnomaly) mapped[normalizeNodeId(oltId)] = true;
    });
    return mapped;
  }, [filteredCustomers, resolveCustomerOltId, aiNodeInsights.byId]);

  const metricsHealth = systemMetrics?.genieacs_health || null;
  const metricsTransfer = systemMetrics?.transfer_approval || null;
  const metricsSource = metricsHealth?.last_source?.source || '-';
  const metricsLatencyAvg = Number.isFinite(Number(metricsHealth?.avg_latency_ms)) ? `${Number(metricsHealth?.avg_latency_ms)} ms` : '-';
  const metricsLatencyP95 = Number.isFinite(Number(metricsHealth?.p95_latency_ms)) ? `${Number(metricsHealth?.p95_latency_ms)} ms` : '-';
  const mapSearchResults = useMemo(() => {
    const key = searchQuery.trim();
    if (key.length < 3) return [];
    return filteredCustomers.slice(0, 12);
  }, [filteredCustomers, searchQuery]);



  const openFieldTickets = useMemo(() => fieldTickets.filter((ticket) => !doneTicketStatuses.has(ticket.status)), [fieldTickets]);

  const completedFieldTickets = useMemo(() => fieldTickets.filter((ticket) => doneTicketStatuses.has(ticket.status)), [fieldTickets]);
  const canResolveMonitoringIncidents = useMemo(() => {
    const role = String(user?.role || '').trim().toLowerCase();
    return ['superadmin', 'admin', 'noc'].includes(role);
  }, [user?.role]);
  const monitoringIncidentSummary = useMemo(() => {
    const summary = {
      total: 0,
      open: 0,
      monitoring: 0,
      resolved: 0,
      falsePositive: 0,
      critical: 0,
      high: 0,
    };
    (Array.isArray(monitoringIncidents) ? monitoringIncidents : []).forEach((incident) => {
      summary.total += 1;
      const status = String(incident?.status || '').toLowerCase();
      if (status === 'open') summary.open += 1;
      else if (status === 'monitoring') summary.monitoring += 1;
      else if (status === 'resolved') summary.resolved += 1;
      else if (status === 'false_positive') summary.falsePositive += 1;
      const severity = String(incident?.severity || '').toLowerCase();
      if (severity === 'critical') summary.critical += 1;
      if (severity === 'high') summary.high += 1;
    });
    return summary;
  }, [monitoringIncidents]);
  const monitoringIncidentsBySelectedCustomer = useMemo(() => {
    const customerId = String(selectedCustomerId || '').trim();
    if (!customerId) return [];
    return (Array.isArray(monitoringIncidents) ? monitoringIncidents : []).filter((incident) => {
      const sourceType = normalizeIncidentSourceType(incident?.root_source_type);
      const sourceId = String(incident?.root_source_id || '').trim();
      return sourceType === 'customer' && sourceId === customerId;
    });
  }, [monitoringIncidents, selectedCustomerId]);
  const monitoringIncidentsBySelectedTask = useMemo(() => {
    const taskCustomerId = String(
      selectedTaskDetail?.context?.customer?.id
      || technicianTasks.find((task) => String(task?.id || '') === String(selectedTaskId || ''))?.customer_id
      || '',
    ).trim();
    if (!taskCustomerId) return [];
    return (Array.isArray(monitoringIncidents) ? monitoringIncidents : [])
      .filter((incident) => {
        const sourceType = normalizeIncidentSourceType(incident?.root_source_type);
        const sourceId = String(incident?.root_source_id || '').trim();
        return sourceType === 'customer' && sourceId === taskCustomerId;
      })
      .sort((a, b) => String(b?.updated_at || '').localeCompare(String(a?.updated_at || '')));
  }, [monitoringIncidents, selectedTaskDetail?.context?.customer?.id, selectedTaskId, technicianTasks]);

  const aiMapTargets = useMemo(() => {
    const extractIdArray = (value) => {
      if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean);
      if (typeof value === 'string') {
        return value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return [];
    };
    const extractReasonLabels = (incident) => {
      const probableCauses = Array.isArray(incident?.probable_causes) ? incident.probable_causes : [];
      const causeLabels = probableCauses
        .map((cause) => String(cause?.label || cause?.code || '').trim())
        .filter(Boolean);
      if (causeLabels.length > 0) return causeLabels.slice(0, 4);
      const fallbackReason = String(incident?.summary || incident?.description || '').trim();
      return fallbackReason ? [fallbackReason] : ['Perubahan pola monitoring terdeteksi'];
    };
    const map = {};
    const pushTarget = (target) => {
      const targetId = String(target?.id || '').trim();
      if (!targetId) return;
      if (map[targetId]) return;
      map[targetId] = {
        ...target,
        reasons: Array.isArray(target?.reasons) ? target.reasons.slice(0, 5) : [],
        relatedCustomerIds: Array.isArray(target?.relatedCustomerIds) ? Array.from(new Set(target.relatedCustomerIds.map((item) => String(item || '').trim()).filter(Boolean))) : [],
        relatedOdpIds: Array.isArray(target?.relatedOdpIds) ? Array.from(new Set(target.relatedOdpIds.map((item) => String(item || '').trim()).filter(Boolean))) : [],
        relatedRouterIds: Array.isArray(target?.relatedRouterIds) ? Array.from(new Set(target.relatedRouterIds.map((item) => String(item || '').trim()).filter(Boolean))) : [],
      };
    };

    (Array.isArray(monitoringIncidents) ? monitoringIncidents : []).forEach((incident) => {
      const incidentId = String(incident?.id || '').trim();
      if (!incidentId) return;
      const status = String(incident?.status || '').trim().toLowerCase();
      if (status === 'resolved' || status === 'false_positive') return;

      const sourceType = normalizeIncidentSourceType(incident?.root_source_type);
      const sourceId = String(incident?.root_source_id || '').trim();
      const severity = normalizeAiSeverity(incident?.severity);
      const confidence = Math.max(0.3, Math.min(0.99, Number(incident?.confidence || 0.62)));
      const reasons = extractReasonLabels(incident);
      const label = String(incident?.title || incident?.incident_type || 'Anomali Monitoring')
        .trim()
        .replace(/_/g, ' ');
      const relatedCustomerIds = extractIdArray(incident?.affected_customer_ids || incident?.customer_ids);
      const relatedOdpIds = extractIdArray(incident?.affected_odp_ids || incident?.odp_ids);
      const relatedRouterIds = extractIdArray(incident?.affected_router_ids || incident?.router_ids || incident?.nas_ids);
      const incidentCoordinates = resolveIncidentCoordinates(incident);

      if (sourceType === 'customer' && sourceId) {
        const customer = filteredCustomers.find((item) => String(item?.id || '') === sourceId) || null;
        if (!customer || !hasValidCoordinates(customer?.latitude, customer?.longitude)) return;
        pushTarget({
          id: `incident-customer-${incidentId}`,
          type: 'CUSTOMER_ALERT',
          targetEntityType: 'CUSTOMER',
          targetEntityId: sourceId,
          severity,
          confidence,
          label,
          reasons,
          coordinates: { latitude: Number(customer.latitude), longitude: Number(customer.longitude) },
          relatedCustomerIds: [sourceId, ...relatedCustomerIds],
          relatedOdpIds,
          relatedRouterIds,
        });
        return;
      }

      if (sourceType === 'odp' && sourceId) {
        const odp = filteredOdpsById[normalizeNodeId(sourceId)] || null;
        if (!odp || !hasValidCoordinates(odp?.latitude, odp?.longitude)) return;
        pushTarget({
          id: `incident-odp-${incidentId}`,
          type: 'ODP_ANOMALY',
          targetEntityType: 'ODP',
          targetEntityId: normalizeNodeId(odp?.id || sourceId),
          severity,
          confidence,
          label,
          reasons,
          coordinates: { latitude: Number(odp.latitude), longitude: Number(odp.longitude) },
          relatedCustomerIds,
          relatedOdpIds: [normalizeNodeId(odp?.id || sourceId), ...relatedOdpIds],
          relatedRouterIds,
        });
        return;
      }

      if ((sourceType === 'nas' || sourceType === 'topology_node') && sourceId) {
        const router = filteredNasById[normalizeNodeId(sourceId)] || nasByRouterId[normalizeNodeId(sourceId)] || null;
        if (!router || !hasValidCoordinates(router?.latitude, router?.longitude)) return;
        pushTarget({
          id: `incident-router-${incidentId}`,
          type: 'ROUTER_ANOMALY',
          targetEntityType: 'ROUTER',
          targetEntityId: normalizeNodeId(router?.id || router?.router_id || sourceId),
          severity,
          confidence,
          label,
          reasons,
          coordinates: { latitude: Number(router.latitude), longitude: Number(router.longitude) },
          relatedCustomerIds,
          relatedOdpIds,
          relatedRouterIds: [normalizeNodeId(router?.id || router?.router_id || sourceId), ...relatedRouterIds],
        });
        return;
      }

      if (sourceType === 'cluster' && incidentCoordinates) {
        pushTarget({
          id: `incident-cluster-${incidentId}`,
          type: 'AREA_INCIDENT',
          targetEntityType: 'AREA',
          targetEntityId: incidentId,
          severity,
          confidence,
          label,
          reasons,
          coordinates: incidentCoordinates,
          relatedCustomerIds,
          relatedOdpIds,
          relatedRouterIds,
        });
      }
    });

    const odpAnomalyCluster = {};
    filteredCustomers.forEach((customer) => {
      const customerId = String(customer?.id || '').trim();
      if (!customerId) return;
      const insight = aiNodeInsights.byId[customerId];
      if (!insight?.anomaly?.hasAnomaly) return;
      const odp = resolveFilteredOdp(customer);
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId || !hasValidCoordinates(odp?.latitude, odp?.longitude)) return;
      if (!odpAnomalyCluster[odpId]) {
        odpAnomalyCluster[odpId] = {
          odp,
          customerIds: [],
          downCount: 0,
          issueCount: 0,
        };
      }
      odpAnomalyCluster[odpId].customerIds.push(customerId);
      if (String(customer?.connection_status || '').toLowerCase() === 'offline') {
        odpAnomalyCluster[odpId].downCount += 1;
      }
      odpAnomalyCluster[odpId].issueCount += Array.isArray(insight?.anomaly?.issues) ? insight.anomaly.issues.length : 0;
    });

    Object.entries(odpAnomalyCluster).forEach(([odpId, payload]) => {
      const impacted = payload.customerIds.length;
      if (impacted < 3) return;
      const severity = payload.downCount >= 2 || impacted >= 6 ? 'CRITICAL' : 'WARNING';
      const confidence = Math.min(0.92, 0.52 + impacted * 0.05);
      pushTarget({
        id: `insight-odp-${odpId}`,
        type: 'ODP_ANOMALY',
        targetEntityType: 'ODP',
        targetEntityId: odpId,
        severity,
        confidence,
        label: `Anomali area ODP ${payload.odp?.name || payload.odp?.code || odpId}`,
        reasons: [
          `${impacted} pelanggan terdampak`,
          `${payload.downCount} pelanggan down`,
          `Issue telemetry terdeteksi ${payload.issueCount} kali`,
        ],
        coordinates: {
          latitude: Number(payload.odp.latitude),
          longitude: Number(payload.odp.longitude),
        },
        relatedCustomerIds: payload.customerIds,
        relatedOdpIds: [odpId],
        relatedRouterIds: [normalizeNodeId(payload.odp?.router_id)],
      });
    });

    const routerAnomalyCluster = {};
    filteredCustomers.forEach((customer) => {
      const customerId = String(customer?.id || '').trim();
      if (!customerId) return;
      const insight = aiNodeInsights.byId[customerId];
      if (!insight?.anomaly?.hasAnomaly) return;
      const routerId = normalizeNodeId(resolveCustomerRouterId(customer));
      const router = filteredNasById[routerId] || nasByRouterId[routerId] || null;
      if (!routerId || !router || !hasValidCoordinates(router?.latitude, router?.longitude)) return;
      if (!routerAnomalyCluster[routerId]) {
        routerAnomalyCluster[routerId] = {
          router,
          customerIds: [],
          htbCount: 0,
          pppDownCount: 0,
        };
      }
      const accessType = String(customer?.access_type || customer?.accessType || '').trim().toUpperCase();
      if (accessType.includes('HTB')) routerAnomalyCluster[routerId].htbCount += 1;
      if (String(customer?.pppoe_connection_status || '').toLowerCase() === 'offline') {
        routerAnomalyCluster[routerId].pppDownCount += 1;
      }
      routerAnomalyCluster[routerId].customerIds.push(customerId);
    });

    Object.entries(routerAnomalyCluster).forEach(([routerId, payload]) => {
      const impacted = payload.customerIds.length;
      if (impacted < 3) return;
      const severity = payload.pppDownCount >= 2 || payload.htbCount >= 3 ? 'CRITICAL' : 'WARNING';
      const confidence = Math.min(0.9, 0.5 + impacted * 0.05);
      pushTarget({
        id: `insight-router-${routerId}`,
        type: 'ROUTER_ANOMALY',
        targetEntityType: 'ROUTER',
        targetEntityId: routerId,
        severity,
        confidence,
        label: `Anomali Router/NAS ${payload.router?.name || payload.router?.nas_identifier || routerId}`,
        reasons: [
          `${impacted} pelanggan terkait`,
          `${payload.pppDownCount} PPPoE down`,
          `${payload.htbCount} pelanggan HTB terdampak`,
        ],
        coordinates: {
          latitude: Number(payload.router.latitude),
          longitude: Number(payload.router.longitude),
        },
        relatedCustomerIds: payload.customerIds,
        relatedOdpIds: [],
        relatedRouterIds: [routerId],
      });
    });

    return Object.values(map).sort((a, b) => {
      const severityDiff = getSeverityRank(String(b?.severity || '').toLowerCase()) - getSeverityRank(String(a?.severity || '').toLowerCase());
      if (severityDiff !== 0) return severityDiff;
      const confidenceDiff = Number(b?.confidence || 0) - Number(a?.confidence || 0);
      if (confidenceDiff !== 0) return confidenceDiff;
      const impactA = (a.relatedCustomerIds?.length || 0) + (a.relatedOdpIds?.length || 0) + (a.relatedRouterIds?.length || 0);
      const impactB = (b.relatedCustomerIds?.length || 0) + (b.relatedOdpIds?.length || 0) + (b.relatedRouterIds?.length || 0);
      return impactB - impactA;
    });
  }, [aiNodeInsights.byId, filteredCustomers, filteredNasById, filteredOdpsById, monitoringIncidents, nasByRouterId, resolveCustomerRouterId, resolveFilteredOdp]);

  const selectedAiMapTarget = useMemo(
    () => aiMapTargets.find((item) => String(item?.id || '') === String(selectedAiTargetId || '')) || null,
    [aiMapTargets, selectedAiTargetId],
  );
  const topAiMapTargets = useMemo(() => aiMapTargets.slice(0, 3), [aiMapTargets]);
  const handleFocusAiTarget = useCallback((target, options = {}) => {
    if (!target) return;
    const targetId = String(target?.id || '').trim();
    if (!targetId) return;
    setSelectedAiTargetId(targetId);

    const targetType = String(target?.targetEntityType || '').trim().toUpperCase();
    const targetEntityId = normalizeNodeId(target?.targetEntityId);
    if (targetType === 'CUSTOMER' && targetEntityId) {
      selectEntity('customer', targetEntityId);
    } else if (targetType === 'ODP' && targetEntityId) {
      selectEntity('odp', targetEntityId);
    } else if (targetType === 'ROUTER' && targetEntityId) {
      selectEntity('router', targetEntityId);
    } else if (targetType === 'AREA') {
      const fallbackOdp = Array.isArray(target?.relatedOdpIds) ? normalizeNodeId(target.relatedOdpIds[0]) : '';
      const fallbackRouter = Array.isArray(target?.relatedRouterIds) ? normalizeNodeId(target.relatedRouterIds[0]) : '';
      const fallbackCustomer = Array.isArray(target?.relatedCustomerIds) ? normalizeNodeId(target.relatedCustomerIds[0]) : '';
      if (fallbackOdp) selectEntity('odp', fallbackOdp);
      else if (fallbackRouter) selectEntity('router', fallbackRouter);
      else if (fallbackCustomer) selectEntity('customer', fallbackCustomer);
      else setSelectedEntity(null);
    }

    const focusPoints = [];
    if (hasValidCoordinates(target?.coordinates?.latitude, target?.coordinates?.longitude)) {
      focusPoints.push([Number(target.coordinates.latitude), Number(target.coordinates.longitude)]);
    }
    (Array.isArray(target?.relatedCustomerIds) ? target.relatedCustomerIds : []).slice(0, 40).forEach((customerId) => {
      const customer = filteredCustomers.find((item) => String(item?.id || '') === String(customerId || ''));
      if (customer && hasValidCoordinates(customer?.latitude, customer?.longitude)) {
        focusPoints.push([Number(customer.latitude), Number(customer.longitude)]);
      }
    });
    (Array.isArray(target?.relatedOdpIds) ? target.relatedOdpIds : []).slice(0, 10).forEach((odpId) => {
      const odp = filteredOdpsById[normalizeNodeId(odpId)];
      if (odp && hasValidCoordinates(odp?.latitude, odp?.longitude)) {
        focusPoints.push([Number(odp.latitude), Number(odp.longitude)]);
      }
    });
    (Array.isArray(target?.relatedRouterIds) ? target.relatedRouterIds : []).slice(0, 10).forEach((routerId) => {
      const router = filteredNasById[normalizeNodeId(routerId)] || nasByRouterId[normalizeNodeId(routerId)];
      if (router && hasValidCoordinates(router?.latitude, router?.longitude)) {
        focusPoints.push([Number(router.latitude), Number(router.longitude)]);
      }
    });
    if (focusPoints.length > 0) {
      setAiMapFocusRequest({ id: targetId, points: focusPoints, ts: Date.now() });
    }
    if (!options?.silent) {
      toast.info(`Fokus anomaly: ${target.label || target.type || 'Anomali Monitoring'}`);
    }
  }, [filteredCustomers, filteredNasById, filteredOdpsById, nasByRouterId, selectEntity]);

  useEffect(() => {
    if (!aiModeEnabled || !aiAutoFocusEnabled) return;
    if (selectedEntityId || selectedAiTargetId) return;
    const criticalTarget = aiMapTargets.find((target) => String(target?.severity || '').toUpperCase() === 'CRITICAL') || null;
    if (!criticalTarget?.id) return;
    const nowTs = Date.now();
    const last = lastAiTargetFocusRef.current;
    const sameTarget = String(last.id || '') === String(criticalTarget.id || '');
    if (sameTarget && nowTs - Number(last.at || 0) < 60000) return;
    lastAiTargetFocusRef.current = { id: String(criticalTarget.id || ''), at: nowTs };
    handleFocusAiTarget(criticalTarget, { silent: true });
  }, [aiAutoFocusEnabled, aiMapTargets, aiModeEnabled, handleFocusAiTarget, selectedAiTargetId, selectedEntityId]);

  const sortedTechnicianTasks = useMemo(() => {
    const rows = Array.isArray(technicianTasks) ? [...technicianTasks] : [];
    rows.sort((a, b) => {
      const pA = taskPriorityOrder[String(a?.priority || '').toLowerCase()] ?? 9;
      const pB = taskPriorityOrder[String(b?.priority || '').toLowerCase()] ?? 9;
      if (pA !== pB) return pA - pB;
      const onProgressA = String(a?.status || '') === 'on_progress' ? 0 : 1;
      const onProgressB = String(b?.status || '') === 'on_progress' ? 0 : 1;
      if (onProgressA !== onProgressB) return onProgressA - onProgressB;
      return String(b?.created_at || '').localeCompare(String(a?.created_at || ''));
    });
    return rows;
  }, [technicianTasks]);

  const assignedTechnicianTasks = useMemo(
    () => sortedTechnicianTasks.filter((item) => String(item?.assigned_to || '') === String(user?.id || '') && String(item?.status || '') !== 'completed'),
    [sortedTechnicianTasks, user?.id],
  );

  const nearbyTechnicianTasks = useMemo(
    () => sortedTechnicianTasks.filter((item) => Number.isFinite(Number(item?.distance_km))),
    [sortedTechnicianTasks],
  );

  const selectedTask = useMemo(
    () => sortedTechnicianTasks.find((item) => String(item?.id || '') === String(selectedTaskId || '')) || null,
    [sortedTechnicianTasks, selectedTaskId],
  );

  useEffect(() => {
    if (!sortedTechnicianTasks.length) {
      if (selectedTaskId) setSelectedTaskId('');
      return;
    }
    if (!selectedTaskId) {
      setSelectedTaskId(sortedTechnicianTasks[0].id);
      return;
    }
    const exists = sortedTechnicianTasks.some((item) => String(item.id) === String(selectedTaskId));
    if (!exists) setSelectedTaskId(sortedTechnicianTasks[0].id);
  }, [selectedTaskId, sortedTechnicianTasks]);

  useEffect(() => {
    const timer = window.setInterval(() => setQuickActionNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);



  const technicianPerformance = useMemo(() => {

    const performanceByKey = {};

    const normalizeKey = (value) => String(value || '').trim().toLowerCase();



    const ensureTechnician = (id, name) => {

      const resolvedName = String(name || '').trim() || 'Tanpa Nama';

      const resolvedId = String(id || '').trim();

      const key = resolvedId || `name:${normalizeKey(resolvedName)}`;



      if (!performanceByKey[key]) {

        performanceByKey[key] = {

          key,

          id: resolvedId || '',

          name: resolvedName,

          approved_count: 0,

          completed_count: 0,

          special_count: 0,

          active_ticket_count: 0,

          last_activity_at: '',

        };

      }



      return performanceByKey[key];

    };



    fieldTickets.forEach((ticket) => {

      const approvedIds = Array.isArray(ticket.approved_technician_ids) ? ticket.approved_technician_ids : [];

      const approvedNames = Array.isArray(ticket.approved_technician_names) ? ticket.approved_technician_names : [];

      const completedIds = Array.isArray(ticket.completed_technician_ids) ? ticket.completed_technician_ids : [];

      const completedNames = Array.isArray(ticket.completed_technician_names) ? ticket.completed_technician_names : [];



      approvedNames.forEach((name, index) => {

        const technician = ensureTechnician(approvedIds[index], name);

        technician.approved_count += 1;



        if (!doneTicketStatuses.has(ticket.status)) {

          technician.active_ticket_count += 1;

        }



        const activityAt = ticket.updated_at || ticket.created_at || '';

        if (activityAt && (!technician.last_activity_at || activityAt > technician.last_activity_at)) {

          technician.last_activity_at = activityAt;

        }

      });



      completedNames.forEach((name, index) => {

        const technician = ensureTechnician(completedIds[index], name);

        technician.completed_count += 1;



        if (ticket.status === 'needs_special_handling' || ticket.needs_special_handling) {

          technician.special_count += 1;

        }



        const activityAt = ticket.completed_at || ticket.updated_at || ticket.created_at || '';

        if (activityAt && (!technician.last_activity_at || activityAt > technician.last_activity_at)) {

          technician.last_activity_at = activityAt;

        }

      });

    });



    if (role === 'technician' && user?.name) {

      ensureTechnician(user.id, user.name);

    }



    return Object.values(performanceByKey)

      .map((item) => ({

        ...item,

        completion_rate:

          item.approved_count > 0

            ? Math.round((item.completed_count / item.approved_count) * 100)

            : (item.completed_count > 0 ? 100 : 0),

      }))

      .sort((a, b) => {

        if (b.completed_count !== a.completed_count) return b.completed_count - a.completed_count;

        if (b.approved_count !== a.approved_count) return b.approved_count - a.approved_count;

        return a.name.localeCompare(b.name);

      });

  }, [fieldTickets, role, user]);



  const resetPasswordForm = () => {

    setPasswordForm({

      old_password: '',

      new_password: '',

      confirm_password: '',

    });

  };



  const resetCustomerForm = useCallback(async () => {
    const nextPppoe = await fetchNextPppoeFromServer();
    setDistributionSearch('');
    setCustomerForm({
      name: '',
      phone: '',
      address: '',
      email: '',
      router_id: '',
      olt_id: '',
      package: '',
      pppoe_username: nextPppoe.pppoe_username,
      pppoe_password: nextPppoe.pppoe_password,
      ktp_photo_url: '',
      odp_id: '',
      latitude: '',
      longitude: '',
      onu_serial: '',
      notes: '',
    });
  }, [fetchNextPppoeFromServer]);

  useEffect(() => {
    if (!config.canAddCustomers) return;
    if (!packageOptions.length) return;
    if (customerForm.pppoe_username && customerForm.pppoe_password) return;
    void resetCustomerForm();
  }, [
    config.canAddCustomers,
    customerForm.pppoe_password,
    customerForm.pppoe_username,
    packageOptions.length,
    resetCustomerForm,
  ]);

  useEffect(() => {
    if (!config.canAddCustomers) return;
    if (customerForm.package && packageOptionsByRouter.some((pkg) => pkg.value === customerForm.package)) return;
    const nextPackage = packageOptionsByRouter[0]?.value || '';
    if (customerForm.package === nextPackage) return;
    setCustomerForm((prev) => ({ ...prev, package: nextPackage }));
  }, [config.canAddCustomers, customerForm.package, packageOptionsByRouter]);



  const handleChangePassword = async (event) => {

    event.preventDefault();



    if (!passwordForm.old_password || !passwordForm.new_password || !passwordForm.confirm_password) {

      toast.error('Lengkapi semua field password');

      return;

    }



    if (passwordForm.new_password.length < 6) {

      toast.error('Password baru minimal 6 karakter');

      return;

    }



    if (passwordForm.new_password !== passwordForm.confirm_password) {

      toast.error('Konfirmasi password tidak sama');

      return;

    }



    setPasswordSaving(true);

    try {

      await axios.put(

        `${API}/auth/change-password`,

        {

          old_password: passwordForm.old_password,

          new_password: passwordForm.new_password,

        },

        { headers: getHeaders() }

      );

      toast.success('Password berhasil diubah');

      setPasswordDialogOpen(false);

      resetPasswordForm();

    } catch (error) {

      handleApiError(error, 'Gagal mengubah password');

    } finally {

      setPasswordSaving(false);

    }

  };



  const handleLogout = async () => {
    await requestStaffLogout();
    clearStaffSession();
    navigate('/dadu/login');
  };



  const handleRefresh = async () => {

    await fetchData(true);

    if (config.canHandleTickets || config.canViewPerformance) await fetchFieldTickets();
    if (config.canHandleTickets || config.canViewPerformance) await fetchMonitoringIncidents();
    if (config.canHandleTickets) await fetchTechnicianTasks();
    await fetchRecoverySummary();

    await fetchNotifications();

  };

  const handleRetryNow = useCallback(() => {
    clearFetchRetry();
    fetchData(true);
  }, [clearFetchRetry, fetchData]);

  const getIncidentActionLabel = useCallback((action) => {
    const type = String(action?.type || '').trim().toLowerCase();
    if (type === 'monitor') return 'Pantau Monitoring';
    if (type === 'ticket') return 'Buka Tiket';
    if (type === 'task') return 'Buka Tugas';
    if (type === 'broadcast') return 'Info Gangguan';
    return String(action?.label || 'Lihat Detail');
  }, []);

  const handleIncidentRecommendedAction = useCallback((action, incident) => {
    const actionType = String(action?.type || '').trim().toLowerCase();
    const deepLink = String(action?.deep_link || '').trim().toLowerCase();
    const sourceType = normalizeIncidentSourceType(incident?.root_source_type);
    const sourceId = String(incident?.root_source_id || '').trim();
    const incidentId = String(incident?.id || '').trim();

    if (actionType === 'monitor' || deepLink === 'map') {
      const mappedTarget = aiMapTargets.find((target) => String(target?.id || '').includes(incidentId)) || null;
      if (mappedTarget) {
        handleFocusAiTarget(mappedTarget, { silent: true });
        toast.success(`Monitoring difokuskan ke anomaly ${mappedTarget.label}.`);
        return;
      }
      if (sourceType === 'customer' && sourceId) {
        selectEntity('customer', sourceId);
        toast.success(`Monitoring difokuskan ke pelanggan ${sourceId}.`);
      } else if (sourceType === 'odp' && sourceId) {
        selectEntity('odp', sourceId);
        toast.success(`Monitoring difokuskan ke ODP ${sourceId}.`);
      } else if ((sourceType === 'nas' || sourceType === 'topology_node') && sourceId) {
        selectEntity('router', sourceId);
        toast.success(`Monitoring difokuskan ke Router/NAS ${sourceId}.`);
      } else {
        toast.info('Monitoring difokuskan ke source incident.');
      }
      return;
    }

    if (actionType === 'ticket' || deepLink === 'tickets') {
      const el = document.getElementById('section-field-tickets');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      toast.info('Buka panel tiket untuk tindak lanjut incident.');
      return;
    }

    if (actionType === 'task' || deepLink === 'tasks') {
      const el = document.getElementById('section-technician-tasks');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      toast.info('Buka panel tugas lapangan terkait incident.');
      return;
    }

    if (actionType === 'broadcast') {
      toast.info('Broadcast gangguan dikelola oleh tim NOC/Admin.');
      return;
    }

    if (deepLink === 'home') {
      navigate('/dadu');
      return;
    }

    toast.info('Aksi rekomendasi belum memiliki navigasi khusus.');
  }, [aiMapTargets, handleFocusAiTarget, navigate, selectEntity]);

  const prioritizeIncidentActions = useCallback((actionsRaw) => {
    const actions = Array.isArray(actionsRaw) ? actionsRaw : [];
    const rank = (action) => {
      const type = String(action?.type || '').trim().toLowerCase();
      if (type === 'task') return 1;
      if (type === 'ticket') return 2;
      if (type === 'monitor') return 3;
      if (type === 'broadcast') return 4;
      return 9;
    };
    return actions
      .slice()
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, 3);
  }, []);

  const handleGetCurrentLocation = () => {

    if (!navigator.geolocation) {

      toast.error('Browser tidak mendukung akses lokasi');

      return;

    }



    setGettingLocation(true);

    navigator.geolocation.getCurrentPosition(

      (position) => {

        const latitude = position.coords.latitude.toFixed(6);

        const longitude = position.coords.longitude.toFixed(6);

        setCustomerForm((prev) => ({ ...prev, latitude, longitude }));

        setGettingLocation(false);

        toast.success('Lokasi berhasil diperbarui');

      },

      () => {

        setGettingLocation(false);

        toast.error('Gagal mengambil lokasi perangkat');

      },

      { enableHighAccuracy: true, timeout: 10000 }

    );

  };



  const handleCustomerMapLocationChange = (latitude, longitude) => {

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

    setCustomerForm((prev) => ({

      ...prev,

      latitude: latitude.toFixed(6),

      longitude: longitude.toFixed(6),

    }));

  };

  const handleCustomerKtpUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('File KTP harus berupa gambar');
      event.target.value = '';
      return;
    }

    setKtpUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: {
          ...getHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      const relativeUrl = String(response.data?.file_url || response.data?.url || '').trim();
      if (!relativeUrl) {
        toast.error('Upload foto KTP gagal: URL tidak ditemukan');
        return;
      }
      setCustomerForm((prev) => ({ ...prev, ktp_photo_url: relativeUrl }));
      toast.success('Foto KTP berhasil diupload');
    } catch (error) {
      handleApiError(error, 'Gagal upload foto KTP');
    } finally {
      setKtpUploading(false);
      event.target.value = '';
    }
  };



  const handleCreateCustomer = async (event) => {

    event.preventDefault();



    const name = String(customerForm.name || '').trim();

    const phone = String(customerForm.phone || '').trim();

    const address = String(customerForm.address || '').trim();

    const email = String(customerForm.email || '').trim();

    const latitude = String(customerForm.latitude || '').trim();

    const longitude = String(customerForm.longitude || '').trim();
    const pppoeUsername = String(customerForm.pppoe_username || '').trim();
    const pppoePassword = String(customerForm.pppoe_password || '').trim();



    if (!name || !phone || !address) {

      toast.error('Nama, nomor HP, dan alamat wajib diisi');

      return;

    }

    if (!String(customerForm.package || '').trim()) {
      toast.error('Pilih paket internet PPP terlebih dahulu');
      return;
    }

    if (!pppoeUsername || !pppoePassword) {
      toast.error('Username/password PPPoE belum tersedia');
      return;
    }



    if (odps.length > 0 && !customerForm.odp_id) {

      toast.error('Pilih ODP pelanggan terlebih dahulu');

      return;

    }



    if ((latitude || longitude) && !hasValidCoordinates(latitude || 0, longitude || 0)) {

      toast.error('Latitude/Longitude tidak valid');

      return;

    }



    const selectedOdp = odpsWithCoordinates.find((item) => normalizeNodeId(item.id) === normalizeNodeId(customerForm.odp_id));
    const linkedOlt = selectedOdp ? oltsById[normalizeNodeId(selectedOdp.olt_id)] : null;
    const resolvedRouterId = normalizeNodeId(selectedOdp?.router_id || linkedOlt?.router_id || customerForm.router_id);
    const resolvedOltId = normalizeNodeId(selectedOdp?.olt_id || customerForm.olt_id);



    setCustomerSaving(true);

    try {

      await axios.post(

        `${API}/customers`,

        {

          name,

          phone,

          address,

          email: email || null,

          package: customerForm.package,
          pppoe_username: pppoeUsername,
          pppoe_password: pppoePassword,

          router_id: resolvedRouterId || null,

          olt_id: resolvedOltId || null,

          odp_id: customerForm.odp_id || null,

          odp: selectedOdp ? (selectedOdp.code || selectedOdp.name || null) : null,

          latitude: latitude ? Number(latitude) : null,

          longitude: longitude ? Number(longitude) : null,

          onu_serial: String(customerForm.onu_serial || '').trim() || null,

          notes: String(customerForm.notes || '').trim() || null,
          ktp_photo_url: String(customerForm.ktp_photo_url || '').trim() || null,

        },

        { headers: getHeaders() }

      );



      toast.success('Pelanggan baru berhasil ditambahkan');

      await resetCustomerForm();

      fetchData(false);

    } catch (error) {

      handleApiError(error, 'Gagal menambah pelanggan');

    } finally {

      setCustomerSaving(false);

    }

  };



  const updateCompletionForm = (ticketId, patch) => {

    setCompletionFormByTicketId((prev) => {

      const current = prev[ticketId] || { completion_status: 'completed', completion_notes: '', evidence_files: [] };



      return {

        ...prev,

        [ticketId]: {

          ...current,

          ...patch,

        },

      };

    });

  };



  const getCompletionForm = (ticketId) => {

    return completionFormByTicketId[ticketId] || { completion_status: 'completed', completion_notes: '', evidence_files: [] };



  };

  const updateTaskReportForm = (taskId, patch) => {
    setTaskReportById((prev) => {
      const current = prev[taskId] || { notes: '', resolution_type: 'fixed_on_site', photo_urls: '', photo_files: [] };
      return { ...prev, [taskId]: { ...current, ...patch } };
    });
  };

  const getTaskReportForm = (taskId) => taskReportById[taskId] || { notes: '', resolution_type: 'fixed_on_site', photo_urls: '', photo_files: [] };

  const uploadTaskReportPhoto = async (file) => {
    const uploadData = new FormData();
    uploadData.append('file', file);
    const response = await axios.post(`${API}/upload`, uploadData, {
      headers: {
        ...getHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return String(response.data?.file_url || response.data?.url || '').trim();
  };

  const runTaskAction = async (taskId, action, payload = null) => {
    if (!taskId) return;
    setTaskActionLoadingId(`${action}:${taskId}`);
    try {
      const endpoint =
        action === 'assign'
          ? `${API}/technician-tasks/${taskId}/assign-self`
          : action === 'start'
            ? `${API}/technician-tasks/${taskId}/start`
            : action === 'block'
              ? `${API}/technician-tasks/${taskId}/block`
              : `${API}/technician-tasks/${taskId}/complete`;
      await axios.post(endpoint, payload || {}, { headers: getHeaders() });
      if (action === 'assign') toast.success('Tugas berhasil diambil');
      if (action === 'start') toast.success('Tugas masuk proses pengerjaan');
      if (action === 'block') toast.success('Tugas ditandai blocked');
      if (action === 'complete') toast.success('Laporan lapangan berhasil disimpan');
      if (action === 'complete') {
        setTaskReportById((prev) => {
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
      }
      await fetchTechnicianTasks();
      await fetchTechnicianTaskDetail(taskId);
    } catch (error) {
      handleApiError(error, 'Aksi tugas gagal diproses');
    } finally {
      setTaskActionLoadingId('');
    }
  };

  const handleCompleteTaskWithReport = async (taskId) => {
    const form = getTaskReportForm(taskId);
    const typedPhotoUrls = String(form.photo_urls || '')
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);
    const files = Array.isArray(form.photo_files) ? form.photo_files : [];
    const uploadedUrls = [];
    for (const file of files) {
      try {
        const relativeUrl = await uploadTaskReportPhoto(file);
        if (relativeUrl) uploadedUrls.push(relativeUrl);
      } catch (error) {
        handleApiError(error, 'Gagal upload foto laporan tugas');
        return;
      }
    }
    const photoUrls = [...typedPhotoUrls, ...uploadedUrls];
    await runTaskAction(taskId, 'complete', {
      notes: form.notes || null,
      resolution_type: form.resolution_type || null,
      photo_urls: photoUrls,
    });
  };

  const handleAutoAssignTask = async (taskId) => {
    if (!taskId) return;
    setTaskActionLoadingId(`auto-assign:${taskId}`);
    try {
      await axios.post(`${API}/technician-tasks/${taskId}/auto-assign`, {}, { headers: getHeaders() });
      toast.success('Task berhasil auto-assign');
      await fetchTechnicianTasks();
      await fetchTechnicianTaskDetail(taskId);
      await fetchRegionTaskQueue();
    } catch (error) {
      handleApiError(error, 'Auto-assign task gagal');
    } finally {
      setTaskActionLoadingId('');
    }
  };

  const handleManualAssignTask = async (taskId) => {
    if (!taskId) return;
    const technicianId = String(manualAssignTechnicianId || '').trim();
    if (!technicianId) {
      toast.error('Pilih teknisi untuk manual override');
      return;
    }
    setTaskActionLoadingId(`manual-assign:${taskId}`);
    try {
      await axios.post(
        `${API}/technician-tasks/${taskId}/manual-assign`,
        { technician_id: technicianId },
        { headers: getHeaders() },
      );
      toast.success('Manual assign berhasil');
      await fetchTechnicianTasks();
      await fetchTechnicianTaskDetail(taskId);
      await fetchRegionTaskQueue();
    } catch (error) {
      handleApiError(error, 'Manual assign task gagal');
    } finally {
      setTaskActionLoadingId('');
    }
  };

  const handleRunAutoAssignRegion = async (regionKey) => {
    const targetRegion = String(regionKey || '').trim();
    if (!targetRegion) return;
    setTaskActionLoadingId(`run-auto-region:${targetRegion}`);
    try {
      const response = await axios.post(
        `${API}/technician-task-auto-assign/run`,
        { region_key: targetRegion, limit: 25 },
        { headers: getHeaders() },
      );
      const assignedCount = Array.isArray(response?.data?.assigned) ? response.data.assigned.length : 0;
      toast.success(`Auto-assign region selesai (${assignedCount} task)`);
      await fetchTechnicianTasks();
      await fetchRegionTaskQueue();
      if (selectedTaskId) await fetchTechnicianTaskDetail(selectedTaskId);
    } catch (error) {
      handleApiError(error, 'Batch auto-assign region gagal');
    } finally {
      setTaskActionLoadingId('');
    }
  };

  const handleBlockTask = async (taskId) => {
    const reason = window.prompt('Masukkan alasan tunda/blocked', '');
    if (reason === null) return;
    await runTaskAction(taskId, 'block', { reason: reason.trim() || null });
  };



  const handleJoinTicket = async (ticketId) => {

    setJoiningTicketId(ticketId);

    try {

      await axios.post(`${API}/field-tickets/${ticketId}/join`, {}, { headers: getHeaders() });

      toast.success('Approval turun lapangan berhasil dikirim');

      fetchFieldTickets();

      fetchNotifications();

    } catch (error) {

      handleApiError(error, 'Gagal approve tiket penanganan');

    } finally {

      setJoiningTicketId('');

    }

  };



  const handleCompleteTicket = async (ticketId) => {

    const form = getCompletionForm(ticketId);

    setCompletingTicketId(ticketId);

    try {

      await axios.post(

        `${API}/field-tickets/${ticketId}/complete`,

        {

          completion_status: form.completion_status,

          completion_notes: form.completion_notes || null,

        },

        { headers: getHeaders() }

      );



      const files = Array.isArray(form.evidence_files) ? form.evidence_files : [];

      if (files.length > 0) {

        const formData = new FormData();

        files.forEach((file) => formData.append('files', file));

        await axios.post(`${API}/field-tickets/${ticketId}/evidence`, formData, {

          headers: {

            ...getHeaders(),

            'Content-Type': 'multipart/form-data',

          },

        });

      }



      toast.success('Laporan penyelesaian berhasil dikirim');

      setCompletionFormByTicketId((prev) => {

        const next = { ...prev };

        delete next[ticketId];

        return next;

      });

      fetchFieldTickets();

      fetchNotifications();

    } catch (error) {

      handleApiError(error, 'Gagal mengirim laporan penyelesaian');

    } finally {

      setCompletingTicketId('');

    }

  };



  const getQuickActionCooldownLeft = (actionKey) => {
    const until = Number(quickActionCooldownUntil[actionKey] || 0);
    if (!until) return 0;
    const left = Math.ceil((until - quickActionNowTick) / 1000);
    return left > 0 ? left : 0;
  };

  const runCustomerQuickAction = async (customer, actionKey) => {
    if (!customer?.id) return;
    const cooldownLeft = getQuickActionCooldownLeft(actionKey);
    if (cooldownLeft > 0) return;
    setQuickActionLoading(actionKey);
    setQuickActionFeedback(null);
    try {
      const endpoint =
        actionKey === 'restart_onu'
          ? `${API}/technician-quick-actions/customer/${customer.id}/restart-onu`
          : actionKey === 'reconnect_pppoe'
            ? `${API}/technician-quick-actions/customer/${customer.id}/reconnect-pppoe`
            : actionKey === 'ping_test'
              ? `${API}/technician-quick-actions/customer/${customer.id}/ping-test`
              : `${API}/technician-quick-actions/customer/${customer.id}/check-signal`;
      const response = await axios.post(endpoint, {}, { headers: getHeaders() });
      const cooldownSeconds =
        actionKey === 'restart_onu' ? 120 : actionKey === 'reconnect_pppoe' ? 45 : actionKey === 'ping_test' ? 20 : 15;
      setQuickActionCooldownUntil((prev) => ({ ...prev, [actionKey]: Date.now() + cooldownSeconds * 1000 }));
      const payload = response?.data || {};
      const message = payload.message || 'Aksi berhasil diproses';
      const type = String(payload.status || '').toLowerCase() === 'failed' ? 'warning' : 'success';
      setQuickActionFeedback({ type, message, payload });
      if (type === 'warning') toast.warning(message);
      else toast.success(message);
      await fetchData(false, { keepLoader: true });
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      const detail = String(error?.response?.data?.detail || '').trim() || 'Aksi gagal diproses';
      if (status === 429) {
        const waitMatch = detail.match(/(\d+)/);
        const waitSeconds = waitMatch ? Number(waitMatch[1]) : 20;
        setQuickActionCooldownUntil((prev) => ({ ...prev, [actionKey]: Date.now() + waitSeconds * 1000 }));
      }
      setQuickActionFeedback({ type: 'error', message: detail, payload: null });
      toast.error(detail);
    } finally {
      setQuickActionLoading('');
    }
  };

  const handleRunAutoRecovery = async (customer) => {
    if (!customer?.id) return;
    setQuickActionLoading('reconnect_pppoe');
    setQuickActionFeedback(null);
    try {
      const response = await axios.post(
        `${API}/recovery/customers/${encodeURIComponent(customer.id)}/run`,
        {},
        { headers: getHeaders(), timeout: 20000 },
      );
      const payload = response?.data || {};
      const message = String(payload?.message || payload?.next_action || 'Auto recovery dijalankan').trim();
      setQuickActionFeedback({ type: 'success', message, payload });
      toast.success(message);
      await Promise.all([
        fetchData(false),
        fetchRecoverySummary(),
        fetchRecoveryStatusForCustomers([customer.id]),
      ]);
    } catch (error) {
      const detail = String(error?.response?.data?.detail || '').trim() || 'Gagal menjalankan auto recovery';
      setQuickActionFeedback({ type: 'error', message: detail, payload: null });
      toast.error(detail);
    } finally {
      setQuickActionLoading('');
    }
  };

  const handlePingTest = async (customer) => {
    await runCustomerQuickAction(customer, 'ping_test');
  };

  const handleCheckSignal = async (customer) => {
    await runCustomerQuickAction(customer, 'check_signal');
  };

  const handleOpenCustomerDetail = (customer) => {
    if (!customer?.id) return;
    navigate(`/pelanggan?focus=${encodeURIComponent(customer.id)}`);
  };

  const handleRestartOnu = async (customer) => {

    if (!config.canRestartOnu) {

      toast.error('Role ini tidak memiliki akses reboot ONU');

      return;

    }

    if (!customer?.id) return;

    if (!window.confirm(`Restart ONU untuk ${customer.name}? Koneksi akan terputus sementara.`)) return;



    setRestartingCustomerId(customer.id);

    try {

      await runCustomerQuickAction(customer, 'restart_onu');

      fetchData(false);

      fetchNotifications();

    } catch (error) {

      handleApiError(error, 'Gagal mengirim restart ONU');

    } finally {

      setRestartingCustomerId('');

    }

  };
  const handleToggleLayer = useCallback((key) => {
    setMapLayerVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleToggleFullscreen = useCallback(async () => {
    const target = mapPanelRef.current;
    if (!target) return;

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await target.requestFullscreen();
      }
    } catch {
      toast.error('Fullscreen tidak didukung oleh browser ini.');
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsMapFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleExportMonitoringCsv = useCallback(() => {
    const rows = filteredCustomers.map((item) => [
      item.customer_id || '',
      item.name || '',
      item.phone || '',
      item.pppoe_username || '',
      item.odp || '',
      getStatusLabel(item.connection_status),
      Number.isFinite(item.status_detail?.rx_power_dbm) ? item.status_detail.rx_power_dbm : '',
      Number.isFinite(item.status_detail?.attenuation_db) ? item.status_detail.attenuation_db : '',
      Number.isFinite(item.latitude) ? item.latitude : '',
      Number.isFinite(item.longitude) ? item.longitude : '',
    ]);

    const header = [
      'customer_id',
      'name',
      'phone',
      'pppoe_username',
      'odp',
      'onu_status',
      'rx_power_dbm',
      'attenuation_db',
      'latitude',
      'longitude',
    ];

    const csvContent = [header, ...rows]
      .map((line) => line.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `monitoring-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    toast.success('Export monitoring CSV berhasil.');
  }, [filteredCustomers]);

  const handleCopyCoordinate = useCallback(async (customer) => {
    if (!hasValidCoordinates(customer?.latitude, customer?.longitude)) {
      toast.error('Koordinat pelanggan belum tersedia.');
      return;
    }
    const coordinate = `${Number(customer.latitude).toFixed(6)}, ${Number(customer.longitude).toFixed(6)}`;
    try {
      await navigator.clipboard.writeText(coordinate);
      toast.success(`Koordinat tersalin: ${coordinate}`);
    } catch {
      toast.error('Gagal menyalin koordinat.');
    }
  }, []);
  const totalCustomerCount = filteredCustomers.length;
  const mappedCustomerCount = visibleCustomerPoints.length;

  return (

    <div className="staff-monitoring-map staff-surface min-h-screen">

      <header className="bg-white/90 border-b border-slate-200 sticky top-0 z-[2100] backdrop-blur">

        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">

          <Link to="/" className="flex items-center gap-3">

            <img src={ktcLogo} alt="KTC Logo" className="h-9 w-auto" />

            <div>

              <p className="font-outfit font-bold text-slate-900 leading-tight">{config.title}</p>

              <p className="text-[11px] text-slate-500 leading-tight">{user?.name || '-'}</p>

            </div>

          </Link>



          <div className="flex items-center gap-2">

            <Button onClick={handleRefresh} variant="outline" className="btn-secondary" disabled={loading || ((config.canHandleTickets || config.canViewPerformance) && loadingTickets)}>

              <RefreshCw className={`w-4 h-4 mr-2 ${loading || ((config.canHandleTickets || config.canViewPerformance) && loadingTickets) ? 'animate-spin' : ''}`} />Refresh

            </Button>

            {isRetryScheduled && (
              <Button onClick={handleRetryNow} variant="outline" className="btn-secondary" disabled={loading}>
                Coba Lagi Sekarang
              </Button>
            )}
            <Button onClick={handleExportMonitoringCsv} variant="outline" className="btn-secondary" disabled={loading}>
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
            <Button onClick={() => setAutoFitMap((prev) => !prev)} variant="outline" className="btn-secondary">
              {autoFitMap ? 'Auto-fit: ON' : 'Auto-fit: OFF'}
            </Button>
            <Button
              type="button"
              variant={aiModeEnabled ? 'default' : 'outline'}
              className={aiModeEnabled ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setAiModeEnabled((prev) => !prev)}
            >
              AI MODE: {aiModeEnabled ? 'ON' : 'OFF'}
            </Button>
            <Button onClick={handleToggleFullscreen} variant="outline" className="btn-secondary">
              {isMapFullscreen ? <Minimize2 className="w-4 h-4 mr-2" /> : <Maximize2 className="w-4 h-4 mr-2" />}
              {isMapFullscreen ? 'Keluar Fullscreen' : 'Fullscreen'}
            </Button>

            <Button

              variant="outline"

              size="sm"

              onClick={() => setPasswordDialogOpen(true)}

              className="text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/10"

            >

              <KeyRound className="w-4 h-4 mr-2" />

              Ganti Password

            </Button>

            <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-400 border-red-400/30 hover:bg-red-500/10">

              <LogOut className="w-4 h-4" />

            </Button>

          </div>

        </div>

      </header>



      <main className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-4">

        <div id="section-monitoring">

          <StaffPageHeader
            eyebrow={config.title}
            title="Monitoring Koneksi Pelanggan"
            subtitle={config.description}
          />

        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total Pelanggan</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{totalCustomerCount}</p>
          </div>
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Titik Terpetakan</p>
            <p className="mt-1 text-2xl font-bold text-cyan-700">{mappedCustomerCount}</p>
          </div>
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Online</p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">{onlineCount}</p>
          </div>
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Offline/Loss</p>
            <p className="mt-1 text-2xl font-bold text-rose-700">{offlineCount}</p>
          </div>
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status Lainnya</p>
            <p className="mt-1 text-2xl font-bold text-amber-700">{unknownCount}</p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">PPP Active</p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">{pppActiveCount}</p>
          </div>
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">PPP Down</p>
            <p className="mt-1 text-2xl font-bold text-rose-700">{pppDownCount}</p>
          </div>
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">ONU_UP_PPP_DOWN</p>
            <p className="mt-1 text-2xl font-bold text-orange-700">{onuUpPppDownCount}</p>
          </div>
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Recovery Running</p>
            <p className="mt-1 text-2xl font-bold text-blue-700">{recoveryRunningCount}</p>
          </div>
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Recovery Failed</p>
            <p className="mt-1 text-2xl font-bold text-amber-700">{recoveryFailedCount}</p>
          </div>
          <div className="staff-card p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Escalated Ticket</p>
            <p className="mt-1 text-2xl font-bold text-violet-700">{escalatedCount}</p>
          </div>
        </div>



        <InfraServiceHealthPanel
          apiBase={API}
          getHeaders={getHeaders}
          onUnauthorized={handleAuthExpired}
          statusOverrides={{
            genieacs: serverAvailable ? 'ok' : 'down',
            checkedAt: lastUpdated || null,
          }}
        />

        <section className="staff-card p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'ai', label: 'AI' },
              { key: 'pppoe', label: 'PPPoE' },
              { key: 'korelasi', label: 'Korelasi' },
              { key: 'recovery', label: 'Recovery' },
              { key: 'infrastruktur', label: 'Infrastruktur' },
              { key: 'tickets', label: 'Tickets' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveOpsTab(tab.key)}
                className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  activeOpsTab === tab.key
                    ? 'border-cyan-400 bg-cyan-50 text-cyan-700'
                    : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-cyan-300 hover:text-slate-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeOpsTab === 'ai' && (
            <div className="grid gap-3 md:grid-cols-3 text-xs">
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-orange-800">12 customers detected with ONU online but PPPoE down: <span className="font-semibold">{onuUpPppDownCount}</span></div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-blue-800">Eligible for automated PPP reconnect retry: <span className="font-semibold">{recoveryRunningCount}</span></div>
              <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-violet-800">Retry gagal dan harus eskalasi: <span className="font-semibold">{escalatedCount}</span></div>
            </div>
          )}

          {activeOpsTab === 'pppoe' && (
            <div className="overflow-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Username</th>
                    <th className="px-3 py-2 text-left">Pelanggan</th>
                    <th className="px-3 py-2 text-left">NAS/Router</th>
                    <th className="px-3 py-2 text-left">IP</th>
                    <th className="px-3 py-2 text-left">Uptime</th>
                    <th className="px-3 py-2 text-left">Last Seen</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConnectionMonitors.slice(0, 120).map((row) => (
                    <tr key={`ppp-row-${row.customerId}`} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-900">{row.pppoe.username || '-'}</td>
                      <td className="px-3 py-2 text-slate-700">{row.customerName}</td>
                      <td className="px-3 py-2 text-slate-700">{row.pppoe.nasName || '-'}</td>
                      <td className="px-3 py-2 text-slate-700">{row.pppoe.ipAddress || '-'}</td>
                      <td className="px-3 py-2 text-slate-700">{formatDurationSec(row.pppoe.uptimeSec || 0)}</td>
                      <td className="px-3 py-2 text-slate-700">{row.pppoe.lastSeenAt ? new Date(row.pppoe.lastSeenAt).toLocaleString('id-ID') : '-'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold ${semanticToneToBadgeClass(mapPppSessionStatusToTone(row.pppoe.status))}`}>
                          {row.pppoe.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeOpsTab === 'korelasi' && (
            <div className="overflow-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Customer</th>
                    <th className="px-3 py-2 text-left">Status ONU</th>
                    <th className="px-3 py-2 text-left">Status PPPoE</th>
                    <th className="px-3 py-2 text-left">Status Korelasi</th>
                    <th className="px-3 py-2 text-left">Durasi</th>
                    <th className="px-3 py-2 text-left">Percobaan Ulang</th>
                    <th className="px-3 py-2 text-left">Aksi Berikutnya</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConnectionMonitors.slice(0, 120).map((row) => (
                    <tr key={`corr-row-${row.customerId}`} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-900">{row.customerName}</td>
                      <td className="px-3 py-2 text-slate-700">{row.onu.status}</td>
                      <td className="px-3 py-2 text-slate-700">{row.pppoe.status}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold ${getCorrelationBadgeClass(row.correlation.state)}`}>
                          {row.correlation.state}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{formatDurationSec(row.correlation.durationSec)}</td>
                      <td className="px-3 py-2 text-slate-700">{row.recovery.retryCount}/{row.recovery.maxRetry}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {row.correlation.state === 'ONU_UP_PPP_DOWN'
                          ? row.recovery.status === 'ESCALATED'
                            ? 'Eskalasi teknisi'
                            : 'Retry PPP'
                          : row.correlation.state === 'TOTAL_DOWN'
                            ? 'Buat ticket gangguan'
                            : 'Pantau'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeOpsTab === 'recovery' && (
            <div className="overflow-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Pelanggan</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Percobaan Ulang</th>
                    <th className="px-3 py-2 text-left">Cek Ulang Berikutnya</th>
                    <th className="px-3 py-2 text-left">WA</th>
                    <th className="px-3 py-2 text-left">Ticket</th>
                    <th className="px-3 py-2 text-left">Status Akhir</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConnectionMonitors
                    .filter((row) => row.recovery.status !== 'IDLE')
                    .slice(0, 120)
                    .map((row) => (
                      <tr key={`recovery-row-${row.customerId}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-medium text-slate-900">{row.customerName}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold ${getRecoveryBadgeClass(row.recovery.status)}`}>
                            {row.recovery.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{row.recovery.retryCount}/{row.recovery.maxRetry}</td>
                        <td className="px-3 py-2 text-slate-700">{row.recovery.nextRecheckAt ? new Date(row.recovery.nextRecheckAt).toLocaleString('id-ID') : '-'}</td>
                        <td className="px-3 py-2 text-slate-700">{row.recovery.waSent ? 'Terkirim' : 'Menunggu'}</td>
                        <td className="px-3 py-2 text-slate-700">{row.recovery.ticketId || '-'}</td>
                        <td className="px-3 py-2 text-slate-700">{row.recovery.status}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {activeOpsTab === 'infrastruktur' && (
            <div className="grid gap-3 text-xs">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">OLT aktif terfilter: <span className="font-semibold text-slate-900">{filteredOlts.length}</span></div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">ODP terfilter: <span className="font-semibold text-slate-900">{filteredOdps.length}</span></div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">Summary recovery backend: <span className="font-semibold text-slate-900">{recoverySummary ? 'Tersedia' : 'Belum tersedia'}</span></div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status VPN NAS/Router</p>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold ${semanticToneToBadgeClass('ok')}`}>Online {nasOnlineCount}</span>
                    <span className={`inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold ${semanticToneToBadgeClass('danger')}`}>Offline {nasOfflineCount}</span>
                  </div>
                </div>
                <p className="mb-2 text-[11px] text-slate-600">Menunjukkan apakah router NAS sudah terbaca sistem (khusus router `vpn_required=true` harus `is_online=true`).</p>
                <div className="overflow-auto rounded border border-slate-100">
                  <table className="min-w-full text-[11px]">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-2 py-1.5 text-left">NAS / Router</th>
                        <th className="px-2 py-1.5 text-left">Identifier</th>
                        <th className="px-2 py-1.5 text-left">Service</th>
                        <th className="px-2 py-1.5 text-left">VPN</th>
                        <th className="px-2 py-1.5 text-left">Koneksi Sistem</th>
                        <th className="px-2 py-1.5 text-left">Traffic Saat Ini</th>
                        <th className="px-2 py-1.5 text-left">History</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nasRuntimeRows.map((router) => (
                        <tr key={`nas-runtime-${router.routerId}`} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 font-medium text-slate-900">{router.name}</td>
                          <td className="px-2 py-1.5 text-slate-700">{router.nasIdentifier}</td>
                          <td className="px-2 py-1.5 text-slate-700 uppercase">{router.serviceType}</td>
                          <td className="px-2 py-1.5 text-slate-700">{router.vpnRequired ? 'Required' : 'Optional'}</td>
                          <td className="px-2 py-1.5">
                            <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
                              router.systemReachable
                                ? semanticToneToBadgeClass('ok')
                                : semanticToneToBadgeClass('danger')
                            }`}>
                              {router.systemReachable ? 'ONLINE' : 'OFFLINE'}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-slate-700">
                            {(() => {
                              const payload = routerTrafficById[router.routerId] || {};
                              const interfaces = Array.isArray(payload?.interfaces) ? payload.interfaces : [];
                              const totalIn = interfaces.reduce((sum, item) => sum + Number(item?.in_bps || 0), 0);
                              const totalOut = interfaces.reduce((sum, item) => sum + Number(item?.out_bps || 0), 0);
                              if (interfaces.length === 0) {
                                const reason = String(payload?.degraded_reason || '').trim();
                                return (
                                  <span className="inline-flex rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                                    {reason ? 'Menurun' : 'Belum ada data'}
                                  </span>
                                );
                              }
                              return (
                                <div className="flex flex-col">
                                  <span className="font-semibold text-emerald-700">IN {formatBps(totalIn)}</span>
                                  <span className="font-semibold text-sky-700">OUT {formatBps(totalOut)}</span>
                                  <span className="text-[10px] text-slate-500">{interfaces.length} antarmuka</span>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-2 py-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="btn-secondary h-7 px-2 text-[10px]"
                              disabled={loadingRouterHistoryId === router.routerId}
                              onClick={() => fetchRouterTrafficHistory(router.routerId, 180, 120)}
                            >
                              {loadingRouterHistoryId === router.routerId ? 'Loading...' : 'Lihat'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {nasRuntimeRows.length === 0 && (
                        <tr>
                          <td className="px-2 py-3 text-center text-slate-500" colSpan={7}>Belum ada data NAS/Router</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 rounded border border-slate-100 bg-white p-2">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">History Traffic Router</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-secondary h-7 px-2 text-[10px]"
                      disabled={loadingRouterTraffic}
                      onClick={() => fetchRouterTrafficCurrent(nasRuntimeRows.map((item) => item.routerId))}
                    >
                      {loadingRouterTraffic ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </div>
                  {(() => {
                    const activeRouterId = normalizeNodeId(selectedTrafficRouterId || nasRuntimeRows[0]?.routerId);
                    if (!activeRouterId) return <p className="px-1 py-2 text-[11px] text-slate-500">Belum ada router.</p>;
                    const historyPayload = routerTrafficHistoryById[activeRouterId] || {};
                    const samples = Array.isArray(historyPayload?.samples) ? historyPayload.samples : [];
                    return (
                      <div className="overflow-auto rounded border border-slate-100">
                        <table className="min-w-full text-[11px]">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="px-2 py-1.5 text-left">Interface</th>
                              <th className="px-2 py-1.5 text-left">IN</th>
                              <th className="px-2 py-1.5 text-left">OUT</th>
                              <th className="px-2 py-1.5 text-left">Timestamp</th>
                            </tr>
                          </thead>
                          <tbody>
                            {samples.slice(0, 25).map((sample, index) => (
                              <tr key={`router-history-${activeRouterId}-${index}`} className="border-t border-slate-100">
                                <td className="px-2 py-1.5 text-slate-700">{sample?.interface_name || '-'}</td>
                                <td className="px-2 py-1.5 font-semibold text-emerald-700">{formatBps(sample?.in_bps)}</td>
                                <td className="px-2 py-1.5 font-semibold text-sky-700">{formatBps(sample?.out_bps)}</td>
                                <td className="px-2 py-1.5 text-slate-600">{sample?.timestamp ? new Date(sample.timestamp).toLocaleString('id-ID') : '-'}</td>
                              </tr>
                            ))}
                            {samples.length === 0 && (
                              <tr>
                                <td className="px-2 py-3 text-center text-slate-500" colSpan={4}>Belum ada history traffic. Klik tombol Lihat pada router.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {activeOpsTab === 'tickets' && (
            <div className="grid gap-3 md:grid-cols-3 text-xs">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">Open Tickets: <span className="font-semibold text-slate-900">{openFieldTickets.length}</span></div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">Completed Tickets: <span className="font-semibold text-slate-900">{completedFieldTickets.length}</span></div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">Korelasi Eskalasi: <span className="font-semibold text-slate-900">{escalatedCount}</span></div>
            </div>
          )}
        </section>

        <div className="grid xl:grid-cols-4 gap-4 lg:gap-6">

          <div className="xl:col-span-3 staff-card overflow-hidden">

            <div
              ref={mapPanelRef}
              className={`relative h-[70vh] min-h-[520px] ${mapLayerVisibility.animate ? '' : 'map-animations-paused'}`}
            >

              <MapContainer

                center={[DEFAULT_CENTER.latitude, DEFAULT_CENTER.longitude]}

                zoom={13}
                zoomControl={false}

                className="h-full w-full"

                scrollWheelZoom

                renderer={MAP_SVG_RENDERER}

              >

                <LeafletTileLayer />
                <TopLeftZoomControl />
                <MapZoomStateController onZoomChange={setMapZoom} />



                <FitBoundsController points={mapPoints} enabled={autoFitMap} />

                <FocusCustomerController customer={selectedCustomer} />
                <OdpFocusController selectedOdp={selectedOdp} linkedOlt={selectedOltForOdp} customers={odpScopedCustomerPoints} />
                <RouterFocusController selectedRouter={selectedMapRouter} customers={routerScopedCustomerPoints} />
                <AiTargetFocusController request={aiMapFocusRequest} />
                <MapSelectionResetController onReset={clearSelectedEntity} />



                {aiModeEnabled && mapLayerVisibility.showCorrelationAlerts && aiMapTargets.slice(0, 14).map((target) => {
                  if (!hasValidCoordinates(target?.coordinates?.latitude, target?.coordinates?.longitude)) return null;
                  const severity = String(target?.severity || 'WARNING').toUpperCase();
                  const isCritical = severity === 'CRITICAL';
                  const isArea = String(target?.targetEntityType || '').toUpperCase() === 'AREA';
                  const isSelectedTarget = String(selectedAiTargetId || '') === String(target?.id || '');
                  const center = [Number(target.coordinates.latitude), Number(target.coordinates.longitude)];
                  const radius = isArea ? (isSelectedTarget ? 220 : 170) : (isSelectedTarget ? 96 : 68);
                  return (
                    <Circle
                      key={`ai-target-${target.id}`}
                      center={center}
                      radius={radius}
                      eventHandlers={{
                        click: (event) => {
                          if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
                          handleFocusAiTarget(target);
                        },
                      }}
                      pathOptions={{
                        className: `ai-map-target ${isCritical ? 'is-critical' : 'is-warning'} ${isSelectedTarget ? 'is-selected' : ''} ${isArea ? 'is-area' : 'is-node'}`.trim(),
                        color: isCritical ? '#ef4444' : '#f59e0b',
                        fillColor: isCritical ? '#ef4444' : '#f59e0b',
                        fillOpacity: isSelectedTarget ? 0.16 : (isArea ? 0.08 : 0.06),
                        weight: isSelectedTarget ? 2.4 : 1.3,
                        opacity: isSelectedTarget ? 0.95 : 0.55,
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -8]} opacity={0.94}>
                        {target.label || 'Anomali Monitoring'} ({severity})
                      </Tooltip>
                    </Circle>
                  );
                })}

                {mapLayerVisibility.showCoverage && odpCoverageZones.map((zone) => (
                  <Circle
                    key={`odp-coverage-${zone.id}`}
                    center={[zone.latitude, zone.longitude]}
                    radius={zone.radius}
                    pathOptions={{ color: '#16a34a', fillColor: '#16a34a', fillOpacity: 0.2, weight: 1.2, opacity: 0.85, className: 'customer-presence' }}
                  />
                ))}
                {config.canHandleTickets && technicianTaskPoints.map((task) => (
                  <Circle
                    key={`task-pin-${task.id}`}
                    center={[task.lat, task.lng]}
                    radius={18}
                    eventHandlers={{ click: () => setSelectedTaskId(task.id) }}
                    pathOptions={{
                      color: getTaskPriorityColor(task.priority),
                      fillColor: getTaskPriorityColor(task.priority),
                      fillOpacity: String(selectedTaskId || '') === String(task.id) ? 0.5 : 0.28,
                      weight: String(selectedTaskId || '') === String(task.id) ? 2.2 : 1.4,
                    }}
                  >
                    <Popup className="ktc-map-popup">
                      <div className="text-sm space-y-1">
                        <p className="font-semibold">Task Lapangan</p>
                        <p>Prioritas: <span className="font-semibold">{String(task.priority || '-').toUpperCase()}</span></p>
                        <p>Status: {getTaskStatusLabel(task.status)}</p>
                        <p>Issue: {task.issue_type || '-'}</p>
                        <Button
                          size="sm"
                          className="mt-2 w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                          onClick={() => setSelectedTaskId(task.id)}
                        >
                          Buka Detail Task
                        </Button>
                      </div>
                    </Popup>
                  </Circle>
                ))}
                {mapLayerVisibility.showOdp && filteredOdps.map((odp) => {
                  if (!hasValidCoordinates(odp.latitude, odp.longitude)) return null;
                  const odpId = normalizeNodeId(odp.id);
                  const isScopedSelected = hasOdpScopeFocus && odpId === selectedOdpNormalizedId;
                  const linkedOltForOdp = filteredOltsById[normalizeNodeId(odp?.olt_id)];
                  const linkedRouterIdForOdp = normalizeNodeId(odp?.router_id || linkedOltForOdp?.router_id);
                  const isInRouterScope = !hasRouterScopeFocus || linkedRouterIdForOdp === selectedMapRouterNormalizedId;
                  const isInCustomerScope = !hasCustomerScopeFocus || odpId === selectedCustomerOdpNormalizedId;
                  const isScopedDimmed = (hasOdpScopeFocus && !isScopedSelected) || !isInRouterScope || !isInCustomerScope;
                  const odpStatus = String(odpRuntimeStatusById[odpId] || 'offline').toLowerCase();
                  const pelangganCount = Number(customerCountByOdpId[odpId] || 0);
                  const hasAiAnomaly = aiModeEnabled && !!anomalyByOdpId[odpId];
                  const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
                  return (
                    <React.Fragment key={'odp-' + odp.id}>
                      <Marker
                        position={odpPosition}
                        icon={createODPMarker(odpStatus, { focused: isScopedSelected, rootCause: hasAiAnomaly })}
                        opacity={isScopedDimmed ? 0.24 : 1}
                        zIndexOffset={isScopedSelected ? 2200 : (isScopedDimmed ? 50 : 400)}
                        eventHandlers={{
                          click: (event) => {
                            if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
                            toggleEntity('odp', odpId);
                          },
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -10]} opacity={0.95}>
                          ODP - {odp.name || odp.code || '-'}
                        </Tooltip>
                        <Popup className="ktc-map-popup">
                          <div className="text-sm">
                            <p className="font-semibold">{odp.name}</p>
                            <p>Status: <span className={`${semanticToneToTextClass(mapConnectionStatusToTone(odpStatus))} font-semibold`}>{odpStatus === 'online' ? 'Online' : 'Offline'}</span></p>
                            <p>Jumlah pelanggan: {pelangganCount}</p>
                            <p>Kode: {odp.code || '-'}</p>
                            <p>Alamat: {odp.address || '-'}</p>
                          </div>
                        </Popup>
                      </Marker>
                      {isScopedSelected && (
                        <Circle
                          center={odpPosition}
                          radius={56}
                          pathOptions={{
                            className: 'odp-focus-ring',
                            color: '#22d3ee',
                            fillColor: '#22d3ee',
                            fillOpacity: 0.08,
                            weight: 2.2,
                            opacity: 0.9,
                          }}
                        />
                      )}
                      {hasAiAnomaly && (
                        <Circle
                          center={odpPosition}
                          radius={38}
                          pathOptions={{
                            className: 'ai-node-ring',
                            color: '#ef4444',
                            fillColor: '#ef4444',
                            fillOpacity: 0.08,
                            weight: 1.8,
                            opacity: 0.9,
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}



                {mapLayerVisibility.showOlt && filteredOlts.map((olt) => {
                  if (!hasValidCoordinates(olt.latitude, olt.longitude)) return null;
                  const oltId = normalizeNodeId(olt.id);
                  const isLinkedToSelectedOdp = !hasOdpScopeFocus || normalizeNodeId(selectedOdp?.olt_id) === oltId;
                  const isLinkedToSelectedRouter = !hasRouterScopeFocus || normalizeNodeId(olt?.router_id) === selectedMapRouterNormalizedId;
                  const isLinkedToSelectedCustomer = !hasCustomerScopeFocus || selectedCustomerOltNormalizedId === oltId;
                  const isInScope = isLinkedToSelectedOdp && isLinkedToSelectedRouter && isLinkedToSelectedCustomer;
                  const oltStatus = String(oltRuntimeStatusById[oltId] || 'offline').toLowerCase();
                  const pelangganCount = Number(oltCustomerCountById[oltId] || 0);
                  const hasAiAnomaly = aiModeEnabled && !!anomalyByOltId[oltId];
                  const oltPosition = getInfraDisplayPosition('olt', olt) || [Number(olt.latitude), Number(olt.longitude)];
                  return (
                    <React.Fragment key={'olt-' + olt.id}>
                      <Marker
                        position={oltPosition}
                        icon={createOLTMarker(oltStatus, { focused: (hasOdpScopeFocus || hasRouterScopeFocus) && isInScope, rootCause: hasAiAnomaly })}
                        opacity={isInScope ? 1 : 0.24}
                        zIndexOffset={isInScope ? 900 : 90}
                      >
                        <Tooltip direction="top" offset={[0, -10]} opacity={0.95}>
                          OLT - {olt.name || olt.code || '-'}
                        </Tooltip>
                        <Popup className="ktc-map-popup">
                          <div className="text-sm">
                            <p className="font-semibold">{olt.name}</p>
                            <p>Status: <span className={`${semanticToneToTextClass(mapConnectionStatusToTone(oltStatus))} font-semibold`}>{oltStatus === 'online' ? 'Online' : 'Offline'}</span></p>
                            <p>Jumlah pelanggan: {pelangganCount}</p>
                            <p>Kode: {olt.code || '-'}</p>
                            <p>Lokasi: {olt.location || '-'}</p>
                          </div>
                        </Popup>
                      </Marker>
                      {hasAiAnomaly && (
                        <Circle
                          center={oltPosition}
                          radius={40}
                          pathOptions={{
                            className: 'ai-node-ring',
                            color: '#ef4444',
                            fillColor: '#ef4444',
                            fillOpacity: 0.08,
                            weight: 1.8,
                            opacity: 0.9,
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}



                
                {mapLayerVisibility.showRouters && filteredNasWithCoordinates.map((router) => {
                  const routerId = normalizeNodeId(router?.id || router?.router_id);
                  if (!routerId || !hasValidCoordinates(router?.latitude, router?.longitude)) return null;
                  const routerPosition = getInfraDisplayPosition('nas', router) || [Number(router.latitude), Number(router.longitude)];
                  const isSelectedRouter = hasRouterScopeFocus && routerId === selectedMapRouterNormalizedId;
                  const resolvedRouterOnline = resolveRouterOnlineState(router);
                  const routerStatus = normalizeInfraMarkerStatus(
                    router?.status
                    || router?.router_status
                    || router?.connection_status
                    || (resolvedRouterOnline.isOnline ? 'online' : 'offline'),
                  );
                  const hasAiAnomaly = aiModeEnabled && aiMapTargets.some((target) => (
                    String(target?.targetEntityType || '').toUpperCase() === 'ROUTER'
                    && normalizeNodeId(target?.targetEntityId) === routerId
                  ));
                  const linkedToSelectedOdp = !hasOdpScopeFocus
                    || routerId === normalizeNodeId(selectedOdp?.router_id || selectedOltForOdp?.router_id);
                  const linkedToSelectedCustomer = !hasCustomerScopeFocus || routerId === selectedCustomerRouterNormalizedId;
                  const isDimmed = !linkedToSelectedOdp || !linkedToSelectedCustomer || (hasRouterScopeFocus && !isSelectedRouter);
                  const trafficPayload = routerTrafficById[routerId] || {};
                  const interfaces = Array.isArray(trafficPayload?.interfaces) ? trafficPayload.interfaces : [];
                  const totalIn = interfaces.reduce((sum, item) => sum + Number(item?.in_bps || 0), 0);
                  const totalOut = interfaces.reduce((sum, item) => sum + Number(item?.out_bps || 0), 0);
                  return (
                    <React.Fragment key={`router-map-${routerId}`}>
                      <Marker
                        position={routerPosition}
                        icon={createRouterNasIcon(routerStatus, isSelectedRouter, hasAiAnomaly)}
                        opacity={isDimmed ? 0.24 : 1}
                        zIndexOffset={isSelectedRouter ? 2600 : (isDimmed ? 70 : 700)}
                        eventHandlers={{
                          click: (event) => {
                            if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
                            toggleEntity('router', routerId);
                          },
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -10]} opacity={0.95}>
                          Router/NAS - {router?.name || router?.nas_identifier || routerId}
                        </Tooltip>
                        <Popup className="ktc-map-popup">
                          <div className="text-sm space-y-1">
                            <p className="font-semibold">{router?.name || '-'}</p>
                            <p>Status: <span className={`${semanticToneToTextClass(mapMonitoringStatusToTone(routerStatus))} font-semibold`}>{routerStatus.toUpperCase()}</span></p>
                            <p>Identifier: {router?.nas_identifier || '-'}</p>
                            <p>Host API: {router?.api_host || '-'}</p>
                            <p>Traffic: IN {formatBps(totalIn)} / OUT {formatBps(totalOut)}</p>
                          </div>
                        </Popup>
                      </Marker>
                      {isSelectedRouter && (
                        <Circle
                          center={routerPosition}
                          radius={62}
                          pathOptions={{
                            className: 'router-focus-ring',
                            color: '#22d3ee',
                            fillColor: '#22d3ee',
                            fillOpacity: 0.08,
                            weight: 2.2,
                            opacity: 0.9,
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}

                {mapLayerVisibility.showCoreLinks && filteredOdps.map((odp) => {

                  const olt = odp?.olt_id ? filteredOltsById[odp.olt_id] : null;

                  if (!olt) return null;

                  if (!hasValidCoordinates(odp.latitude, odp.longitude) || !hasValidCoordinates(olt.latitude, olt.longitude)) return null;

                  const oltPosition = getInfraDisplayPosition('olt', olt) || [Number(olt.latitude), Number(olt.longitude)];

                  const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
                  const odpId = normalizeNodeId(odp.id);
                  const oltId = normalizeNodeId(olt.id);
                  const oltStatus = String(oltRuntimeStatusById[oltId] || 'offline').toLowerCase();
                  const odpStatus = String(odpRuntimeStatusById[odpId] || 'offline').toLowerCase();
                  const hasAiAnomaly = aiModeEnabled && (!!anomalyByOdpId[odpId] || !!anomalyByOltId[oltId]);
                  const isErrorLink = hasAiAnomaly || oltStatus !== 'online' || odpStatus !== 'online';
                  const connection = drawConnection(oltPosition, odpPosition, { error: isErrorLink, weight: 2.8 });
                  const polylineClassName = `${connection.className}${hasAiAnomaly ? ' ai-link-alert' : ''}`;
                  const pelangganCount = Number(customerCountByOdpId[odpId] || 0);
                  const inferredCoreLoad = Math.min(100, pelangganCount * 12 + (hasAiAnomaly ? 12 : 0));

                  const corePositions = buildDistributionPath(oltPosition, odpPosition, `core-${olt.id}-${odp.id}`);
                  const coreBandwidth = getBandwidthStyle(
                    resolveLinkLoad(odp) || inferredCoreLoad,
                    {
                      status: hasAiAnomaly ? 'offline' : (oltStatus === 'online' && odpStatus === 'online' ? 'online' : 'offline'),
                      useGradient: true,
                      minWeight: 2.4,
                      maxWeight: 7.2,
                    },
                  );
                  const coreColor = coreBandwidth.color || connection.pathOptions?.color || '#06b6d4';
                  const linkedRouterIdForOdp = normalizeNodeId(odp?.router_id || olt?.router_id);
                  const isScopedByOdp = !hasOdpScopeFocus || normalizeNodeId(odp?.id) === selectedOdpNormalizedId;
                  const isScopedByRouter = !hasRouterScopeFocus || linkedRouterIdForOdp === selectedMapRouterNormalizedId;
                  const isScopedByCustomer = !hasCustomerScopeFocus || normalizeNodeId(odp?.id) === selectedCustomerOdpNormalizedId;
                  const isScopedCore = isScopedByOdp && isScopedByRouter && isScopedByCustomer;
                  const scopeClass = isScopedCore ? 'map-focus-active odp-scope-core' : 'map-focus-dim';

                  return (
                    <React.Fragment key={`line-olt-odp-${odp.id}`}>
                      <Polyline
                        className={`${polylineClassName} ${coreBandwidth.glowClass} ${scopeClass}`.trim()}
                        positions={corePositions}
                        pathOptions={{
                          ...connection.pathOptions,
                          color: coreColor,
                          weight: isScopedCore ? coreBandwidth.weight : Math.max(1.2, coreBandwidth.weight * 0.7),
                          opacity: isScopedCore ? (connection.pathOptions?.opacity ?? 0.95) : 0.18,
                          className: `${polylineClassName} ${scopeClass}`.trim(),
                        }}
                      />
                      <LeafletFlowDirection
                        positions={corePositions}
                        color={coreColor}
                        offset="9%"
                        repeat="72px"
                        pixelSize={8}
                        className={`${polylineClassName} ${coreBandwidth.glowClass} ${scopeClass}`.trim()}
                      />
                    </React.Fragment>
                  );

                })}



                {mapLayerVisibility.showCustomerLinks && (hasOdpScopeFocus ? odpScopedCustomerPoints : (hasRouterScopeFocus ? routerScopedCustomerPoints : [])).map((customer) => {

                  const odp = resolveFilteredOdp(customer);

                  if (!odp) return null;

                  if (!hasValidCoordinates(customer.latitude, customer.longitude) || !hasValidCoordinates(odp.latitude, odp.longitude)) return null;



                  const insight = aiNodeInsights.byId[String(customer.id || '')];
                  const hasAiAnomaly = aiModeEnabled && !!insight?.anomaly?.hasAnomaly;
                  const baseLineClass = customer.connection_status === 'online'

                    ? 'customer-link distribution-link customer-link-online'

                    : customer.connection_status === 'offline'

                      ? 'customer-link distribution-link customer-link-offline'

                      : 'customer-link distribution-link customer-link-unknown';
                  const lineClass = `${baseLineClass}${hasAiAnomaly ? ' ai-link-alert' : ''} ${
                    hasRouterScopeFocus ? `router-scope-line${enableRouterScopedLineAnimation ? ' router-scope-line-animated' : ''}` : `odp-scope-line${enableScopedLineAnimation ? ' odp-scope-line-animated' : ''}`
                  }`;

                  const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
                  const customerRouterId = normalizeNodeId(resolveCustomerRouterId(customer));
                  const routerScopedNode = hasRouterScopeFocus ? filteredNasById[customerRouterId] : null;
                  const routerScopedPosition = routerScopedNode && hasValidCoordinates(routerScopedNode?.latitude, routerScopedNode?.longitude)
                    ? (getInfraDisplayPosition('nas', routerScopedNode) || [Number(routerScopedNode.latitude), Number(routerScopedNode.longitude)])
                    : null;



                  const customerPositions = buildDistributionPath(
                    hasRouterScopeFocus && routerScopedPosition ? routerScopedPosition : odpPosition,
                    [Number(customer.latitude), Number(customer.longitude)],
                    `${customer.id}-${odp.id}`,
                  );
                  const customerBandwidth = getBandwidthStyle(
                    estimateCustomerLoad(customer),
                    {
                      status: hasAiAnomaly ? 'offline' : customer.connection_status,
                      useGradient: true,
                      minWeight: 2.2,
                      maxWeight: 6.6,
                    },
                  );
                  const customerColor = hasAiAnomaly ? '#ef4444' : customerBandwidth.color;
                  const scopedLineOpacity = hasRouterScopeFocus || hasOdpScopeFocus ? 0.58 : 0.34;

                  return (
                    <React.Fragment key={`line-odp-customer-${customer.id}-${odpScopeRenderTick}`}>
                      <Polyline
                        className={`${lineClass} ${customerBandwidth.glowClass}`.trim()}
                        positions={customerPositions}
                        pathOptions={{
                          color: customerColor,
                          weight: Math.max(2.2, customerBandwidth.weight),
                          opacity: scopedLineOpacity,
                          dashArray: '10 7',
                          className: lineClass,
                        }}
                      />
                      <LeafletFlowDirection
                        positions={customerPositions}
                        color={customerColor}
                        offset="12%"
                        repeat="52px"
                        pixelSize={7}
                        className={`${lineClass} ${customerBandwidth.glowClass}`.trim()}
                      />
                    </React.Fragment>
                  );

                })}



                {mapLayerVisibility.showCustomers && shouldClusterCustomers && customerClusters.map((cluster) => (
                  <Marker
                    key={`customer-cluster-${cluster.key}`}
                    position={[Number(cluster.latitude), Number(cluster.longitude)]}
                    icon={createCustomerClusterIcon({
                      count: cluster.count,
                      status: cluster.status,
                      accessType: cluster.accessType,
                      selected: false,
                      anomaly: cluster.hasCritical,
                    })}
                    zIndexOffset={520}
                    eventHandlers={{
                      click: (event) => {
                        if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
                        const map = event?.target?._map;
                        if (map) {
                          const nextZoom = Math.min(18, Number(map.getZoom() || 13) + 2);
                          map.setView([Number(cluster.latitude), Number(cluster.longitude)], nextZoom, { animate: true, duration: 0.35 });
                        }
                      },
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -10]} opacity={0.95}>
                      Cluster {cluster.count} pelanggan ({String(cluster.accessType || 'ftth').toUpperCase()})
                    </Tooltip>
                  </Marker>
                ))}

                {mapLayerVisibility.showCustomers && (mapLayerVisibility.showPppSessions || monitorMode !== 'pppoe') && renderedCustomerPoints.map((customer) => {

                  const statusColor = getStatusColor(customer.connection_status);

                  const isSelected = selectedCustomerId === customer.id;
                  const isInSelectedOdpScope = !hasOdpScopeFocus || odpScopedCustomerIdSet.has(String(customer?.id || ''));
                  const isInSelectedRouterScope = !hasRouterScopeFocus || routerScopedCustomerIdSet.has(String(customer?.id || ''));
                  const isInSelectedCustomerScope = !hasCustomerScopeFocus || String(customer?.id || '') === String(selectedCustomerId || '');
                  const isInSelectedScope = isInSelectedOdpScope && isInSelectedRouterScope && isInSelectedCustomerScope;
                  const insight = aiNodeInsights.byId[String(customer.id || '')];
                  const monitor = connectionMonitorById[String(customer.id || '')] || null;
                  const correlationColor = getCorrelationMapColor(
                    monitor?.correlation?.state || 'NORMAL',
                    monitor?.recovery?.status || 'IDLE',
                  );
                  const showCorrelationMarker = mapLayerVisibility.showCorrelationAlerts && monitor?.correlation?.state && monitor.correlation.state !== 'NORMAL';
                  const showRecoveryMarker = mapLayerVisibility.showRecoveryQueue && ['RUNNING', 'QUEUED', 'FAILED', 'ESCALATED'].includes(String(monitor?.recovery?.status || ''));



                  return (
                    <React.Fragment key={`customer-${customer.id}`}>
                      {(showCorrelationMarker || showRecoveryMarker) && (
                        <Circle
                          center={[Number(customer.latitude), Number(customer.longitude)]}
                          radius={showRecoveryMarker ? 28 : 22}
                          pathOptions={{
                            color: correlationColor,
                            fillColor: correlationColor,
                            fillOpacity: isInSelectedScope ? 0.12 : 0.03,
                            weight: isInSelectedScope ? (showRecoveryMarker ? 2.6 : 1.8) : 1,
                            opacity: isInSelectedScope ? 0.92 : 0.22,
                          }}
                        />
                      )}
                      <Marker
                        position={[Number(customer.latitude), Number(customer.longitude)]}
                        icon={createWifiIcon(
                          customer.health_status || customer.health || customer.connection_status,
                          isSelected,
                          {
                          anomaly: aiModeEnabled && !!insight?.anomaly?.hasAnomaly,
                          predictive: aiModeEnabled && !!insight?.hasPredictive,
                          },
                          customer.access_type || customer.accessType,
                        )}
                        opacity={isInSelectedScope ? 1 : 0.2}
                        zIndexOffset={isInSelectedScope ? 600 : 100}
                        eventHandlers={{
                          click: (event) => {
                            if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
                            toggleEntity('customer', customer.id);
                          },
                        }}
                      >
                        <Popup>

                        <div className="text-sm min-w-[250px] space-y-2">
                          <div className="border-b border-slate-200 pb-2 space-y-1">
                            <p className="font-semibold text-base text-slate-900 flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-100 text-cyan-700">
                                <User className="w-3.5 h-3.5" />
                              </span>
                              <span>{customer.name}</span>
                            </p>
                            <p className="text-xs text-slate-500">Internet ID: {customer.customer_id || '-'}</p>
                          </div>
                          <p className="flex items-center gap-2 text-slate-700">
                            <Phone className="w-3.5 h-3.5 text-cyan-600" />
                            <span>{customer.phone || '-'}</span>
                          </p>
                          <p className="flex items-center gap-2 text-slate-700">
                            <MapPin className="w-3.5 h-3.5 text-indigo-600" />
                            <span>{customer.odp || '-'}</span>
                          </p>
                          <p className="text-slate-700">Status ONU: <span style={{ color: statusColor, fontWeight: 700 }}>{getStatusLabel(customer.connection_status)}</span></p>
                          <p className="text-slate-700">Status PPPoE: <span style={{ color: getPppoeStatusColor(customer.pppoe_connection_status), fontWeight: 700 }}>{getPppoeStatusLabel(customer.pppoe_connection_status)}</span></p>
                          <p className="text-slate-700">Korelasi: <span style={{ color: correlationColor, fontWeight: 700 }}>{monitor?.correlation?.state || '-'}</span></p>
                          <p className="text-slate-700">Recovery: <span style={{ color: correlationColor, fontWeight: 700 }}>{monitor?.recovery?.status || 'IDLE'}</span></p>
                          <p className="text-slate-700">RX Power/Redaman: {formatSignal(customer.status_detail)}</p>
                          <p className="text-slate-700">Perangkat Terhubung: {Number.isFinite(customer.status_detail?.total_stations) ? customer.status_detail.total_stations : '-'}</p>
                          {aiModeEnabled && insight?.anomaly?.hasAnomaly && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-2">
                              <p className="text-[11px] font-semibold text-red-700 mb-1">AI Anomaly</p>
                              <ul className="text-[11px] text-red-700 list-disc pl-4 space-y-0.5">
                                {insight.anomaly.issues.map((issue) => <li key={`${customer.id}-${issue.code}`}>{issue.label}</li>)}
                              </ul>
                            </div>
                          )}
                          {aiModeEnabled && insight?.predictions?.length > 0 && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 p-2">
                              <p className="text-[11px] font-semibold text-amber-700 mb-1">AI Predictive</p>
                              <ul className="text-[11px] text-amber-700 list-disc pl-4 space-y-0.5">
                                {insight.predictions.map((prediction, idx) => <li key={`${customer.id}-pred-${idx}`}>{prediction}</li>)}
                              </ul>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => handleCopyCoordinate(customer)}
                            className="mt-2 w-full rounded bg-slate-200 text-slate-800 border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-100"
                          >
                            <Copy className="inline w-3.5 h-3.5 mr-1" />
                            Salin Koordinat
                          </button>

                          {config.canRestartOnu && (

                            <button

                              type="button"

                              onClick={() => handleRestartOnu(customer)}

                              disabled={restartingCustomerId === customer.id || customer.connection_status === 'not_mapped'}

                              className="mt-3 w-full rounded bg-red-500/20 text-red-300 border border-red-500/40 px-3 py-1.5 text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-500/30"

                            >

                              {restartingCustomerId === customer.id ? 'Mengirim restart...' : 'Reboot ONU'}

                            </button>

                          )}

                        </div>

                      </Popup>
                      </Marker>
                    </React.Fragment>

                  );

                })}

              </MapContainer>
              <div className="absolute left-1/2 -translate-x-1/2 top-4 z-[500] w-[min(88vw,340px)]">
                <div className="rounded-xl border border-[#dbe7ff] bg-white shadow-md backdrop-blur-sm p-2">
                  <div className="mb-2 flex flex-wrap gap-1">
                    {[
                      { key: 'semua', label: 'Semua' },
                      { key: 'onu', label: 'ONU' },
                      { key: 'pppoe', label: 'PPPoE' },
                      { key: 'korelasi', label: 'Korelasi' },
                      { key: 'recovery', label: 'Recovery' },
                    ].map((mode) => (
                      <button
                        key={`toolbar-mode-${mode.key}`}
                        type="button"
                        onClick={() => setMonitorMode(mode.key)}
                        className={`rounded border px-2 py-1 text-[10px] font-semibold ${
                          monitorMode === mode.key
                            ? 'border-cyan-400 bg-cyan-50 text-cyan-700'
                            : 'border-slate-200 bg-slate-50 text-slate-600'
                        }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Cari customer atau distribusi..."
                      className="pl-9 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                    />
                  </div>
                  {searchQuery.trim().length > 0 && searchQuery.trim().length < 3 && (
                    <p className="mt-2 text-[11px] text-amber-600">Ketik minimal 3 karakter.</p>
                  )}
                  {searchQuery.trim().length >= 3 && (
                    <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white">
                      {searchLoading ? (
                        <p className="px-3 py-2 text-xs text-amber-700">Menunggu response dari server...</p>
                      ) : mapSearchResults.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-500">Data pelanggan tidak ditemukan.</p>
                      ) : (
                        mapSearchResults.map((customer) => (
                          <button
                            type="button"
                            key={`map-search-${customer.id}`}
                            onClick={() => selectEntity('customer', customer.id)}
                            className="w-full px-3 py-2 text-left hover:bg-slate-100 border-b border-slate-200 last:border-b-0"
                          >
                            <p className="text-sm text-slate-900 flex items-center gap-2">
                              <User className="w-3.5 h-3.5 text-cyan-600" />
                              <span className="truncate">{customer.name}</span>
                            </p>
                            <p className="text-[11px] text-slate-500 ml-5">{customer.customer_id || '-'} - {customer.odp || '-'}</p>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              {aiModeEnabled && (
                <div className="absolute right-4 top-4 z-[500] w-[min(88vw,320px)]">
                  <div className="rounded-xl border border-[#f8d7aa] bg-white/95 shadow-md backdrop-blur-sm p-2 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-900">Anomali Aktif</p>
                      <span className="text-[10px] font-semibold text-slate-600">{aiMapTargets.length}</span>
                    </div>
                    {topAiMapTargets.length === 0 ? (
                      <p className="text-[11px] text-slate-500">Belum ada anomali map yang aktif.</p>
                    ) : (
                      <div className="space-y-1">
                        {topAiMapTargets.map((target) => (
                          <button
                            key={`map-top-ai-${target.id}`}
                            type="button"
                            onClick={() => handleFocusAiTarget(target, { silent: true })}
                            className={`w-full rounded-md border px-2 py-1 text-left ${
                              String(selectedAiTargetId || '') === String(target.id || '')
                                ? 'border-cyan-300 bg-cyan-50'
                                : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[11px] font-semibold text-slate-800 truncate">{target.label}</p>
                              <span className={`text-[10px] font-semibold ${semanticToneToTextClass(mapIncidentSeverityToTone(target.severity || 'warning'))}`}>
                                {String(target.severity || 'WARNING').toUpperCase()}
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-600">
                              {Math.round(Number(target.confidence || 0) * 100)}% Â· {(target.relatedCustomerIds?.length || 0)} pelanggan
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="absolute left-4 bottom-4 z-[500] w-[min(92vw,370px)] rounded-xl border border-slate-200 bg-white/95 backdrop-blur-sm shadow-md p-2">
                <p className="text-[11px] font-semibold text-slate-900 mb-1">Legenda Marker</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-slate-700">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="map-legend-marker customer ftth state-healthy">
                      <svg className="map-legend-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M3 10.5 12 3l9 7.5" />
                        <path d="M5 9.5V21h14V9.5" />
                        <path d="M9 21v-6h6v6" />
                      </svg>
                    </span>
            Pelanggan FTTH
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="map-legend-marker customer htb state-healthy">
                      <svg className="map-legend-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M2 8.8a15 15 0 0 1 20 0" />
                        <path d="M5 12.9a10 10 0 0 1 14 0" />
                        <path d="M8.5 16.4a5 5 0 0 1 7 0" />
                        <path d="M12 20h.01" />
                      </svg>
                    </span>
            Pelanggan HTB
                  </span>
                  <span className="inline-flex items-center gap-1.5"><span className="map-legend-dot healthy" />Healthy</span>
                  <span className="inline-flex items-center gap-1.5"><span className="map-legend-dot degraded" />Degraded</span>
                  <span className="inline-flex items-center gap-1.5"><span className="map-legend-dot down pulse" />Down (pulse)</span>
                  <span className="inline-flex items-center gap-1.5"><span className="map-legend-dot unknown" />Unknown</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="map-legend-marker infra odp">
                      <svg className="map-legend-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M6 3v12"></path>
                        <path d="M18 9a3 3 0 1 0-3-3"></path>
                        <path d="M6 15a3 3 0 1 0 3 3"></path>
                        <path d="M9 6h6a3 3 0 0 1 3 3"></path>
                      </svg>
                    </span>
                    ODP Node
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="map-legend-marker infra olt">
                      <svg className="map-legend-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3" y="4" width="18" height="7" rx="2"></rect>
                        <rect x="3" y="13" width="18" height="7" rx="2"></rect>
                        <path d="M7 8h.01"></path>
                        <path d="M7 17h.01"></path>
                      </svg>
                    </span>
                    OLT Hub
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="map-legend-marker infra router">
                      <svg className="map-legend-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3" y="11" width="18" height="8" rx="2"></rect>
                        <path d="M7 15h.01"></path>
                        <path d="M17 15h.01"></path>
                        <path d="M7 11V8a5 5 0 0 1 10 0v3"></path>
                      </svg>
                    </span>
                    Router/NAS
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="map-legend-marker infra router">
                      <svg className="map-legend-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                        <path d="M12 9v4"></path>
                        <path d="M12 17h.01"></path>
                      </svg>
                    </span>
                    AI/Anomaly Overlay
                  </span>
                  <span className="inline-flex items-center gap-1.5"><span className="map-legend-dot selected" />Selected ring biru</span>
                  <span className="inline-flex items-center gap-1.5"><span className="map-legend-line ftth" />FTTH line</span>
                  <span className="inline-flex items-center gap-1.5"><span className="map-legend-line htb" />HTB line</span>
                </div>
              </div>
              {showLoadingOverlay && <MapLoadingOverlay text="Memuat monitoring peta..." />}

            </div>

          </div>



          <div className="space-y-4">

            <div className="staff-card p-4">

              <h3 className="text-slate-900 font-semibold mb-3">Menu Portal</h3>

              <div className="grid grid-cols-2 gap-2 text-xs">

                <a href="#section-monitoring" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors">Monitoring</a>

                {config.canAddCustomers && (

                  <a href="#section-add-customer" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1">

                    <UserPlus className="w-3.5 h-3.5" />Tambah

                  </a>

                )}

                {config.canHandleTickets && (

                  <a href="#section-field-tickets" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1">

                    <ClipboardList className="w-3.5 h-3.5" />Tiket

                  </a>

                )}
                {config.canHandleTickets && (
                  <a href="#section-technician-tasks" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1">
                    <Wrench className="w-3.5 h-3.5" />Tugas
                  </a>
                )}

                {config.canViewPerformance && (

                  <Link to="/performa-teknisi" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1">

                    <BarChart3 className="w-3.5 h-3.5" />Performa

                  </Link>

                )}

              </div>

            </div>

            <div className="staff-card p-4 space-y-3">
              <h3 className="text-slate-900 font-semibold">Quick Action Teknisi</h3>
              {selectedOdp && selectedOdpSummary && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900">ODP Terpilih</p>
                    <button
                      type="button"
                      className="rounded border border-indigo-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-100"
                      onClick={clearSelectedEntity}
                    >
                      Tutup
                    </button>
                  </div>
                  <p className="text-slate-700">
                    <span className="font-semibold text-slate-900">{selectedOdp?.name || selectedOdp?.code || selectedOdpSummary.odpId}</span>
                    {' Â· '}
                    <span className={`${semanticToneToTextClass(mapConnectionStatusToTone(String(odpRuntimeStatusById[selectedOdpSummary.odpId] || 'offline').toLowerCase()))} font-semibold`}>
                      {String(odpRuntimeStatusById[selectedOdpSummary.odpId] || 'offline').toLowerCase() === 'online' ? 'ONLINE' : 'OFFLINE'}
                    </span>
                  </p>
                  <p className="text-slate-700">Parent OLT: <span className="font-semibold text-slate-900">{selectedOdpSummary.linkedOltName}</span></p>
                  <p className="text-slate-700">Pelanggan terkait: <span className="font-semibold text-slate-900">{selectedOdpSummary.linkedCustomerCount}</span></p>
                  <p className="text-slate-700">Down: <span className="font-semibold text-rose-700">{selectedOdpSummary.downCount}</span> Â· Lainnya: <span className="font-semibold text-slate-900">{selectedOdpSummary.degradedCount}</span></p>
                  <p className="text-slate-700">Lokasi: <span className="font-semibold text-slate-900">{selectedOdp?.address || '-'}</span></p>
                </div>
              )}
              {selectedMapRouter && selectedRouterSummary && (
                <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-xs space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900">Router/NAS Terpilih</p>
                    <button
                      type="button"
                      className="rounded border border-cyan-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-cyan-700 hover:bg-cyan-100"
                      onClick={clearSelectedEntity}
                    >
                      Tutup
                    </button>
                  </div>
                  <p className="text-slate-700">
                    <span className="font-semibold text-slate-900">{selectedMapRouter?.name || selectedMapRouter?.nas_identifier || selectedRouterSummary.routerId}</span>
                    {' Â· '}
                    <span className={`${semanticToneToTextClass(resolveRouterOnlineState(selectedMapRouter).isOnline ? 'ok' : 'danger')} font-semibold`}>
                      {resolveRouterOnlineState(selectedMapRouter).isOnline ? 'ONLINE' : 'OFFLINE'}
                    </span>
                  </p>
                  <p className="text-slate-700">Identity/Version: <span className="font-semibold text-slate-900">{selectedMapRouter?.detected_identity || '-'} / {selectedMapRouter?.detected_version || '-'}</span></p>
                  <p className="text-slate-700">Model: <span className="font-semibold text-slate-900">{selectedMapRouter?.detected_model || '-'}</span></p>
                  <p className="text-slate-700">Host/API: <span className="font-semibold text-slate-900">{selectedMapRouter?.api_host || selectedMapRouter?.mgmt_ip || '-'}</span></p>
                  <p className="text-slate-700">Pelanggan terkait: <span className="font-semibold text-slate-900">{selectedRouterSummary.linkedCustomerCount}</span> (HTB {selectedRouterSummary.htbCount} Â· FTTH {selectedRouterSummary.ftthCount})</p>
                  <p className="text-slate-700">PPPoE: <span className="font-semibold text-slate-900">UP {selectedRouterSummary.pppUp}</span> / <span className="font-semibold text-rose-700">DOWN {selectedRouterSummary.pppDown}</span></p>
                  <p className="text-slate-700">Traffic: <span className="font-semibold text-slate-900">IN {formatBps(selectedRouterSummary.totalIn)}</span> Â· <span className="font-semibold text-slate-900">OUT {formatBps(selectedRouterSummary.totalOut)}</span> Â· iface {selectedRouterSummary.interfacesCount}</p>
                </div>
              )}
              {!selectedCustomer ? (
                <p className="text-xs text-slate-500">Pilih node pelanggan di peta untuk menampilkan quick actions.</p>
              ) : (
                <>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">{selectedCustomer.name || '-'}</p>
                      <button
                        type="button"
                        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-100"
                        onClick={clearSelectedEntity}
                      >
                        Tutup
                      </button>
                    </div>
                    <p className="text-slate-600">Internet ID: {selectedCustomer.customer_id || '-'}</p>
                    <p className="text-slate-600">PPPoE: {selectedCustomer.pppoe_username || '-'}</p>
                    <p className="text-slate-600">Status: {getStatusLabel(selectedCustomer.connection_status)}</p>
                  </div>

                  {selectedConnectionMonitor && (
                    <div className="rounded-lg border border-slate-200 bg-white p-2 text-xs space-y-1">
                      <p className="font-semibold text-slate-900">Ringkasan Korelasi</p>
                      <p className="text-slate-700">
                        ONU: <span className="font-semibold text-slate-900">{selectedConnectionMonitor.onu.status}</span>
                      </p>
                      <p className="text-slate-700">
                        PPPoE: <span className="font-semibold text-slate-900">{selectedConnectionMonitor.pppoe.status}</span>
                      </p>
                      <p className="text-slate-700">
                        Korelasi: <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold border ${getCorrelationBadgeClass(selectedConnectionMonitor.correlation.state)}`}>{selectedConnectionMonitor.correlation.state}</span>
                      </p>
                      <p className="text-slate-700">
                        Recovery: <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold border ${getRecoveryBadgeClass(selectedConnectionMonitor.recovery.status)}`}>{selectedConnectionMonitor.recovery.status}</span>
                      </p>
                      <p className="text-slate-700">
                        Durasi: <span className="font-semibold text-slate-900">{formatDurationSec(selectedConnectionMonitor.correlation.durationSec)}</span>
                      </p>
                    </div>
                  )}

                  <div className="rounded-lg border border-slate-200 bg-white p-2 text-xs space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">Customer Context</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[10px] btn-secondary"
                        onClick={() => fetchCustomerContext(selectedCustomer.id, { force: true })}
                        disabled={loadingCustomerContext}
                      >
                        {loadingCustomerContext ? 'Memuat...' : 'Refresh'}
                      </Button>
                    </div>
                    {loadingCustomerContext && !selectedCustomerContext ? (
                      <p className="text-slate-500">Memuat konteks pelanggan...</p>
                    ) : customerContextError ? (
                      <p className="text-rose-600">{customerContextError}</p>
                    ) : selectedCustomerContext ? (
                      <>
                        <p className="text-slate-700">
                          Service Status: <span className="font-semibold text-slate-900">{selectedCustomerContext.customer?.service_status || '-'}</span>
                        </p>
                        <p className="text-slate-700">
                          ONU Status: <span className="font-semibold text-slate-900">{selectedCustomerContext.onu?.status || '-'}</span>
                        </p>
                        <p className="text-slate-700">
                          Signal: <span className="font-semibold text-slate-900">{formatSignal(selectedCustomerContext.signal || {})}</span>
                        </p>
                        <p className="text-slate-700">
                          Last Disconnect: <span className="font-semibold text-slate-900">{selectedCustomerContext.pppoe?.last_disconnect ? new Date(selectedCustomerContext.pppoe.last_disconnect).toLocaleString('id-ID') : '-'}</span>
                        </p>
                        <p className="text-slate-700">
                          Billing Status: <span className="font-semibold text-slate-900">{selectedCustomerContext.customer?.billing_status || '-'}</span>
                        </p>
                        <p className="text-slate-700">
                          Open Tickets: <span className="font-semibold text-slate-900">{Number(selectedCustomerContext.tickets?.open_count || 0)}</span>
                        </p>
                        {Array.isArray(selectedCustomerContext.tickets?.items) && selectedCustomerContext.tickets.items.length > 0 && (
                          <div className="space-y-1 pt-1">
                            {selectedCustomerContext.tickets.items.slice(0, 3).map((ticket) => (
                              <div key={ticket.id} className="rounded border border-slate-200 bg-slate-50 p-1">
                                <p className="text-[11px] text-slate-700">#{ticket.id?.slice(0, 8)} Â· {ticket.status || '-'}</p>
                                <p className="text-[10px] text-slate-500 truncate">{ticket.issue_notes || '-'}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-500">Klik node untuk memuat konteks pelanggan.</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                      disabled={quickActionLoading === 'restart_onu' || getQuickActionCooldownLeft('restart_onu') > 0}
                      onClick={() => handleRestartOnu(selectedCustomer)}
                    >
                      {quickActionLoading === 'restart_onu'
                        ? 'Proses...'
                        : getQuickActionCooldownLeft('restart_onu') > 0
                          ? `Cooldown ${getQuickActionCooldownLeft('restart_onu')}s`
                          : 'Restart ONU'}
                    </Button>
                    <Button
                      size="sm"
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                      disabled={quickActionLoading === 'reconnect_pppoe' || getQuickActionCooldownLeft('reconnect_pppoe') > 0}
                      onClick={() => handleRunAutoRecovery(selectedCustomer)}
                    >
                      {quickActionLoading === 'reconnect_pppoe'
                        ? 'Proses...'
                        : getQuickActionCooldownLeft('reconnect_pppoe') > 0
                          ? `Cooldown ${getQuickActionCooldownLeft('reconnect_pppoe')}s`
                          : 'Retry PPP Now'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-secondary"
                      disabled={quickActionLoading === 'ping_test' || getQuickActionCooldownLeft('ping_test') > 0}
                      onClick={() => handlePingTest(selectedCustomer)}
                    >
                      {quickActionLoading === 'ping_test'
                        ? 'Testing...'
                        : getQuickActionCooldownLeft('ping_test') > 0
                          ? `Cooldown ${getQuickActionCooldownLeft('ping_test')}s`
                          : 'Ping Test'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-secondary"
                      disabled={quickActionLoading === 'check_signal' || getQuickActionCooldownLeft('check_signal') > 0}
                      onClick={() => handleCheckSignal(selectedCustomer)}
                    >
                      {quickActionLoading === 'check_signal'
                        ? 'Cek...'
                        : getQuickActionCooldownLeft('check_signal') > 0
                          ? `Cooldown ${getQuickActionCooldownLeft('check_signal')}s`
                          : 'Check Signal'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-secondary col-span-2"
                      onClick={() => toast.info('WA pelanggan akan dikirim melalui modul notifikasi WhatsApp.')}
                    >
                      Send Customer WA
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-secondary"
                      onClick={() => {
                        setActiveOpsTab('tickets');
                        const el = document.getElementById('section-field-tickets');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                    >
                      Create Ticket
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-secondary"
                      onClick={() => {
                        setActiveOpsTab('recovery');
                        const el = document.getElementById('section-technician-tasks');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                    >
                      Assign Technician
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-secondary col-span-2"
                      onClick={() => handleOpenCustomerDetail(selectedCustomer)}
                    >
                      Open Customer Detail
                    </Button>
                  </div>

                  {quickActionFeedback && (
                    <div
                      className={`rounded-lg border p-2 text-xs ${
                        quickActionFeedback.type === 'success'
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                          : quickActionFeedback.type === 'warning'
                            ? 'border-amber-300 bg-amber-50 text-amber-700'
                            : 'border-rose-300 bg-rose-50 text-rose-700'
                      }`}
                    >
                      <p className="font-semibold">
                        {quickActionFeedback.type === 'success'
                          ? 'Berhasil'
                          : quickActionFeedback.type === 'warning'
                            ? 'Perlu Perhatian'
                            : 'Gagal'}
                      </p>
                      <p>{quickActionFeedback.message}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="staff-card p-4">
              <h3 className="text-slate-900 font-semibold mb-3">Mode Monitoring</h3>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {[
                  { key: 'semua', label: 'Semua' },
                  { key: 'onu', label: 'ONU' },
                  { key: 'pppoe', label: 'PPPoE' },
                  { key: 'korelasi', label: 'Korelasi' },
                  { key: 'recovery', label: 'Recovery' },
                ].map((mode) => (
                  <button
                    key={mode.key}
                    type="button"
                    onClick={() => setMonitorMode(mode.key)}
                    className={`rounded-md border px-3 py-2 transition-colors ${
                      monitorMode === mode.key
                        ? 'border-cyan-400 bg-cyan-50 text-cyan-700'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-cyan-300 hover:text-slate-900'
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="staff-card p-4">
              <h3 className="text-slate-900 font-semibold mb-3">Kontrol Peta (Phase 1-4)</h3>
              {hasCustomerScopeFocus && selectedCustomer && (
                <div className="mb-3 flex items-center justify-between rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-[11px] text-slate-700">
                  <span>
                    Fokus Pelanggan aktif: <span className="font-semibold">{selectedCustomer.name || selectedCustomer.customer_id || selectedCustomerId || '-'}</span>
                  </span>
                  <button
                    type="button"
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-100"
                    onClick={clearSelectedEntity}
                  >
                    Reset Fokus
                  </button>
                </div>
              )}
              {hasOdpScopeFocus && (
                <div className="mb-3 flex items-center justify-between rounded-md border border-cyan-200 bg-cyan-50 px-3 py-2 text-[11px] text-cyan-800">
                  <span>
                    Fokus ODP aktif: <span className="font-semibold">{selectedOdp?.name || selectedOdp?.code || selectedOdp?.id || '-'}</span>
                  </span>
                  <button
                    type="button"
                    className="rounded border border-cyan-300 bg-white px-2 py-1 text-[10px] font-semibold text-cyan-700 hover:bg-cyan-100"
                    onClick={clearSelectedEntity}
                  >
                    Reset Fokus
                  </button>
                </div>
              )}
              {hasRouterScopeFocus && (
                <div className="mb-3 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
                  <span>
                    Fokus Router/NAS aktif: <span className="font-semibold">{selectedMapRouter?.name || selectedMapRouter?.nas_identifier || selectedMapRouterNormalizedId || '-'}</span>
                  </span>
                  <button
                    type="button"
                    className="rounded border border-emerald-300 bg-white px-2 py-1 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100"
                    onClick={clearSelectedEntity}
                  >
                    Reset Fokus
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <button type="button" onClick={() => handleToggleLayer('showOlt')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                  {mapLayerVisibility.showOlt ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} OLT
                </button>
                <button type="button" onClick={() => handleToggleLayer('showOdp')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                  {mapLayerVisibility.showOdp ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} ODP
                </button>
                <button type="button" onClick={() => handleToggleLayer('showRouters')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                  {mapLayerVisibility.showRouters ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Router/NAS
                </button>
                <button type="button" onClick={() => handleToggleLayer('showCustomers')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                  {mapLayerVisibility.showCustomers ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Pelanggan
                </button>
                <button type="button" onClick={() => handleToggleLayer('showCustomerLinks')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                  {mapLayerVisibility.showCustomerLinks ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Link ONU
                </button>
                <button type="button" onClick={() => handleToggleLayer('showCoreLinks')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                  {mapLayerVisibility.showCoreLinks ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Link Core
                </button>
                <button type="button" onClick={() => handleToggleLayer('showCoverage')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                  {mapLayerVisibility.showCoverage ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Area ODP
                </button>
                <button type="button" onClick={() => handleToggleLayer('showPppSessions')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                  {mapLayerVisibility.showPppSessions ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} PPP Sessions
                </button>
              <button type="button" onClick={() => handleToggleLayer('showCorrelationAlerts')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                {mapLayerVisibility.showCorrelationAlerts ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Peringatan Korelasi
              </button>
              <button type="button" onClick={() => handleToggleLayer('showRecoveryQueue')} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                {mapLayerVisibility.showRecoveryQueue ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Antrian Recovery
              </button>
                <button type="button" onClick={() => handleToggleLayer('animate')} className="col-span-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 hover:border-cyan-300 hover:text-slate-900 transition-colors inline-flex items-center gap-1 justify-center">
                  {mapLayerVisibility.animate ? 'Matikan Animasi Peta' : 'Aktifkan Animasi Peta'}
                </button>
              </div>
            </div>



            <div className="staff-card p-4">

              <h3 className="text-slate-900 font-semibold mb-3">Filter Monitoring</h3>

              <div className="space-y-3">
<Select value={statusFilter} onValueChange={setStatusFilter}>

                  <SelectTrigger className="bg-white border-slate-200 text-slate-900">

                    <SelectValue placeholder="Filter status" />

                  </SelectTrigger>

                  <SelectContent className="bg-white border-slate-200">

                    <SelectItem value="all" className="text-slate-800">Semua status</SelectItem>

                    <SelectItem value="online" className="text-slate-800">Online</SelectItem>

                    <SelectItem value="offline" className="text-slate-800">Offline/Loss</SelectItem>

                    <SelectItem value="not_mapped" className="text-slate-800">Belum mapping</SelectItem>

                    <SelectItem value="unknown" className="text-slate-800">Tidak diketahui</SelectItem>

                  </SelectContent>

                </Select>



                <Select value={infraFilter} onValueChange={setInfraFilter}>

                  <SelectTrigger className="bg-white border-slate-200 text-slate-900">

                    <SelectValue placeholder="Filter infrastruktur" />

                  </SelectTrigger>

                  <SelectContent className="bg-white border-slate-200">

                    <SelectItem value="all" className="text-slate-800">Semua Infrastruktur</SelectItem>

                    <SelectItem value="live" className="text-slate-800">Live</SelectItem>

                    <SelectItem value="plan" className="text-slate-800">Plan</SelectItem>

                  </SelectContent>

                </Select>

                <Select value={selectedNasId} onValueChange={setSelectedNasId}>

                  <SelectTrigger className="bg-white border-slate-200 text-slate-900">

                    <SelectValue placeholder="Filter NAS" />

                  </SelectTrigger>

                  <SelectContent className="bg-white border-slate-200">

                    <SelectItem value="all" className="text-slate-800">Semua NAS</SelectItem>

                    {nasOptions.map((nas) => (

                      <SelectItem key={nas.router_id} value={String(nas.router_id)} className="text-slate-800">{nas.name}</SelectItem>

                    ))}

                  </SelectContent>

                </Select>

                <Select value={selectedOltId} onValueChange={setSelectedOltId}>

                  <SelectTrigger className="bg-white border-slate-200 text-slate-900">

                    <SelectValue placeholder="Filter OLT" />

                  </SelectTrigger>

                  <SelectContent className="bg-white border-slate-200">

                    <SelectItem value="all" className="text-slate-800">Semua OLT</SelectItem>

                    {filteredOlts.map((olt) => (

                      <SelectItem key={olt.id} value={olt.id} className="text-slate-800">{olt.code ? `${olt.code} - ${olt.name}` : olt.name}</SelectItem>

                    ))}

                  </SelectContent>

                </Select>

              </div>

            </div>

            <div className="staff-card p-4 noc-ai-panel">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="text-slate-900 font-semibold">AI Insight</h3>
                <span className={`noc-ai-state ${aiModeEnabled ? 'is-on' : 'is-off'}`}>
                  {aiModeEnabled ? 'ON' : 'OFF'}
                </span>
              </div>
              {!aiModeEnabled ? (
                <p className="text-xs text-slate-500">Aktifkan AI MODE untuk mendeteksi anomaly node dan prediksi potensi gangguan.</p>
              ) : (
                <div className="space-y-2 text-xs">
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold text-slate-800">AI Map Focus</p>
                      <button
                        type="button"
                        onClick={() => setAiAutoFocusEnabled((prev) => !prev)}
                        className={`rounded border px-2 py-0.5 text-[10px] font-semibold ${aiAutoFocusEnabled ? 'border-cyan-300 bg-cyan-50 text-cyan-700' : 'border-slate-300 bg-white text-slate-600'}`}
                      >
                        {aiAutoFocusEnabled ? 'Auto Focus ON' : 'Auto Focus OFF'}
                      </button>
                    </div>
                    <p className="mt-1 text-[10px] text-slate-600">Anomali critical dapat difokuskan otomatis 1x per menit saat peta idle.</p>
                  </div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> {aiNodeInsights.summary.totalAnomaly} node terdeteksi anomaly</div>
                  <div className="noc-ai-item"><span className="dot is-warning" /> {aiNodeInsights.summary.latencyHigh} node latency tinggi</div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> {aiNodeInsights.summary.signalWeak} node sinyal lemah</div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> {aiNodeInsights.summary.offline} node offline</div>
                  <div className="noc-ai-item"><span className="dot is-info" /> {aiNodeInsights.summary.predictiveCount} node berpotensi gangguan</div>
                  <div className="noc-ai-item"><span className="dot is-warning" /> {onuUpPppDownCount} pelanggan ONU online tapi PPPoE down</div>
                  <div className="noc-ai-item"><span className="dot is-info" /> {recoveryRunningCount} incident siap/berjalan auto recovery</div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> {escalatedCount} incident perlu eskalasi ticket</div>
                  <div className="rounded-md border border-slate-200 bg-white p-2 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold text-slate-900">Anomali Aktif di Map</p>
                      <span className="text-[10px] text-slate-500">{aiMapTargets.length} target</span>
                    </div>
                    {topAiMapTargets.length === 0 ? (
                      <p className="text-[11px] text-slate-500">Belum ada target anomali yang bisa dipetakan.</p>
                    ) : (
                      <div className="space-y-1">
                        {topAiMapTargets.map((target) => {
                          const isSelected = String(selectedAiTargetId || '') === String(target.id || '');
                          return (
                            <button
                              key={`ai-map-target-card-${target.id}`}
                              type="button"
                              onClick={() => handleFocusAiTarget(target)}
                              className={`w-full rounded border px-2 py-1 text-left transition-colors ${
                                isSelected
                                  ? 'border-cyan-300 bg-cyan-50'
                                  : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[11px] font-semibold text-slate-800 truncate">{target.label}</p>
                                <span className={`text-[10px] font-semibold ${semanticToneToTextClass(mapIncidentSeverityToTone(target.severity || 'warning'))}`}>
                                  {String(target.severity || 'WARNING').toUpperCase()}
                                </span>
                              </div>
                              <p className="text-[10px] text-slate-600 mt-0.5">
                                Confidence {Math.round(Number(target.confidence || 0) * 100)}% Â· Impact {(target.relatedCustomerIds?.length || 0)}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {selectedAiMapTarget && (
                    <div className="rounded-md border border-cyan-200 bg-cyan-50 p-2 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[11px] font-semibold text-slate-900">Target Anomali Terpilih</p>
                        <button
                          type="button"
                          onClick={() => setSelectedAiTargetId('')}
                          className="rounded border border-cyan-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-cyan-700 hover:bg-cyan-100"
                        >
                          Dismiss
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-800 font-semibold">{selectedAiMapTarget.label}</p>
                      <p className="text-[10px] text-slate-700">
                        Severity {String(selectedAiMapTarget.severity || 'WARNING').toUpperCase()} Â· Confidence {Math.round(Number(selectedAiMapTarget.confidence || 0) * 100)}%
                      </p>
                      <ul className="list-disc pl-4 space-y-0.5 text-[10px] text-slate-700">
                        {(Array.isArray(selectedAiMapTarget.reasons) ? selectedAiMapTarget.reasons : []).slice(0, 3).map((reason, idx) => (
                          <li key={`selected-ai-reason-${idx}`}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                <Button size="sm" variant="outline" className="btn-secondary text-[11px]" onClick={() => setActiveOpsTab('recovery')}>Buka Antrian Recovery</Button>
                    <Button size="sm" variant="outline" className="btn-secondary text-[11px]" onClick={() => toast.info('Batch retry PPP disiapkan. Integrasi eksekusi massal dapat dihubungkan ke backend recovery.')}>Retry PPP (Batch)</Button>
                    <Button size="sm" variant="outline" className="btn-secondary text-[11px]" onClick={() => toast.info('Draft ticket batch disiapkan untuk incident yang gagal recovery.')}>Create Ticket Batch</Button>
                    <Button size="sm" variant="outline" className="btn-secondary text-[11px]" onClick={() => toast.info('Draft WA batch disiapkan untuk pelanggan terdampak.')}>Send WA Batch</Button>
                  </div>
                </div>
              )}
            </div>



            <div className="staff-card p-4 space-y-3 text-sm">
              <div className="flex flex-wrap gap-2">
                <MapStatusPill
                  label="Status GenieACS"
                  value={serverAvailable ? 'Terhubung' : 'Tidak terhubung'}
                  variant={serverAvailable ? 'success' : 'danger'}
                  tone="light"
                />
                <MapStatusPill
                  label="Status Realtime"
                  value={streamConnected ? 'Aktif (SSE auto update)' : 'Menyambungkan ulang...'}
                  variant={streamConnected ? 'info' : 'warning'}
                  tone="light"
                />
                <MapStatusPill
                  label="Reconnect SSE"
                  value={streamReconnectCount}
                  variant={streamReconnectCount > 0 ? 'warning' : 'success'}
                  tone="light"
                />
                <MapStatusPill
                  label="Auto Recovery"
                  value={recoverySummary ? 'Aktif' : 'Data Cadangan'}
                  variant={recoverySummary ? 'info' : 'warning'}
                  tone="light"
                />
              </div>

              <p className="text-slate-700">Filter Infrastruktur: <span className="text-slate-900 font-semibold">{infraFilter === "all" ? "Semua" : infraFilter === "live" ? "Live" : "Plan"}</span></p>
              <p className="text-slate-700">Filter NAS: <span className="text-slate-900 font-semibold">{selectedNasId === "all" ? "Semua" : (nasByRouterId[normalizeNodeId(selectedNasId)]?.name || selectedNasId)}</span></p>
              <p className="text-slate-700">Filter OLT: <span className="text-slate-900 font-semibold">{selectedOltId === "all" ? "Semua" : (olts.find((olt) => normalizeNodeId(olt.id) === normalizeNodeId(selectedOltId))?.name || selectedOltId)}</span></p>

              <div className="flex flex-wrap gap-2">
                <MapStatusPill label="Online" value={onlineCount} variant="success" tone="light" />
                <MapStatusPill label="Offline/Loss" value={offlineCount} variant="danger" tone="light" />
                <MapStatusPill label="Lainnya" value={unknownCount} variant="warning" tone="light" />
              </div>

              <p className="text-slate-500 text-xs">Pembaruan Terakhir: {lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}</p>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Metrik Sistem</p>
                <p className="text-xs text-slate-700">GenieACS Source: <span className="text-slate-900 font-semibold">{metricsSource}</span></p>
                <p className="text-xs text-slate-700">Latency avg/p95: <span className="text-slate-900 font-semibold">{metricsLatencyAvg} / {metricsLatencyP95}</span></p>
                <p className="text-xs text-slate-700">Approve transfer: <span className="text-slate-900 font-semibold">{metricsTransfer?.success ?? 0}</span> sukses, <span className="text-amber-700 font-semibold">{metricsTransfer?.conflict ?? 0}</span> konflik, <span className="text-rose-700 font-semibold">{metricsTransfer?.fail ?? 0}</span> gagal</p>
              </div>
            </div>



            <div className="staff-card p-4">

              <div className="flex items-center justify-between gap-2 mb-3">

                <h3 className="text-slate-900 font-semibold flex items-center gap-2"><Bell className="w-4 h-4" />Notifikasi</h3>

                <span className="text-[11px] text-slate-500">{notifications.filter((item) => !item.is_read).length} baru</span>

              </div>

              <div className="space-y-2 max-h-56 overflow-auto pr-1">

                {loadingNotifications ? (

                  <p className="text-slate-500 text-xs">Memuat notifikasi...</p>

                ) : notifications.length === 0 ? (

                  <p className="text-slate-500 text-xs">Belum ada notifikasi internal.</p>

                ) : notifications.map((item) => (

                  <div key={item.id} className={`rounded-lg border p-3 ${item.level === 'error' ? 'border-red-500/30 bg-red-500/10' : item.level === 'warning' ? 'border-yellow-500/30 bg-yellow-500/10' : 'border-cyan-500/20 bg-cyan-500/10'}`}>

                    <div className="flex items-start justify-between gap-2">

                      <div>

                        <p className="text-xs text-white font-semibold">{item.title || '-'}</p>

                        <p className="text-[11px] text-gray-300 mt-1 leading-relaxed">{item.message || '-'}</p>

                      </div>

                      {!item.is_read && <span className="h-2 w-2 rounded-full bg-cyan-300 mt-1" />}

                    </div>

                    <p className="text-[10px] text-gray-500 mt-2">{item.created_at ? new Date(item.created_at).toLocaleString('id-ID') : '-'}</p>

                  </div>

                ))}

              </div>

            </div>



            {config.canAddCustomers && (

              <div id="section-add-customer" className="bg-[#121212] rounded-xl p-4 border border-white/10 space-y-3">

                <h3 className="text-white font-semibold flex items-center gap-2"><UserPlus className="w-4 h-4" />Tambah Pelanggan (Teknisi)</h3>



                <form onSubmit={handleCreateCustomer} className="space-y-3">

                  <Input

                    value={customerForm.name}

                    onChange={(event) => setCustomerForm((prev) => ({ ...prev, name: event.target.value }))}

                    placeholder="Nama pelanggan"

                    className="bg-[#0A0A0A] border-white/10 text-white"

                  />

                  <Input

                    value={customerForm.phone}

                    onChange={(event) => setCustomerForm((prev) => ({ ...prev, phone: event.target.value }))}

                    placeholder="Nomor HP"

                    className="bg-[#0A0A0A] border-white/10 text-white"

                  />

                  <Input

                    value={customerForm.address}

                    onChange={(event) => setCustomerForm((prev) => ({ ...prev, address: event.target.value }))}

                    placeholder="Alamat"

                    className="bg-[#0A0A0A] border-white/10 text-white"

                  />

                  <Input

                    value={customerForm.email}

                    onChange={(event) => setCustomerForm((prev) => ({ ...prev, email: event.target.value }))}

                    placeholder="Email (opsional)"

                    className="bg-[#0A0A0A] border-white/10 text-white"

                  />



                  <Select
                    value={customerForm.router_id || undefined}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setCustomerForm((prev) => ({ ...prev, router_id: '', olt_id: '', odp_id: '', package: '' }));
                        return;
                      }
                      setCustomerForm((prev) => ({ ...prev, router_id: value, olt_id: '', odp_id: '', package: '' }));
                    }}
                  >

                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">

                      <SelectValue placeholder="Pilih NAS / Router" />

                    </SelectTrigger>

                    <SelectContent className="bg-[#121212] border-white/10 max-h-60">
                      <SelectItem value="none" className="text-white">Tanpa NAS</SelectItem>
                      {nasOptions.map((nas) => (
                        <SelectItem key={nas.router_id} value={nas.router_id} className="text-white">
                          {nas.name}
                        </SelectItem>
                      ))}
                    </SelectContent>

                  </Select>

                  <Select
                    value={customerForm.olt_id || undefined}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setCustomerForm((prev) => ({ ...prev, olt_id: '', odp_id: '' }));
                        return;
                      }
                      const selectedOlt = oltsById[normalizeNodeId(value)];
                      setCustomerForm((prev) => ({
                        ...prev,
                        olt_id: value,
                        router_id: normalizeNodeId(selectedOlt?.router_id || prev.router_id),
                        odp_id: '',
                      }));
                    }}
                  >

                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">

                      <SelectValue placeholder="Pilih OLT" />

                    </SelectTrigger>

                    <SelectContent className="bg-[#121212] border-white/10 max-h-60">
                      <SelectItem value="none" className="text-white">Semua OLT</SelectItem>
                      {customerOltsForForm.map((olt) => (
                        <SelectItem key={olt.id} value={String(olt.id)} className="text-white">
                          {olt.code ? `${olt.code} - ${olt.name}` : olt.name}
                        </SelectItem>
                      ))}
                    </SelectContent>

                  </Select>

                  <Select
                    value={customerForm.odp_id || undefined}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setCustomerForm((prev) => ({ ...prev, odp_id: '' }));
                        return;
                      }
                      const selectedOdp = odpsById[normalizeNodeId(value)];
                      const linkedOltId = normalizeNodeId(selectedOdp?.olt_id);
                      const linkedOlt = oltsById[linkedOltId];
                      setCustomerForm((prev) => ({
                        ...prev,
                        odp_id: value,
                        olt_id: linkedOltId || prev.olt_id,
                        router_id: normalizeNodeId(selectedOdp?.router_id || linkedOlt?.router_id || prev.router_id),
                      }));
                    }}
                  >

                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">

                      <SelectValue placeholder="Pilih ODP" />

                    </SelectTrigger>

                    <SelectContent className="bg-[#121212] border-white/10 max-h-60">

                      <SelectItem value="none" className="text-white">Pilih ODP</SelectItem>
                      {customerOdpsForForm.map((odp) => (

                        <SelectItem key={odp.id} value={String(odp.id)} className="text-white">

                          {(odp.code || odp.name || '-') + ' | ' + (odp.kriteria === 'plan' ? 'Plan' : 'Live')}

                        </SelectItem>

                      ))}

                    </SelectContent>

                  </Select>

                  <div className="md:col-span-2 rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3">
                    <p className="text-xs font-semibold text-cyan-300 mb-2">Distribusi</p>
                    <Input
                      value={distributionSearch}
                      onChange={(event) => setDistributionSearch(event.target.value)}
                      placeholder="Cari distribusi (kode ODP / nama / OLT)"
                      className="bg-[#0A0A0A] border-white/10 text-white mb-2"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-44 overflow-auto pr-1">
                      {distributionCards.length === 0 && (
                        <p className="text-xs text-gray-400 col-span-full">Tidak ada distribusi sesuai filter.</p>
                      )}
                      {distributionCards.map((item) => {
                        const isSelected = normalizeNodeId(customerForm.odp_id) === item.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => {
                              const selectedOdp = odpsById[item.id];
                              const linkedOltId = normalizeNodeId(selectedOdp?.olt_id);
                              const linkedOlt = oltsById[linkedOltId];
                              setCustomerForm((prev) => ({
                                ...prev,
                                odp_id: item.id,
                                olt_id: linkedOltId || prev.olt_id,
                                router_id: normalizeNodeId(selectedOdp?.router_id || linkedOlt?.router_id || prev.router_id),
                              }));
                            }}
                            className={`rounded-lg border p-2 text-left transition ${
                              isSelected ? 'border-cyan-400 bg-cyan-500/15' : 'border-white/10 bg-black/20 hover:border-cyan-300/50'
                            }`}
                          >
                            <p className="text-xs font-semibold text-white">{item.code || item.name || 'ODP'}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">OLT: {item.oltName || '-'}</p>
                            <p className="text-[11px] mt-1">
                              <span className="text-gray-300">Terpakai: {item.used}</span>
                              <span className={`ml-2 ${item.free > 0 ? 'text-emerald-300' : 'text-red-300'}`}>Tersedia: {item.free}</span>
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>



                  <Select value={customerForm.package} onValueChange={(value) => setCustomerForm((prev) => ({ ...prev, package: value }))}>

                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">

                      <SelectValue placeholder="Pilih paket" />

                    </SelectTrigger>

                    <SelectContent className="bg-[#121212] border-white/10">

                      {packageOptionsByRouter.map((pkg) => (

                        <SelectItem key={pkg.value} value={pkg.value} className="text-white">
                          {pkg.label} ({pkg.speed > 0 ? `${pkg.speed} Mbps` : '-'})
                        </SelectItem>

                      ))}

                    </SelectContent>

                  </Select>
                  <p className="text-[11px] text-gray-400">Paket otomatis difilter sesuai NAS/Router yang dipilih.</p>

                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={customerForm.pppoe_username}
                      readOnly
                      placeholder="Username PPPoE Auto"
                      className="bg-[#0A0A0A] border-white/10 text-white font-mono opacity-80"
                    />
                    <Input
                      value={customerForm.pppoe_password}
                      readOnly
                      placeholder="Password PPPoE Auto"
                      className="bg-[#0A0A0A] border-white/10 text-white font-mono opacity-80"
                    />
                  </div>

                  <div className="space-y-2 rounded-lg border border-white/10 bg-[#0A0A0A] p-3">
                    <p className="text-xs text-gray-300 flex items-center gap-1">
                      <Camera className="w-3 h-3" /> Foto KTP (opsional)
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleCustomerKtpUpload}
                      className="block w-full text-[11px] text-gray-300 file:mr-3 file:rounded file:border-0 file:bg-cyan-500/20 file:px-2 file:py-1 file:text-cyan-200"
                      disabled={ktpUploading}
                    />
                    {ktpUploading && <p className="text-[11px] text-cyan-300">Mengupload foto KTP...</p>}
                    {customerForm.ktp_photo_url && (
                      <div className="space-y-2">
                        <img
                          src={buildAssetUrl(customerForm.ktp_photo_url)}
                          alt="Preview KTP"
                          className="max-h-40 rounded border border-white/10 object-contain bg-black/30"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="h-8 border-red-400/30 text-red-300 hover:bg-red-500/10"
                          onClick={() => setCustomerForm((prev) => ({ ...prev, ktp_photo_url: '' }))}
                        >
                          Hapus Foto
                        </Button>
                      </div>
                    )}
                  </div>



                  <div>

                    <div className="flex items-center justify-between gap-2 mb-2">

                      <p className="text-xs text-gray-400">Lokasi Pelanggan</p>

                      <Button

                        type="button"

                        size="sm"

                        variant="outline"

                        onClick={handleGetCurrentLocation}

                        disabled={gettingLocation}

                        className="btn-secondary"

                      >

                        <MapPin className="w-4 h-4 mr-1" />

                        {gettingLocation ? 'Mengambil...' : 'Ambil Lokasi Terkini'}

                      </Button>

                    </div>



                    <div className="grid grid-cols-2 gap-2">

                      <Input

                        type="number"

                        step="any"

                        value={customerForm.latitude}

                        onChange={(event) => setCustomerForm((prev) => ({ ...prev, latitude: event.target.value }))}

                        placeholder="Latitude"

                        className="bg-[#0A0A0A] border-white/10 text-white"

                      />

                      <Input

                        type="number"

                        step="any"

                        value={customerForm.longitude}

                        onChange={(event) => setCustomerForm((prev) => ({ ...prev, longitude: event.target.value }))}

                        placeholder="Longitude"

                        className="bg-[#0A0A0A] border-white/10 text-white"

                      />

                    </div>



                    <div className="mt-3">

                      <LeafletPinPicker

                        title="Peta Lokasi Pelanggan"

                        latitude={customerForm.latitude}

                        longitude={customerForm.longitude}

                        fallbackCenter={DEFAULT_CUSTOMER_FORM_CENTER}

                        onChange={handleCustomerMapLocationChange}

                      />

                    </div>

                  </div>



                  <Input

                    value={customerForm.onu_serial}

                    onChange={(event) => setCustomerForm((prev) => ({ ...prev, onu_serial: event.target.value }))}

                    placeholder="Serial ONU (opsional)"

                    className="bg-[#0A0A0A] border-white/10 text-white"

                  />



                  <Textarea

                    value={customerForm.notes}

                    onChange={(event) => setCustomerForm((prev) => ({ ...prev, notes: event.target.value }))}

                    rows={2}

                    className="bg-[#0A0A0A] border-white/10 text-white resize-none"

                    placeholder="Catatan pelanggan"

                  />



                  <div className="grid grid-cols-2 gap-2">

                    <Button type="submit" disabled={customerSaving} className="bg-brand-blue hover:bg-brand-blue/90 text-white w-full">

                      {customerSaving ? 'Menyimpan...' : 'Tambah Pelanggan'}

                    </Button>

                    <Button type="button" variant="outline" className="btn-secondary w-full" onClick={() => void resetCustomerForm()} disabled={customerSaving}>

                      Reset

                    </Button>

                  </div>

                </form>

              </div>

            )}



            {config.canViewPerformance && (

              <div id="section-performance" className="bg-[#121212] rounded-xl p-4 border border-white/10 space-y-3">

                <div className="flex items-center justify-between gap-2">

                  <h3 className="text-white font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4" />Performa Teknisi</h3>

                  <span className="text-[11px] text-gray-400">{technicianPerformance.length} teknisi</span>

                </div>



                <div className="space-y-2 max-h-72 overflow-auto pr-1">

                  {technicianPerformance.length === 0 && (

                    <p className="text-gray-500 text-xs">Belum ada aktivitas teknisi di tiket lapangan.</p>

                  )}



                  {technicianPerformance.map((item) => (

                    <div key={item.key} className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">

                      <div className="flex items-center justify-between gap-2">

                        <p className="text-sm text-white font-semibold truncate">{item.name}</p>

                        <span className="text-[10px] px-2 py-1 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">{item.completion_rate}% selesai</span>

                      </div>

                      <div className="grid grid-cols-2 gap-2 text-[11px] text-gray-300">

                        <p>Approve: <span className="text-cyan-300 font-semibold">{item.approved_count}</span></p>

                        <p>Selesai: <span className="text-green-300 font-semibold">{item.completed_count}</span></p>

                        <p>Butuh Khusus: <span className="text-red-300 font-semibold">{item.special_count}</span></p>

                        <p>Tiket Aktif: <span className="text-yellow-300 font-semibold">{item.active_ticket_count}</span></p>

                      </div>

                      <p className="text-[10px] text-gray-500">Aktivitas terakhir: {item.last_activity_at ? new Date(item.last_activity_at).toLocaleString('id-ID') : '-'}</p>

                    </div>

                  ))}

                </div>

              </div>

            )}



            {config.canHandleTickets && (
              <div id="section-technician-tasks" className="bg-[#121212] rounded-xl p-4 border border-white/10 space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Wrench className="w-4 h-4" />Task Lapangan
                </h3>
                <Button size="sm" variant="outline" className="btn-secondary" onClick={fetchTechnicianTasks} disabled={loadingTasks}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingTasks ? 'animate-spin' : ''}`} />Refresh
                </Button>
              </div>

              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-white font-semibold">Auto Assign</p>
                  <Button
                    size="sm"
                    variant={assignmentSettings.enabled ? 'default' : 'outline'}
                    className={assignmentSettings.enabled ? 'bg-cyan-600 hover:bg-cyan-700 text-white h-7 px-2 text-[11px]' : 'btn-secondary h-7 px-2 text-[11px]'}
                    disabled={savingAssignmentSettings}
                    onClick={() => saveAssignmentSettings({ ...assignmentSettings, enabled: !assignmentSettings.enabled })}
                  >
                    {assignmentSettings.enabled ? 'ON' : 'OFF'}
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className={`h-7 text-[11px] ${assignmentSettings.prefer_nearest ? 'border-cyan-400 text-cyan-300' : 'btn-secondary'}`}
                    disabled={savingAssignmentSettings}
                    onClick={() => saveAssignmentSettings({ ...assignmentSettings, prefer_nearest: !assignmentSettings.prefer_nearest })}
                  >
                    Nearest
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={`h-7 text-[11px] ${assignmentSettings.balance_workload ? 'border-cyan-400 text-cyan-300' : 'btn-secondary'}`}
                    disabled={savingAssignmentSettings}
                    onClick={() => saveAssignmentSettings({ ...assignmentSettings, balance_workload: !assignmentSettings.balance_workload })}
                  >
                    Workload
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={`h-7 text-[11px] ${assignmentSettings.priority_override ? 'border-cyan-400 text-cyan-300' : 'btn-secondary'}`}
                    disabled={savingAssignmentSettings}
                    onClick={() => saveAssignmentSettings({ ...assignmentSettings, priority_override: !assignmentSettings.priority_override })}
                  >
                    Priority
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-white font-semibold">Task Queue by Region</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="btn-secondary h-7 px-2 text-[11px]"
                    onClick={fetchRegionTaskQueue}
                    disabled={loadingRegionTaskQueue}
                  >
                    {loadingRegionTaskQueue ? 'Memuat...' : 'Refresh Queue'}
                  </Button>
                </div>
                <div className="space-y-1 max-h-28 overflow-auto pr-1">
                  {regionTaskQueue.length === 0 && <p className="text-[11px] text-gray-500">Belum ada queue region.</p>}
                  {regionTaskQueue.slice(0, 8).map((region) => (
                    <div key={region.region_key} className="rounded border border-white/10 bg-black/20 p-2 text-[11px]">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-gray-200 truncate">{region.region_label || region.region_key}</p>
                        <p className="text-cyan-300 font-semibold">U:{region.unassigned || 0}</p>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-gray-500">C:{region.critical || 0} H:{region.high || 0} T:{region.total || 0}</p>
                        <Button
                          size="sm"
                          className="h-6 px-2 text-[10px] bg-cyan-600 hover:bg-cyan-700 text-white"
                          disabled={!assignmentSettings.enabled || taskActionLoadingId === `run-auto-region:${region.region_key}`}
                          onClick={() => handleRunAutoAssignRegion(region.region_key)}
                        >
                          {taskActionLoadingId === `run-auto-region:${region.region_key}` ? 'Proses...' : 'Run'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={taskScope === 'assigned' ? 'default' : 'outline'}
                    className={taskScope === 'assigned' ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : 'btn-secondary'}
                    onClick={() => setTaskScope('assigned')}
                  >
                    Assigned ({assignedTechnicianTasks.length})
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={taskScope === 'nearby' ? 'default' : 'outline'}
                    className={taskScope === 'nearby' ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : 'btn-secondary'}
                    onClick={() => setTaskScope('nearby')}
                  >
                    Nearby ({nearbyTechnicianTasks.length})
                  </Button>
                </div>

                {taskScope === 'nearby' && (
                  <div className="space-y-1">
                    <label className="text-[11px] text-gray-400">Radius nearby (km)</label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      step="1"
                      value={taskRadiusKm}
                      onChange={(event) => setTaskRadiusKm(Math.max(1, Math.min(50, Number(event.target.value || 5))))}
                      className="bg-[#0A0A0A] border-white/10 text-white"
                    />
                  </div>
                )}

                <div className="space-y-2 max-h-56 overflow-auto pr-1">
                  {sortedTechnicianTasks.length === 0 && <p className="text-gray-500 text-xs">Belum ada tugas untuk filter ini.</p>}
                  {sortedTechnicianTasks.map((task) => {
                    const isSelectedTask = String(selectedTaskId || '') === String(task.id || '');
                    return (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => setSelectedTaskId(task.id)}
                        className={`w-full rounded-lg border p-3 text-left space-y-1 transition ${
                          isSelectedTask ? 'border-cyan-400 bg-cyan-500/10' : 'border-white/10 bg-[#0A0A0A] hover:border-cyan-300/40'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs text-white font-semibold truncate">{task.issue_type || 'Task Lapangan'}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${getTaskPriorityClass(task.priority)}`}>{String(task.priority || 'low').toUpperCase()}</span>
                        </div>
                        <p className={`text-[11px] ${getTaskStatusClass(task.status)}`}>{getTaskStatusLabel(task.status)}</p>
                        <p className="text-[11px] text-gray-400">Customer: {task.customer_ref || task.customer_id || '-'}</p>
                        {Number.isFinite(Number(task.distance_km)) && (
                          <p className="text-[11px] text-cyan-300 inline-flex items-center gap-1">
                            <Navigation className="w-3 h-3" />
                            {Number(task.distance_km).toFixed(2)} km
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {selectedTask && (
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm text-white font-semibold">{selectedTask.issue_type || 'Task Lapangan'}</p>
                        <p className="text-[11px] text-gray-400">Task ID: {selectedTask.id}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${getTaskPriorityClass(selectedTask.priority)}`}>
                        {String(selectedTask.priority || 'low').toUpperCase()}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[11px] text-gray-300">
                      <p>Status: <span className={getTaskStatusClass(selectedTask.status)}>{getTaskStatusLabel(selectedTask.status)}</span></p>
                      <p>Assigned: <span className="text-white">{selectedTask.assigned_to_name || '-'}</span></p>
                    </div>

                    <div className="rounded border border-white/10 bg-[#050505] p-2 space-y-1 text-[11px]">
                      <p className="text-cyan-300 font-semibold">Task Detail</p>
                      <p className="text-gray-300">Customer: {selectedTaskDetail?.context?.customer?.name || '-'}</p>
                      <p className="text-gray-300">Koneksi: {selectedTaskDetail?.context?.connection_status || '-'}</p>
                      <p className="text-gray-300">Billing: {selectedTaskDetail?.context?.billing_status || '-'}</p>
                      <p className="text-gray-300">Last ticket: {selectedTaskDetail?.context?.last_ticket?.status || '-'}</p>
                      <p className="text-gray-300">Last signal: {formatSignal(selectedTaskDetail?.context?.last_signal || {})}</p>
                    </div>

                    <div className="rounded border border-violet-400/25 bg-violet-500/5 p-2 space-y-2 text-[11px]">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-violet-200 font-semibold">Incident Context</p>
                        <span className="text-slate-400 text-[10px]">Terkait tugas: {monitoringIncidentsBySelectedTask.length}</span>
                      </div>
                      {monitoringIncidentsBySelectedTask.length === 0 ? (
                        <p className="text-slate-500">Belum ada incident aktif untuk customer ini.</p>
                      ) : (
                        <div className="space-y-2">
                          {monitoringIncidentsBySelectedTask.slice(0, 2).map((incident) => {
                            const incidentId = String(incident?.id || '').trim();
                            const severity = String(incident?.severity || 'low').toLowerCase();
                            const status = String(incident?.status || 'monitoring').toLowerCase();
                            const topCause = Array.isArray(incident?.probable_causes) ? incident.probable_causes[0] : null;
                            const recommendedActions = Array.isArray(incident?.recommended_actions) ? incident.recommended_actions : [];
                            const prioritizedActions = prioritizeIncidentActions(recommendedActions);
                            const isResolvedState = status === 'resolved' || status === 'false_positive';
                            const severityClass = semanticToneToBadgeClass(mapIncidentSeverityToTone(severity));
                            const statusClass = semanticToneToBadgeClass(mapIncidentStatusToTone(status));
                            return (
                              <div key={`task-incident-${incidentId}`} className="rounded border border-white/10 bg-[#060b14] px-2 py-2">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-white text-[11px] font-semibold truncate">
                                    {String(incident?.incident_type || 'incident').replace(/_/g, ' ')}
                                  </p>
                                  <span className={`inline-flex px-1.5 py-0.5 rounded-full border text-[10px] ${severityClass}`}>{getIncidentSeverityLabel(severity)}</span>
                                </div>
                                <div className="mt-1 flex items-center gap-1.5">
                                  <span className={`inline-flex px-1.5 py-0.5 rounded-full border text-[10px] ${statusClass}`}>{getIncidentStatusLabel(status)}</span>
                                  <span className="inline-flex px-1.5 py-0.5 rounded-full border text-[10px] text-slate-200 border-slate-500/40 bg-slate-600/10">
                                    Confidence {Math.round(Number(incident?.confidence || 0) * 100)}%
                                  </span>
                                  <span className="inline-flex px-1.5 py-0.5 rounded-full border text-[10px] text-slate-200 border-slate-500/40 bg-slate-600/10">
                                    Dampak {Number(incident?.impact_count || 0)}
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Akar masalah: {topCause?.reason || 'Perlu investigasi lanjutan.'}
                                </p>
                                {prioritizedActions.length > 0 ? (
                                  <div className="mt-2 space-y-1">
                                    <p className="text-[10px] uppercase tracking-wide text-violet-200">Rekomendasi</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {prioritizedActions.map((action, idx) => (
                                        <button
                                          key={`task-incident-action-${incidentId}-${idx}`}
                                          type="button"
                                          className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] text-cyan-100 hover:bg-cyan-500/20"
                                          onClick={() => handleIncidentRecommendedAction(action, incident)}
                                        >
                                          {getIncidentActionLabel(action)}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}
                                <div className="mt-2 grid grid-cols-2 gap-1.5">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-[10px] btn-secondary"
                                    onClick={() => {
                                      const sourceId = String(incident?.root_source_id || '').trim();
                                      const sourceType = normalizeIncidentSourceType(incident?.root_source_type);
                                      if (sourceType === 'customer' && sourceId) {
                                        selectEntity('customer', sourceId);
                                        toast.success(`Fokus ke pelanggan ${sourceId}`);
                                      } else {
                                        toast.info(`Source ${sourceType.toUpperCase()} ${sourceId || '-'}`);
                                      }
                                    }}
                                  >
                                    Fokus Source
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-7 text-[10px] bg-emerald-500/85 hover:bg-emerald-400 text-slate-950"
                                    onClick={() => handleResolveMonitoringIncident(incident, false)}
                                    disabled={!canResolveMonitoringIncidents || isResolvedState || resolvingMonitoringIncidentId === incidentId}
                                  >
                                    {resolvingMonitoringIncidentId === incidentId ? '...' : 'Resolve'}
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        disabled={taskActionLoadingId === `assign:${selectedTask.id}` || String(selectedTask.status) === 'completed'}
                        onClick={() => runTaskAction(selectedTask.id, 'assign')}
                      >
                        <ClipboardList className="w-3.5 h-3.5 mr-1" />Ambil Tugas
                      </Button>
                      <Button
                        size="sm"
                        className="bg-cyan-600 hover:bg-cyan-700 text-white"
                        disabled={taskActionLoadingId === `start:${selectedTask.id}` || String(selectedTask.status) === 'completed'}
                        onClick={() => runTaskAction(selectedTask.id, 'start')}
                      >
                        <PlayCircle className="w-3.5 h-3.5 mr-1" />Mulai
                      </Button>
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={taskActionLoadingId === `block:${selectedTask.id}` || String(selectedTask.status) === 'completed'}
                        onClick={() => handleBlockTask(selectedTask.id)}
                      >
                        <Flag className="w-3.5 h-3.5 mr-1" />Tunda/Blocked
                      </Button>
                    </div>

                    <div className="rounded border border-white/10 p-2 space-y-2">
                      <p className="text-[11px] text-gray-300 font-semibold">Assignment Control</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          disabled={taskActionLoadingId === `auto-assign:${selectedTask.id}` || String(selectedTask.status) === 'completed'}
                          onClick={() => handleAutoAssignTask(selectedTask.id)}
                        >
                          {taskActionLoadingId === `auto-assign:${selectedTask.id}` ? 'Proses...' : 'Auto Assign'}
                        </Button>
                        <Select value={manualAssignTechnicianId || undefined} onValueChange={setManualAssignTechnicianId}>
                          <SelectTrigger className="h-8 bg-[#050505] border-white/10 text-white text-xs">
                            <SelectValue placeholder={loadingTechnicianUsers ? 'Memuat teknisi...' : 'Pilih teknisi'} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#121212] border-white/10">
                            {technicianUsers.map((tech) => (
                              <SelectItem key={tech.id} value={String(tech.id)} className="text-white text-xs">
                                {tech.name || tech.username || tech.id}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-secondary w-full"
                        disabled={taskActionLoadingId === `manual-assign:${selectedTask.id}` || !manualAssignTechnicianId || String(selectedTask.status) === 'completed'}
                        onClick={() => handleManualAssignTask(selectedTask.id)}
                      >
                        {taskActionLoadingId === `manual-assign:${selectedTask.id}` ? 'Menyimpan...' : 'Tetapkan Manual'}
                      </Button>
                    </div>

                    {String(selectedTask.status || '') !== 'completed' && (
                      <div className="space-y-2 rounded border border-white/10 p-2">
                        <p className="text-[11px] text-gray-300 font-semibold">Field Report</p>
                        <Input
                          value={getTaskReportForm(selectedTask.id).resolution_type}
                          onChange={(event) => updateTaskReportForm(selectedTask.id, { resolution_type: event.target.value })}
                          placeholder="Jenis penyelesaian (contoh: fixed_on_site)"
                          className="bg-[#050505] border-white/10 text-white text-xs"
                        />
                        <Textarea
                          rows={2}
                          value={getTaskReportForm(selectedTask.id).notes}
                          onChange={(event) => updateTaskReportForm(selectedTask.id, { notes: event.target.value })}
                          className="bg-[#050505] border-white/10 text-white text-xs resize-none"
                          placeholder="Catatan hasil pekerjaan"
                        />
                        <Textarea
                          rows={2}
                          value={getTaskReportForm(selectedTask.id).photo_urls}
                          onChange={(event) => updateTaskReportForm(selectedTask.id, { photo_urls: event.target.value })}
                          className="bg-[#050505] border-white/10 text-white text-xs resize-none"
                          placeholder="URL foto bukti (opsional, pisah per baris)"
                        />
                        <div className="space-y-1">
                          <label className="text-[11px] text-gray-400 flex items-center gap-1"><Camera className="w-3 h-3" />Upload Foto Bukti (opsional)</label>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(event) => updateTaskReportForm(selectedTask.id, { photo_files: Array.from(event.target.files || []) })}
                            className="block w-full text-[11px] text-gray-400 file:mr-3 file:rounded file:border-0 file:bg-cyan-500/20 file:px-2 file:py-1 file:text-cyan-200"
                          />
                          {Array.isArray(getTaskReportForm(selectedTask.id).photo_files) && getTaskReportForm(selectedTask.id).photo_files.length > 0 && (
                            <p className="text-[10px] text-cyan-300">{getTaskReportForm(selectedTask.id).photo_files.length} foto siap diunggah</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={taskActionLoadingId === `complete:${selectedTask.id}`}
                          onClick={() => handleCompleteTaskWithReport(selectedTask.id)}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Selesai
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {config.canHandleTickets && (

              <div id="section-field-tickets" className="bg-[#121212] rounded-xl p-4 border border-white/10 space-y-4">

                <div className="flex items-center justify-between gap-2">

                  <h3 className="text-white font-semibold flex items-center gap-2">

                    <ClipboardList className="w-4 h-4" />Approval & Selesai

                  </h3>

                  <Button
                    size="sm"
                    variant="outline"
                    className="btn-secondary"
                    onClick={() => {
                      fetchFieldTickets();
                      fetchMonitoringIncidents();
                    }}
                    disabled={loadingTickets || loadingMonitoringIncidents}
                  >

                    <RefreshCw className={`w-4 h-4 mr-1 ${(loadingTickets || loadingMonitoringIncidents) ? 'animate-spin' : ''}`} />Refresh

                  </Button>

                </div>

                <div className="rounded-lg border border-violet-400/25 bg-violet-500/5 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] uppercase tracking-wide text-violet-200">Monitoring Incident</p>
                    <span className="text-[10px] text-slate-400">
                      Context customer: {monitoringIncidentsBySelectedCustomer.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[11px]">
                    <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                      <p className="text-slate-400">Open/Monitoring</p>
                      <p className="text-slate-100 font-medium">{monitoringIncidentSummary.open + monitoringIncidentSummary.monitoring}</p>
                    </div>
                    <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                      <p className="text-slate-400">Kritis/Tinggi</p>
                      <p className="text-slate-100 font-medium">{monitoringIncidentSummary.critical + monitoringIncidentSummary.high}</p>
                    </div>
                    <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                      <p className="text-slate-400">Resolved/FP</p>
                      <p className="text-slate-100 font-medium">{monitoringIncidentSummary.resolved + monitoringIncidentSummary.falsePositive}</p>
                    </div>
                  </div>
                  {monitoringIncidents.length === 0 ? (
                    <p className="text-xs text-slate-500">Belum ada incident monitoring.</p>
                  ) : (
                    <div className="space-y-2 max-h-44 overflow-auto pr-1">
                      {monitoringIncidents.slice(0, 6).map((incident) => {
                        const incidentId = String(incident?.id || '').trim();
                        const severity = String(incident?.severity || 'low').toLowerCase();
                        const status = String(incident?.status || 'monitoring').toLowerCase();
                        const sourceType = normalizeIncidentSourceType(incident?.root_source_type);
                        const sourceId = String(incident?.root_source_id || '').trim();
                        const isResolvedState = status === 'resolved' || status === 'false_positive';
                        const statusClass = semanticToneToBadgeClass(mapIncidentStatusToTone(status));
                        const severityClass = semanticToneToBadgeClass(mapIncidentSeverityToTone(severity));
                        return (
                          <div key={`staff-monitoring-incident-${incidentId}`} className="rounded border border-white/10 bg-[#060b14] px-2 py-2">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[12px] text-white font-semibold truncate">
                                {String(incident?.incident_type || 'incident').replace(/_/g, ' ')}
                              </p>
                              <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${severityClass}`}>{getIncidentSeverityLabel(severity)}</span>
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                              <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${statusClass}`}>{getIncidentStatusLabel(status)}</span>
                              <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] text-slate-200 border-slate-500/40 bg-slate-600/10">
                                Dampak {Number(incident?.impact_count || 0)}
                              </span>
                              <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] text-slate-300 border-slate-500/30 bg-slate-700/20">
                                Confidence {Math.round(Number(incident?.confidence || 0) * 100)}%
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1">
                              Root: {sourceType.toUpperCase()} â€¢ {sourceId || '-'}
                            </p>
                            <div className="mt-2 grid grid-cols-3 gap-1.5">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-[10px] btn-secondary"
                                onClick={() => {
                                  if (sourceType === 'customer' && sourceId) {
                                    selectEntity('customer', sourceId);
                                    toast.success(`Fokus ke pelanggan ${sourceId}`);
                                  } else {
                                    toast.info(`Fokus source ${sourceType.toUpperCase()} ${sourceId || '-'}`);
                                  }
                                }}
                              >
                                Fokus Source
                              </Button>
                              <Button
                                size="sm"
                                className="h-7 text-[10px] bg-emerald-500/85 hover:bg-emerald-400 text-slate-950"
                                onClick={() => handleResolveMonitoringIncident(incident, false)}
                                disabled={!canResolveMonitoringIncidents || isResolvedState || resolvingMonitoringIncidentId === incidentId}
                              >
                                {resolvingMonitoringIncidentId === incidentId ? '...' : 'Resolve'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-[10px] border-rose-400/35 text-rose-200 bg-rose-500/10 hover:bg-rose-500/20"
                                onClick={() => handleResolveMonitoringIncident(incident, true)}
                                disabled={!canResolveMonitoringIncidents || isResolvedState || resolvingMonitoringIncidentId === incidentId}
                              >
                                False Positive
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>



                <div className="space-y-2 max-h-72 overflow-auto pr-1">

                  {openFieldTickets.length === 0 && <p className="text-gray-500 text-xs">Tidak ada tiket berjalan.</p>}

                  {openFieldTickets.map((ticket) => {

                    const form = getCompletionForm(ticket.id);

                    return (

                      <div key={ticket.id} className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">

                        <div className="flex items-center justify-between gap-2">

                          <p className="text-sm text-white font-semibold truncate">{ticket.customer_name}</p>

                          <span className={`text-[10px] font-semibold ${getTicketStatusClass(ticket.status)}`}>{getTicketStatusLabel(ticket.status)}</span>

                        </div>

                        <p className="text-[11px] text-gray-400">Teknisi: {(ticket.approved_technician_names || []).join(', ') || '-'}</p>

                        {ticket.issue_notes && <p className="text-xs text-gray-300">Keluhan: {ticket.issue_notes}</p>}



                        <Button size="sm" onClick={() => handleJoinTicket(ticket.id)} disabled={joiningTicketId === ticket.id} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">

                          {joiningTicketId === ticket.id ? 'Mengirim approval...' : 'Approve Turun Lapangan'}

                        </Button>



                        <div className="space-y-2 rounded border border-white/10 p-2">

                          <select

                            value={form.completion_status}

                            onChange={(event) => updateCompletionForm(ticket.id, { completion_status: event.target.value })}

                            className="w-full rounded bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs"

                          >

                            <option value="completed">Selesai</option>

                            <option value="needs_special_handling">Butuh Penanganan Khusus</option>

                          </select>



                          <Textarea

                            value={form.completion_notes}

                            onChange={(event) => updateCompletionForm(ticket.id, { completion_notes: event.target.value })}

                            rows={2}

                            className="bg-[#050505] border-white/10 text-white resize-none text-xs"

                            placeholder="Catatan hasil perbaikan"

                          />



                          <div className="space-y-1">

                            <label className="text-[11px] text-gray-400 flex items-center gap-1"><Camera className="w-3 h-3" />Bukti pekerjaan (opsional)</label>

                            <input

                              type="file"

                              accept="image/*"

                              multiple

                              onChange={(event) => updateCompletionForm(ticket.id, { evidence_files: Array.from(event.target.files || []) })}

                              className="block w-full text-[11px] text-gray-400 file:mr-3 file:rounded file:border-0 file:bg-cyan-500/20 file:px-2 file:py-1 file:text-cyan-200"

                            />

                            {Array.isArray(form.evidence_files) && form.evidence_files.length > 0 && (

                              <p className="text-[10px] text-cyan-300">{form.evidence_files.length} file siap diunggah</p>

                            )}

                          </div>



                          <Button size="sm" onClick={() => handleCompleteTicket(ticket.id)} disabled={completingTicketId === ticket.id} className="w-full bg-green-600 hover:bg-green-700 text-white">

                            {completingTicketId === ticket.id ? 'Mengirim laporan...' : 'Simpan Form Selesai'}

                          </Button>

                        </div>

                      </div>

                    );

                  })}

                </div>



                <div className="border-t border-white/10 pt-3">

                  <p className="text-xs text-white font-semibold mb-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Riwayat Selesai ({completedFieldTickets.length})</p>

                  <div className="space-y-2 max-h-36 overflow-auto pr-1">

                    {completedFieldTickets.length === 0 && <p className="text-gray-500 text-xs">Belum ada laporan selesai.</p>}

                    {completedFieldTickets.map((ticket) => (

                      <div key={ticket.id} className="rounded border border-white/10 bg-[#0A0A0A] p-2 space-y-1">

                        <div className="flex items-center justify-between gap-2">

                          <p className="text-xs text-white">{ticket.customer_name}</p>

                          <span className={`text-[10px] ${getTicketStatusClass(ticket.status)}`}>{getTicketStatusLabel(ticket.status)}</span>

                        </div>

                        <p className="text-[10px] text-gray-400">Teknisi: {(ticket.completed_technician_names || []).join(', ') || '-'}</p>

                        {Array.isArray(ticket.evidence) && ticket.evidence.length > 0 && (

                          <div className="flex flex-wrap gap-1 pt-1">

                            {ticket.evidence.slice(0, 3).map((item) => (

                              <a key={item.id || item.url} href={``} target="_blank" rel="noreferrer" className="block h-10 w-10 overflow-hidden rounded border border-white/10">

                                <img src={``} alt={item.original_name || 'Bukti'} className="h-full w-full object-cover" />

                              </a>

                            ))}

                          </div>

                        )}

                        {ticket.needs_special_handling && <p className="text-[10px] text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Butuh penanganan khusus</p>}

                      </div>

                    ))}

                  </div>

                </div>

              </div>

            )}

          </div>

        </div>

      </main>



      <Dialog open={passwordDialogOpen} onOpenChange={(open) => {

        setPasswordDialogOpen(open);

        if (!open) resetPasswordForm();

      }}>

        <DialogContent className="bg-[#121212] border-white/10 text-white">

          <DialogHeader>

            <DialogTitle>Ganti Password</DialogTitle>

            <DialogDescription className="text-gray-400">

              Ubah password akun {config.title.toLowerCase()} Anda.

            </DialogDescription>

          </DialogHeader>



          <form onSubmit={handleChangePassword} className="space-y-4">

            <div>

              <label className="staff-form-label">Password Lama</label>

              <Input

                type="password"

                value={passwordForm.old_password}

                onChange={(event) => setPasswordForm((prev) => ({ ...prev, old_password: event.target.value }))}

                className="bg-[#0A0A0A] border-white/10 text-white"

                placeholder="Password lama"

              />

            </div>



            <div>

              <label className="staff-form-label">Password Baru</label>

              <Input

                type="password"

                value={passwordForm.new_password}

                onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password: event.target.value }))}

                className="bg-[#0A0A0A] border-white/10 text-white"

                placeholder="Minimal 6 karakter"

              />

            </div>



            <div>

              <label className="staff-form-label">Konfirmasi Password Baru</label>

              <Input

                type="password"

                value={passwordForm.confirm_password}

                onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirm_password: event.target.value }))}

                className="bg-[#0A0A0A] border-white/10 text-white"

                placeholder="Ulangi password baru"

              />

            </div>



            <DialogFooter>

              <Button type="button" variant="outline" className="btn-secondary" onClick={() => setPasswordDialogOpen(false)}>

                Batal

              </Button>

              <Button type="submit" disabled={passwordSaving} className="bg-brand-blue hover:bg-brand-blue/90 text-white">

                {passwordSaving ? 'Menyimpan...' : 'Simpan Password'}

              </Button>

            </DialogFooter>

          </form>

        </DialogContent>

      </Dialog>

    </div>

  );

};



export default StaffMonitoringMap;













































